import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { Typography, Box, Button, Chip, CircularProgress } from '@mui/material';
import { buildTimeSeriesParams, buildBreakdownParams, formatV2Series, formatBucketLabel } from '../utils/v2StatsHelpers';
import type { DashboardStatsTypes, CreditsTypes, PolicyTypes } from '@flashbacktech/flashbackclient/api';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
  Legend,
} from 'recharts';

// ─── Types ───────────────────────────────────────────────────────────────────
type SubSection = 'gateway' | 'chat' | 'policies' | 'credits';

interface StatisticsExplorerProps {
  orgName: string;
  workspaces: any[];
  repositories: any[];
  bucketsCount: number;
  repositoriesCount: number;
  storageUsed: string;
  storageLimit: string;
  team: any[];
  aiStats: {
    totalApiCalls: number;
    totalTokensIn: number;
    totalTokensOut: number;
    totalLlmTokensIn: number;
    totalPolicyViolations: number;
    totalAlerts: number;
    totalBlocks: number;
  };
  subSection: SubSection;
  onSubSectionChange?: (s: SubSection) => void;
  apiClient: any;
  withTokenRefresh: <T>(fn: () => Promise<T>) => Promise<T>;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmtNum = (n: number): string => {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return String(n);
};
const fmtCredits = (n: number): string => {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(2) + 'K';
  return n.toFixed(2);
};

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const getRelativeTime = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  return `${diffD}d ago`;
};

type DatePreset = '24h' | '7d' | '30d' | '90d' | '180d' | '365d';

const getDateRangeFromPreset = (preset: DatePreset): { startDate: Date; endDate: Date } => {
  const endDate = new Date();
  const startDate = new Date();
  switch (preset) {
    case '24h':
      startDate.setHours(startDate.getHours() - 24);
      break;
    case '7d':
      startDate.setDate(startDate.getDate() - 7);
      break;
    case '30d':
      startDate.setDate(startDate.getDate() - 30);
      break;
    case '90d':
      startDate.setDate(startDate.getDate() - 90);
      break;
    case '180d':
      startDate.setDate(startDate.getDate() - 180);
      break;
    case '365d':
      startDate.setDate(startDate.getDate() - 365);
      break;
  }
  return { startDate, endDate };
};

// ─── Sub-components ──────────────────────────────────────────────────────────
const ChevronIcon = ({ expanded }: { expanded: boolean }) => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}>
    <path d="M4 2L8 6L4 10" stroke="#667085" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const KpiCard = ({ label, value, color, loading }: { label: string; value: string; color: string; loading?: boolean }) => (
  <Box sx={{ bgcolor: 'white', borderRadius: '10px', border: '1px solid #E9EAEB', p: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
      <Typography sx={{ fontSize: '11px', color: '#667085', fontWeight: 500 }}>{label}</Typography>
    </Box>
    {loading ? (
      <CircularProgress size={16} sx={{ color: '#667085' }} />
    ) : (
      <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#101828' }}>{value}</Typography>
    )}
  </Box>
);

const SectionHeader = ({ title, subtitle, collapsed, onToggle }: { title: string; subtitle?: string; collapsed: boolean; onToggle: () => void }) => (
  <Box sx={{ mb: 2 }}>
    <Box onClick={onToggle} sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}>
      <ChevronIcon expanded={!collapsed} />
      <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#101828' }}>{title}</Typography>
      <Box sx={{ flex: 1, height: '1px', bgcolor: '#E9EAEB', ml: 1 }} />
    </Box>
    {subtitle && (
      <Typography sx={{ fontSize: '12px', color: '#667085', mt: 0.5, ml: 2.5 }}>{subtitle}</Typography>
    )}
  </Box>
);

const chartTooltipStyle = { borderRadius: '8px', border: '1px solid #E5E7EB', fontSize: 12 };
const axisTick = { fontSize: 10, fill: '#9CA3AF' };

// ─── Sampling strategy ──────────────────────────────────────────────────────
const getSamplingIntervalMs = (preset: DatePreset): number => {
  switch (preset) {
    case '24h': return 10 * 60 * 1000; // 10 min
    case '7d': return 60 * 60 * 1000; // 1 hour
    case '30d': return 6 * 60 * 60 * 1000; // 6 hours
    case '90d': return 12 * 60 * 60 * 1000; // 12 hours
    case '180d': return 24 * 60 * 60 * 1000; // 1 day
    case '365d': return 24 * 60 * 60 * 1000; // 1 day
  }
};

// formatBucketLabel is imported from ../utils/v2StatsHelpers

interface RawTimedEntry {
  timestamp: number; // unix seconds or ms
  [key: string]: any;
}

const sampleTimeSeries = <T extends Record<string, number>>(
  raw: RawTimedEntry[],
  preset: DatePreset,
  aggregateKeys: (keyof T)[],
): Array<{ name: string; ts: number } & T> => {
  if (raw.length === 0) return [];
  const intervalMs = getSamplingIntervalMs(preset);
  const { startDate, endDate } = getDateRangeFromPreset(preset);
  const startMs = startDate.getTime();
  const endMs = endDate.getTime();

  // Create buckets
  const buckets: Map<number, Record<string, number>> = new Map();
  for (let t = startMs; t <= endMs; t += intervalMs) {
    const bucket: Record<string, number> = {};
    aggregateKeys.forEach(k => { bucket[k as string] = 0; });
    buckets.set(t, bucket);
  }

  // Place raw data into buckets
  raw.forEach(item => {
    let tsMs = item.timestamp;
    if (tsMs < 1e12) tsMs *= 1000; // convert unix seconds to ms
    // Find the bucket this belongs to
    const bucketTs = startMs + Math.floor((tsMs - startMs) / intervalMs) * intervalMs;
    const bucket = buckets.get(bucketTs);
    if (bucket) {
      aggregateKeys.forEach(k => {
        bucket[k as string] += Number(item[k as string] || 0);
      });
    }
  });

  // Convert to array
  return Array.from(buckets.entries()).map(([ts, data]) => ({
    name: formatBucketLabel(ts, preset),
    ts,
    ...data as unknown as T,
  }));
};

// Smart X-axis tick calculator
const getSmartTickIndices = (dataLength: number, maxTicks = 8): number[] => {
  if (dataLength <= maxTicks) return Array.from({ length: dataLength }, (_, i) => i);
  const indices: number[] = [0]; // always show first
  const step = (dataLength - 1) / (maxTicks - 1);
  for (let i = 1; i < maxTicks - 1; i++) {
    indices.push(Math.round(step * i));
  }
  indices.push(dataLength - 1); // always show last
  return [...new Set(indices)].sort((a, b) => a - b);
};

// TradingView-style tooltip
const TradingViewTooltip = ({ active, payload, label, valueFormatter, color }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{
      bgcolor: 'rgba(255,255,255,0.96)', borderRadius: '8px', border: '1px solid #E5E7EB',
      px: 1.5, py: 1, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', minWidth: 120,
    }}>
      <Typography sx={{ fontSize: '11px', color: '#9CA3AF', mb: 0.5 }}>{label}</Typography>
      {payload.map((p: any, i: number) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: p.color || color || '#617BFF' }} />
          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#101828' }}>
            {p.name}: {valueFormatter ? valueFormatter(p.value) : p.value?.toLocaleString()}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// Full-width chart container
const CHART_HEIGHT = 280;
const FullWidthChartCard = ({ title, children, loading }: { title: string; children: React.ReactNode; loading?: boolean }) => (
  <Box sx={{ bgcolor: 'white', borderRadius: '10px', border: '1px solid #E9EAEB', p: 2.5, mb: 2.5 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
      <Typography sx={{ fontWeight: 600, fontSize: '13px', color: '#101828' }}>{title}</Typography>
      {loading && <CircularProgress size={14} sx={{ color: '#617BFF' }} />}
    </Box>
    <Box sx={{ height: CHART_HEIGHT, width: '100%' }}>{children}</Box>
  </Box>
);

// Chart view mode selector (aggregated / temporal) with chart type toggle
const ChartViewModeSelector = ({ 
  viewMode, 
  onViewModeChange, 
  chartType, 
  onChartTypeChange 
}: { 
  viewMode: 'aggregated' | 'temporal'; 
  onViewModeChange: (mode: 'aggregated' | 'temporal') => void; 
  chartType: 'line' | 'bar'; 
  onChartTypeChange: (type: 'line' | 'bar') => void; 
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
    {/* View mode toggle: Aggregated / Temporal */}
    <Box sx={{ display: 'flex', bgcolor: '#F3F4F6', borderRadius: '6px', p: 0.25 }}>
      <Box
        onClick={() => onViewModeChange('aggregated')}
        sx={{
          px: 1.5, py: 0.5, borderRadius: '5px', cursor: 'pointer',
          bgcolor: viewMode === 'aggregated' ? 'white' : 'transparent',
          boxShadow: viewMode === 'aggregated' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
          transition: 'all 0.15s',
        }}
      >
        <Typography sx={{ fontSize: '11px', fontWeight: 500, color: viewMode === 'aggregated' ? '#101828' : '#667085' }}>Aggregated</Typography>
      </Box>
      <Box
        onClick={() => onViewModeChange('temporal')}
        sx={{
          px: 1.5, py: 0.5, borderRadius: '5px', cursor: 'pointer',
          bgcolor: viewMode === 'temporal' ? 'white' : 'transparent',
          boxShadow: viewMode === 'temporal' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
          transition: 'all 0.15s',
        }}
      >
        <Typography sx={{ fontSize: '11px', fontWeight: 500, color: viewMode === 'temporal' ? '#101828' : '#667085' }}>Temporal</Typography>
      </Box>
    </Box>
    {/* Chart type toggle: Line / Bar (only for temporal) */}
    {viewMode === 'temporal' && (
      <Box sx={{ display: 'flex', bgcolor: '#F3F4F6', borderRadius: '6px', p: 0.25 }}>
        <Box
          onClick={() => onChartTypeChange('line')}
          sx={{
            px: 1, py: 0.5, borderRadius: '5px', cursor: 'pointer',
            bgcolor: chartType === 'line' ? 'white' : 'transparent',
            boxShadow: chartType === 'line' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={chartType === 'line' ? '#617BFF' : '#9CA3AF'} strokeWidth="2">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
          </svg>
        </Box>
        <Box
          onClick={() => onChartTypeChange('bar')}
          sx={{
            px: 1, py: 0.5, borderRadius: '5px', cursor: 'pointer',
            bgcolor: chartType === 'bar' ? 'white' : 'transparent',
            boxShadow: chartType === 'bar' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={chartType === 'bar' ? '#617BFF' : '#9CA3AF'} strokeWidth="2">
            <rect x="3" y="12" width="4" height="9" /><rect x="10" y="6" width="4" height="15" /><rect x="17" y="3" width="4" height="18" />
          </svg>
        </Box>
      </Box>
    )}
  </Box>
);

// Enhanced chart card with view mode selector
const EnhancedChartCard = ({ 
  title, 
  loading, 
  viewMode, 
  onViewModeChange, 
  chartType, 
  onChartTypeChange,
  children 
}: { 
  title: string; 
  loading?: boolean; 
  viewMode: 'aggregated' | 'temporal'; 
  onViewModeChange: (mode: 'aggregated' | 'temporal') => void; 
  chartType: 'line' | 'bar'; 
  onChartTypeChange: (type: 'line' | 'bar') => void;
  children: React.ReactNode;
}) => (
  <Box sx={{ bgcolor: 'white', borderRadius: '10px', border: '1px solid #E9EAEB', p: 2.5, mb: 2.5 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography sx={{ fontWeight: 600, fontSize: '13px', color: '#101828' }}>{title}</Typography>
        {loading && <CircularProgress size={14} sx={{ color: '#617BFF' }} />}
      </Box>
      <ChartViewModeSelector 
        viewMode={viewMode} 
        onViewModeChange={onViewModeChange} 
        chartType={chartType} 
        onChartTypeChange={onChartTypeChange} 
      />
    </Box>
    <Box sx={{ height: CHART_HEIGHT, width: '100%' }}>{children}</Box>
  </Box>
);

// Dropdown multi-select for comparison selectors
const MultiSelectDropdown = ({ 
  label, 
  options, 
  selected, 
  onToggle, 
  maxItems = 5,
  colors 
}: { 
  label: string; 
  options: string[]; 
  selected: Set<string>; 
  onToggle: (item: string) => void; 
  maxItems?: number;
  colors: string[];
}) => {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = React.useState<{ top: number; left: number }>({ top: 0, left: 0 });

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current && triggerRef.current.contains(target)) return;
      if (menuRef.current && menuRef.current.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Recalculate position when opening
  const handleToggle = () => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, left: rect.left });
    }
    setOpen(!open);
  };

  const selectedArray = [...selected];

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
      {/* Dropdown trigger */}
      <Box ref={triggerRef} sx={{ position: 'relative' }}>
        <Box
          onClick={handleToggle}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75,
            border: '1px solid #E9EAEB', borderRadius: '8px', cursor: 'pointer',
            bgcolor: open ? '#F9FAFB' : 'white', minWidth: 160,
            '&:hover': { borderColor: '#D0D5DD' },
          }}
        >
          <Typography sx={{ fontSize: '12px', color: '#667085', flex: 1 }}>
            {label} ({selected.size}/{maxItems})
          </Typography>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
            <polyline points={open ? "18 15 12 9 6 15" : "6 9 12 15 18 9"} />
          </svg>
        </Box>
        {/* Dropdown menu via Portal */}
        {open && ReactDOM.createPortal(
          <Box ref={menuRef} sx={{
            position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 99999,
            bgcolor: 'white', border: '1px solid #E9EAEB', borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)', minWidth: 200, maxHeight: 280, overflow: 'auto',
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-thumb': { bgcolor: '#D0D5DD', borderRadius: 2 },
          }}>
            {options.map((item) => {
              const isSelected = selected.has(item);
              const canSelect = isSelected || selected.size < maxItems;
              return (
                <Box
                  key={item}
                  onClick={() => canSelect && onToggle(item)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1,
                    cursor: canSelect ? 'pointer' : 'not-allowed',
                    opacity: canSelect ? 1 : 0.4,
                    bgcolor: isSelected ? '#F0F2FF' : 'transparent',
                    '&:hover': { bgcolor: canSelect ? (isSelected ? '#E8ECFF' : '#F9FAFB') : 'transparent' },
                  }}
                >
                  <Box sx={{
                    width: 16, height: 16, borderRadius: '4px', border: '1px solid',
                    borderColor: isSelected ? '#617BFF' : '#D0D5DD',
                    bgcolor: isSelected ? '#617BFF' : 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {isSelected && (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: '12px', color: '#344054', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item}
                  </Typography>
                </Box>
              );
            })}
            {options.length === 0 && (
              <Box sx={{ px: 1.5, py: 2, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '11px', color: '#9CA3AF' }}>No items available</Typography>
              </Box>
            )}
          </Box>,
          document.body
        )}
      </Box>
      {/* Selected items as chips */}
      {selectedArray.map((item, idx) => (
        <Box
          key={item}
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5,
            bgcolor: (colors[idx % colors.length]) + '18',
            border: `1px solid ${colors[idx % colors.length]}40`,
            borderRadius: '6px',
          }}
        >
          <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: colors[idx % colors.length] }} />
          <Typography sx={{ fontSize: '11px', color: colors[idx % colors.length], fontWeight: 500, maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item}
          </Typography>
          <Box
            onClick={(e) => { e.stopPropagation(); onToggle(item); }}
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', ml: 0.25, '&:hover': { opacity: 0.7 } }}
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={colors[idx % colors.length]} strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const ChartTypeSelector = ({ value, onChange }: { value: 'line' | 'column'; onChange: (v: 'line' | 'column') => void }) => (
  <Box sx={{ display: 'flex', gap: 0.25, bgcolor: '#F3F4F6', borderRadius: '6px', p: '2px' }}>
    {(['line', 'column'] as const).map(t => (
      <Box
        key={t}
        onClick={() => onChange(t)}
        sx={{
          px: 1, py: 0.25, borderRadius: '4px', cursor: 'pointer', fontSize: '10px', fontWeight: 600,
          bgcolor: value === t ? 'white' : 'transparent',
          color: value === t ? '#101828' : '#9CA3AF',
          boxShadow: value === t ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
          textTransform: 'capitalize', lineHeight: '18px',
          transition: 'all 0.15s',
        }}
      >
        {t === 'line' ? 'Line' : 'Bar'}
      </Box>
    ))}
  </Box>
);

// ─── Main Component ──────────────────────────────────────────────────────────
export default function StatisticsExplorer({
  orgName,
  workspaces,
  repositories,
  bucketsCount,
  repositoriesCount,
  storageUsed,
  storageLimit,
  team,
  aiStats,
  subSection,
  onSubSectionChange,
  apiClient,
  withTokenRefresh,
}: StatisticsExplorerProps) {
  const { authState } = useAuth();

  // Explorer tree state
  const [explorerWs, setExplorerWs] = useState<string | null>(null);
  const [explorerRepo, setExplorerRepo] = useState<string | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [explorerCollapsed, setExplorerCollapsed] = useState(false);
  const [dateRange, setDateRange] = useState<DatePreset>('30d');

  // Local repos+buckets state - fetched directly like OverviewMockup to get ALL data
  const [localRepos, setLocalRepos] = useState<any[]>([]);
  const [localReposLoaded, setLocalReposLoaded] = useState(false);
  const [localBuckets, setLocalBuckets] = useState<any[]>([]);

  // Use localRepos if loaded, otherwise fall back to prop
  const effectiveRepositories = localReposLoaded && localRepos.length > 0 ? localRepos : repositories;

  // Track if content has scrolled past the explorer sidebar → expand to full width
  const sidebarRef = useRef<HTMLDivElement>(null);
  const aiLlmSectionRef = useRef<HTMLDivElement>(null);
  const comparisonsSectionRef = useRef<HTMLDivElement>(null);
  const [pastSidebar, setPastSidebar] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (sidebarRef.current) {
        const sidebarRect = sidebarRef.current.getBoundingClientRect();
        // When bottom of sidebar is above viewport top, we've scrolled past it
        setPastSidebar(sidebarRect.bottom < 100);
      }
      // Auto-collapse explorer when a deep section enters the viewport (top 60%)
      if (!explorerCollapsed) {
        const threshold = window.innerHeight * 0.6;
        // Gateway: AI LLM section
        if (aiLlmSectionRef.current) {
          const aiRect = aiLlmSectionRef.current.getBoundingClientRect();
          if (aiRect.top < threshold && aiRect.top > 0) { setExplorerCollapsed(true); return; }
        }
        // Credits / Policies / Chat: Comparisons section
        if (comparisonsSectionRef.current) {
          const cmpRect = comparisonsSectionRef.current.getBoundingClientRect();
          if (cmpRect.top < threshold && cmpRect.top > 0) { setExplorerCollapsed(true); return; }
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, [explorerCollapsed]);

  // Fetch repos + buckets directly like OverviewMockup does (parallel fetch)
  useEffect(() => {
    const loadStorageData = async () => {
      const token = authState?.token;
      if (!token || !apiClient || !withTokenRefresh) return;
      try {
        const baseUrl = process.env.REACT_APP_API_URL || 'https://backend.flashback.tech';

        // Parallel fetch: buckets via apiClient + repos via direct /repo (like OverviewMockup)
        const [bucketsResponse, reposResponse] = await Promise.all([
          withTokenRefresh(apiClient.getStorageBuckets.bind(apiClient)),
          fetch(`${baseUrl}/repo`, {
            method: 'GET',
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }).then((res) => (res.ok ? res.json() : { repos: [] })),
        ]);

        // Process buckets
        const allBuckets = (bucketsResponse as any)?.buckets || [];
        console.log('[StatisticsExplorer] Loaded', allBuckets.length, 'buckets');
        setLocalBuckets(allBuckets);

        // Process repos
        const allRepos = reposResponse?.repos || [];
        console.log('[StatisticsExplorer] Loaded', allRepos.length, 'repos from /repo');
        allRepos.forEach((repo: any, i: number) => {
          const wid = repo.workspaceId || repo.workspace_id || repo.workspace?.id || repo.workspace;
          const bucketCount = (repo.buckets || []).length;
          console.log(`[StatisticsExplorer] Repo[${i}]: "${repo.name}", id=${repo.id}, workspaceId=${wid}, buckets=${bucketCount}`);
        });

        setLocalRepos(allRepos);
        setLocalReposLoaded(true);
      } catch (e) {
        console.error('[StatisticsExplorer] Error fetching storage data:', e);
      }
    };
    loadStorageData();
  }, [authState?.token, apiClient, withTokenRefresh]);

  // Workspace table sorting (General section)
  type SortColumn = 'name' | 'creationDate' | 'repos' | 'connectedResources' | 'requests' | 'storageTraffic' | 'tokenTraffic' | 'credits';
  const [wsSortColumn, setWsSortColumn] = useState<SortColumn>('credits');
  const [wsSortDirection, setWsSortDirection] = useState<'asc' | 'desc'>('desc');

  // Credits comparison
  const [selectedCompareWs, setSelectedCompareWs] = useState<Set<string>>(new Set());
  const [selectedCreditsCompareRepos, setSelectedCreditsCompareRepos] = useState<Set<string>>(new Set());

  // Chart view mode & type selectors
  type ChartViewMode = 'aggregated' | 'temporal';
  type ChartType = 'line' | 'bar';
  // Gateway section
  const [gwRequestsViewMode, setGwRequestsViewMode] = useState<ChartViewMode>('temporal');
  const [gwRequestsChartType, setGwRequestsChartType] = useState<ChartType>('line');
  const [gwTrafficViewMode, setGwTrafficViewMode] = useState<ChartViewMode>('temporal');
  const [gwTrafficChartType, setGwTrafficChartType] = useState<ChartType>('line');
  const [gwStorageViewMode, setGwStorageViewMode] = useState<ChartViewMode>('temporal');
  const [gwStorageChartType, setGwStorageChartType] = useState<ChartType>('line');
  // AI/LLM comparison view modes (per metric: requests, tokensIn, tokensOut)
  const [aiProviderViewMode, setAiProviderViewMode] = useState<Record<string, ChartViewMode>>({ requests: 'aggregated', tokensIn: 'aggregated', tokensOut: 'aggregated' });
  const [aiProviderChartType, setAiProviderChartType] = useState<Record<string, ChartType>>({ requests: 'line', tokensIn: 'line', tokensOut: 'line' });
  const [aiWsViewMode, setAiWsViewMode] = useState<Record<string, ChartViewMode>>({ requests: 'aggregated', tokensIn: 'aggregated', tokensOut: 'aggregated' });
  const [aiWsChartType, setAiWsChartType] = useState<Record<string, ChartType>>({ requests: 'line', tokensIn: 'line', tokensOut: 'line' });
  const [aiModelViewMode, setAiModelViewMode] = useState<Record<string, ChartViewMode>>({ requests: 'aggregated', tokensIn: 'aggregated', tokensOut: 'aggregated' });
  const [aiModelChartType, setAiModelChartType] = useState<Record<string, ChartType>>({ requests: 'line', tokensIn: 'line', tokensOut: 'line' });
  // Gateway AI/LLM overview chart types (Line/Bar toggle per metric)
  const [aiOverviewChartType, setAiOverviewChartType] = useState<Record<string, 'line' | 'bar'>>({ requests: 'line', tokensIn: 'line', tokensOut: 'line' });
  // Credits collapsible sections
  const [creditsCollapsedSections, setCreditsCollapsedSections] = useState<Set<string>>(new Set());
  const toggleCreditsSection = (key: string) => setCreditsCollapsedSections(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  // Credits overview view modes (aggregated / temporal + Line/Bar toggle per metric)
  const [creditsOverviewViewMode, setCreditsOverviewViewMode] = useState<Record<string, ChartViewMode>>({ ai: 'temporal', storage: 'temporal', global: 'temporal' });
  const [creditsOverviewChartType, setCreditsOverviewChartType] = useState<Record<string, 'line' | 'bar'>>({ ai: 'line', storage: 'line', global: 'line' });
  // Credits comparison view modes (aggregated / temporal + Line/Bar toggle per metric)
  const [creditsWsCompareViewMode, setCreditsWsCompareViewMode] = useState<Record<string, ChartViewMode>>({ total: 'aggregated', ai: 'aggregated', storage: 'aggregated' });
  const [creditsWsCompareChartType, setCreditsWsCompareChartType] = useState<Record<string, ChartType>>({ total: 'line', ai: 'line', storage: 'line' });
  const [creditsRepoCompareViewMode, setCreditsRepoCompareViewMode] = useState<Record<string, ChartViewMode>>({ total: 'aggregated', ai: 'aggregated', storage: 'aggregated' });
  const [creditsRepoCompareChartType, setCreditsRepoCompareChartType] = useState<Record<string, ChartType>>({ total: 'line', ai: 'line', storage: 'line' });
  // Legacy AI/LLM chart types
  const [aiChartType, setAiChartType] = useState<ChartType>('line');
  const [storageChartType, setStorageChartType] = useState<ChartType>('line');
  const [selectedAiCompareWs, setSelectedAiCompareWs] = useState<Set<string>>(new Set());
  const [selectedStorageCompareWs, setSelectedStorageCompareWs] = useState<Set<string>>(new Set());
  // Repository comparison selectors (for when workspace is selected)
  const [selectedAiCompareRepos, setSelectedAiCompareRepos] = useState<Set<string>>(new Set());
  const [selectedStorageCompareRepos, setSelectedStorageCompareRepos] = useState<Set<string>>(new Set());
  // Repository comparison view modes
  const [aiRepoViewMode, setAiRepoViewMode] = useState<Record<string, ChartViewMode>>({ requests: 'aggregated', tokensIn: 'aggregated', tokensOut: 'aggregated' });
  const [aiRepoChartType, setAiRepoChartType] = useState<Record<string, ChartType>>({ requests: 'line', tokensIn: 'line', tokensOut: 'line' });

  // AI/LLM comparison selectors (provider, model)
  const [selectedAiProviders, setSelectedAiProviders] = useState<Set<string>>(new Set());
  const [selectedAiModels, setSelectedAiModels] = useState<Set<string>>(new Set());
  // Storage comparison selectors (provider, bucket)
  const [selectedStorageProviders, setSelectedStorageProviders] = useState<Set<string>>(new Set());
  const [selectedStorageBuckets, setSelectedStorageBuckets] = useState<Set<string>>(new Set());

  // AI/LLM sampled overview chart data (separate arrays)
  const [aiRequestsChart, setAiRequestsChart] = useState<any[]>([]);
  const [aiTokensInChart, setAiTokensInChart] = useState<any[]>([]);
  const [aiTokensOutChart, setAiTokensOutChart] = useState<any[]>([]);
  // AI/LLM per-provider and per-model raw aggregated data
  const [aiProviderList, setAiProviderList] = useState<string[]>([]);
  const [aiModelList, setAiModelList] = useState<string[]>([]);
  const [aiPerProviderTimeSeries, setAiPerProviderTimeSeries] = useState<Record<string, any[]>>({});
  const [aiPerModelTimeSeries, setAiPerModelTimeSeries] = useState<Record<string, any[]>>({});
  const [aiPerWsTimeSeries, setAiPerWsTimeSeries] = useState<Record<string, any[]>>({});

  // ─── Credits data (real API) ────────────────────────────────────────────────
  const [creditsBalance, setCreditsBalance] = useState<{
    realtimeBalance: number; creditBalance: number; pendingConsumption: number;
    breakdown: { storage: number; ai: number };
  } | null>(null);
  const [monthlyStats, setMonthlyStats] = useState<Array<{
    month: string; consumption: number; purchases: number; grants: number; balance: number;
  }>>([]);
  const [creditsLoading, setCreditsLoading] = useState(false);

  // Credits comparison selectors
  const [selectedCreditsProviders, setSelectedCreditsProviders] = useState<Set<string>>(new Set());
  const [selectedCreditsModels, setSelectedCreditsModels] = useState<Set<string>>(new Set());
  const [selectedCreditsBuckets, setSelectedCreditsBuckets] = useState<Set<string>>(new Set());
  // V2 credit breakdown data
  interface CreditEntity { entityId: string; entityName: string; creditsAi: number; creditsStorage: number; creditsTotal: number; creditsPurchased: number; creditsGranted: number; series?: any[] }
  const [creditWsBreakdown, setCreditWsBreakdown] = useState<CreditEntity[]>([]);
  const [creditRepoBreakdown, setCreditRepoBreakdown] = useState<CreditEntity[]>([]);
  const [creditModelBreakdown, setCreditModelBreakdown] = useState<CreditEntity[]>([]);
  const [creditProviderBreakdown, setCreditProviderBreakdown] = useState<CreditEntity[]>([]);
  const [creditBucketBreakdown, setCreditBucketBreakdown] = useState<CreditEntity[]>([]);
  const [creditsProviderChartMode, setCreditsProviderChartMode] = useState<'line' | 'bar'>('line');
  const [creditsModelChartMode, setCreditsModelChartMode] = useState<'line' | 'bar'>('line');
  // Credits Model Comparison view mode (aggregated / temporal)
  const [creditsModelViewMode, setCreditsModelViewMode] = useState<ChartViewMode>('aggregated');
  const [creditsModelChartType, setCreditsModelChartType] = useState<ChartType>('line');

  // ─── Per-workspace stats (real API aggregated) ──────────────────────────────
  const [perWsStats, setPerWsStats] = useState<Record<string, {
    requests: number; upload: number; download: number;
    tokensIn: number; tokensOut: number; apiCalls: number;
  }>>({});
  const [perWsStatsLoading, setPerWsStatsLoading] = useState(false);

  // ─── Per-repository stats (for bucket-level traffic aggregation) ───────────
  const [perRepoStats, setPerRepoStats] = useState<Record<string, {
    requests: number; upload: number; download: number;
  }>>({});

  // ─── Gateway stats ─────────────────────────────────────────────────────────
  const [statsLoading, setStatsLoading] = useState(false);
  const [requestsChartData, setRequestsChartData] = useState<any[]>([]);
  const [trafficChartData, setTrafficChartData] = useState<any[]>([]);
  const [storageChartData, setStorageChartData] = useState<any[]>([]);
  const [totalRequests, setTotalRequests] = useState(0);
  const [totalUpload, setTotalUpload] = useState(0);
  const [totalDownload, setTotalDownload] = useState(0);
  const [totalStorageChange, setTotalStorageChange] = useState(0);
  const [avgLatency, setAvgLatency] = useState(0);

  // ─── AI / LLM stats (gateway LLM section + chat section) ─────────────────
  const [aiStatsLoading, setAiStatsLoading] = useState(false);
  const [aiTokensChartData, setAiTokensChartData] = useState<any[]>([]);
  const [aiLatencyChartData, setAiLatencyChartData] = useState<any[]>([]);
  const [aiModelDistribution, setAiModelDistribution] = useState<{ name: string; percent: number; color: string }[]>([]);
  const [aiTotalApiCalls, setAiTotalApiCalls] = useState(0);
  const [aiTotalTokensIn, setAiTotalTokensIn] = useState(0);
  const [aiTotalTokensOut, setAiTotalTokensOut] = useState(0);
  const [aiTotalPolicyViolations, setAiTotalPolicyViolations] = useState(0);
  const [aiTotalAlerts, setAiTotalAlerts] = useState(0);
  const [aiTotalBlocks, setAiTotalBlocks] = useState(0);
  const [aiAvgLatency, setAiAvgLatency] = useState(0);

  // ─── Policies stats ───────────────────────────────────────────────────────
  const [policiesLoading, setPoliciesLoading] = useState(false);
  const [policiesTotal, setPoliciesTotal] = useState(0);
  const [policiesBlocked, setPoliciesBlocked] = useState(0);
  const [policiesLogged, setPoliciesLogged] = useState(0);
  const [recentViolations, setRecentViolations] = useState<any[]>([]);
  const [violationsChartData, setViolationsChartData] = useState<any[]>([]);
  const [violationTypes, setViolationTypes] = useState<{ name: string; percent: number; color: string }[]>([]);

  // ─── Enhanced Policies stats (per-ws, per-repo, per-user via V2 breakdowns) ─
  const [policyTotalAlerts, setPolicyTotalAlerts] = useState(0);
  const [policyOvertimeData, setPolicyOvertimeData] = useState<any[]>([]);
  // Per-workspace policy stats
  interface PolicyWsStat { wsId: string; wsName: string; violations: number; blocks: number; alerts: number; tokensIn: number; tokensOut: number; }
  const [policyWsStats, setPolicyWsStats] = useState<PolicyWsStat[]>([]);
  // Per-repo policy stats (for selected workspace)
  interface PolicyRepoStat { repoId: string; repoName: string; violations: number; blocks: number; alerts: number; tokensIn: number; tokensOut: number; }
  const [policyRepoStats, setPolicyRepoStats] = useState<PolicyRepoStat[]>([]);
  // Per-user policy stats
  interface PolicyUserStat { id: string; name: string; email: string; violations: number; blocks: number; alerts: number; tokensIn: number; tokensOut: number; }
  const [policyUserStats, setPolicyUserStats] = useState<PolicyUserStat[]>([]);
  const [policySelectedUsers, setPolicySelectedUsers] = useState<Set<string>>(new Set());
  // V2 breakdown series: per-entity time series from getPolicyBreakdown
  const [policyRuleSeriesMap, setPolicyRuleSeriesMap] = useState<Record<string, any[]>>({});
  const [policyUserSeriesMap, setPolicyUserSeriesMap] = useState<Record<string, any[]>>({});
  // Policies: Workspace comparison selectors
  const [policySelectedCompareWs, setPolicySelectedCompareWs] = useState<Set<string>>(new Set());
  const [policyWsViewMode, setPolicyWsViewMode] = useState<Record<string, ChartViewMode>>({ violations: 'aggregated', blocks: 'aggregated', alerts: 'aggregated', tokensIn: 'aggregated', tokensOut: 'aggregated' });
  const [policyWsChartType, setPolicyWsChartType] = useState<Record<string, ChartType>>({ violations: 'line', blocks: 'line', alerts: 'line', tokensIn: 'line', tokensOut: 'line' });
  // Policies: Repo comparison selectors (when workspace selected)
  const [policySelectedCompareRepos, setPolicySelectedCompareRepos] = useState<Set<string>>(new Set());
  const [policyRepoViewMode, setPolicyRepoViewMode] = useState<Record<string, ChartViewMode>>({ violations: 'aggregated', blocks: 'aggregated', alerts: 'aggregated', tokensIn: 'aggregated', tokensOut: 'aggregated' });
  const [policyRepoChartType, setPolicyRepoChartType] = useState<Record<string, ChartType>>({ violations: 'line', blocks: 'line', alerts: 'line', tokensIn: 'line', tokensOut: 'line' });
  // Policies: overview view modes (aggregated / temporal + Line/Bar toggle per metric)
  const [policyOverviewViewMode, setPolicyOverviewViewMode] = useState<Record<string, ChartViewMode>>({ violations: 'temporal', blocks: 'temporal', alerts: 'temporal', tokensIn: 'temporal', tokensOut: 'temporal' });
  const [policyOverviewChartType, setPolicyOverviewChartType] = useState<Record<string, 'line' | 'bar'>>({ violations: 'line', blocks: 'line', alerts: 'line', tokensIn: 'line', tokensOut: 'line' });
  // Policies: User comparison view modes (aggregated / temporal + Line/Bar toggle per metric)
  const [policyUserViewMode, setPolicyUserViewMode] = useState<Record<string, ChartViewMode>>({ violations: 'aggregated', blocks: 'aggregated', alerts: 'aggregated', tokensIn: 'aggregated', tokensOut: 'aggregated' });
  const [policyUserChartType, setPolicyUserChartType] = useState<Record<string, ChartType>>({ violations: 'line', blocks: 'line', alerts: 'line', tokensIn: 'line', tokensOut: 'line' });
  // Policies: collapsible sections
  const [policyCollapsedSections, setPolicyCollapsedSections] = useState<Set<string>>(new Set());
  const togglePolicySection = (key: string) => setPolicyCollapsedSections(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  // Policies: toggle comparison ws/repo (max 5)
  const togglePolicyCompareWs = (wsId: string) => { const s = new Set(policySelectedCompareWs); if (s.has(wsId)) s.delete(wsId); else if (s.size < 5) s.add(wsId); setPolicySelectedCompareWs(s); };
  const togglePolicyCompareRepo = (repoId: string) => { const s = new Set(policySelectedCompareRepos); if (s.has(repoId)) s.delete(repoId); else if (s.size < 5) s.add(repoId); setPolicySelectedCompareRepos(s); };

  // ─── Private Chat: Per-user stats ──────────────────────────────────────────
  interface ChatUserStat {
    id: string;
    name: string;
    email: string;
    conversations: number;
    tokensIn: number;
    tokensOut: number;
    apiCalls: number;
    lastModel: string;
    // Per-day time series for comparison charts
    dailyData: Array<{ timestamp: number; tokensIn: number; tokensOut: number; apiCalls: number }>;
    // Model distribution for this user
    modelUsage: Record<string, number>;
  }
  const [chatUserStats, setChatUserStats] = useState<ChatUserStat[]>([]);
  const [chatUserStatsLoading, setChatUserStatsLoading] = useState(false);
  const [chatSelectedUsers, setChatSelectedUsers] = useState<Set<string>>(new Set());
  const [chatExplorerUser, setChatExplorerUser] = useState<string | null>(null); // selected user in explorer sidebar
  // Chat KPI totals (aggregated across all users or selected user)
  const [chatTotalConversations, setChatTotalConversations] = useState(0);
  const [chatTotalTokensIn, setChatTotalTokensIn] = useState(0);
  const [chatTotalTokensOut, setChatTotalTokensOut] = useState(0);
  const [chatTotalModels, setChatTotalModels] = useState(0);
  // Chat overtime chart data
  const [chatOvertimeData, setChatOvertimeData] = useState<any[]>([]);
  // Chat overview chart modes (aggregated / temporal + line / bar per metric)
  const [chatOverviewViewMode, setChatOverviewViewMode] = useState<Record<string, ChartViewMode>>({ tokensIn: 'temporal', tokensOut: 'temporal', apiCalls: 'temporal' });
  const [chatOverviewChartType, setChatOverviewChartType] = useState<Record<string, 'line' | 'bar'>>({ tokensIn: 'line', tokensOut: 'line', apiCalls: 'line' });
  // Chat collapsible sections
  const [chatCollapsedSections, setChatCollapsedSections] = useState<Set<string>>(new Set());
  const toggleChatSection = (key: string) => setChatCollapsedSections(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  // Chat user comparison view modes (aggregated / temporal + Line/Bar toggle per metric)
  const [chatCompareViewMode, setChatCompareViewMode] = useState<Record<string, ChartViewMode>>({ tokensIn: 'aggregated', tokensOut: 'aggregated', apiCalls: 'aggregated' });
  const [chatCompareChartType, setChatCompareChartType] = useState<Record<string, 'line' | 'bar'>>({ tokensIn: 'line', tokensOut: 'line', apiCalls: 'line' });
  // Chat model usage view mode: 'global' (aggregated) or per-user
  const [chatModelViewMode, setChatModelViewMode] = useState<'global' | 'perUser'>('global');
  const [chatModelSelectedUser, setChatModelSelectedUser] = useState<string | null>(null);
  // Chat model comparison (for Model Comparison section)
  const [chatSelectedModels, setChatSelectedModels] = useState<Set<string>>(new Set());
  const [chatModelCompareViewMode, setChatModelCompareViewMode] = useState<Record<string, ChartViewMode>>({ tokensIn: 'aggregated', tokensOut: 'aggregated', apiCalls: 'aggregated' });
  const [chatModelCompareChartType, setChatModelCompareChartType] = useState<Record<string, ChartType>>({ tokensIn: 'line', tokensOut: 'line', apiCalls: 'line' });
  // Chat user table sort
  type ChatUserSortCol = 'name' | 'conversations' | 'tokensIn' | 'tokensOut' | 'apiCalls' | 'lastModel';
  const [chatUserSortCol, setChatUserSortCol] = useState<ChatUserSortCol>('tokensIn');
  const [chatUserSortDir, setChatUserSortDir] = useState<'asc' | 'desc'>('desc');

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Get repos filtered by the selected workspace
  const getReposForWorkspace = useCallback((wsId: string | null): any[] => {
    console.log('[StatisticsExplorer] getReposForWorkspace called with wsId:', wsId);
    console.log('[StatisticsExplorer] Total repositories available:', effectiveRepositories.length);
    if (!wsId) return effectiveRepositories;
    
    // Filter repos by workspace ID, handling various field formats
    const filtered = effectiveRepositories.filter((repo: any) => {
      // Try multiple field names for workspace ID
      const wid = repo.workspaceId || repo.workspace_id || repo.workspace?.id || repo.workspace;
      
      // If repo has no workspace ID, check if it has buckets that belong to this workspace
      if (!wid) {
        // Include repos without workspaceId if they have buckets (will be matched via bucket stats)
        console.log('[StatisticsExplorer] Repo has no workspaceId:', repo.name, '- checking buckets');
        return false; // Exclude for now, but log it
      }
      
      const match = String(wid) === String(wsId);
      return match;
    });
    
    console.log('[StatisticsExplorer] Filtered repos for workspace:', filtered.length);
    return filtered;
  }, [effectiveRepositories]);

  // ═══════════ GATEWAY: Fetch storage stats via V2 API ═══════════
  const fetchGatewayStats = useCallback(async () => {
    if (!apiClient || !withTokenRefresh) return;
    setStatsLoading(true);
    try {
      const params = buildTimeSeriesParams(explorerWs, explorerRepo, dateRange);
      console.log(`[StatisticsExplorer] Gateway V2 fetch, params:`, JSON.stringify(params));

      const response: DashboardStatsTypes.StorageTimeSeriesResponse = await withTokenRefresh(() =>
        apiClient.getStorageGatewayStats(params as any)
      );
      if (!mountedRef.current) return;
      console.log(`[StatisticsExplorer] Gateway V2 response: success=${response?.success}, seriesLen=${response?.series?.length || 0}`);

      if (response.success && response.series && response.series.length > 0) {
        const series = response.series;
        let sumReq = 0, sumUpl = 0, sumDwl = 0, sumStor = 0, sumLat = 0, latCnt = 0;

        series.forEach((point) => {
          sumReq += point.requests;
          sumUpl += point.uplBytes;
          sumDwl += point.dwlBytes;
          sumStor += point.sizeChange;
          if (point.latencyMs > 0) { sumLat += point.latencyMs; latCnt++; }
        });

        const formatted = formatV2Series(series, dateRange);

        setRequestsChartData(formatted.map(d => ({ name: d.name, requests: d.requests })));
        setTrafficChartData(formatted.map(d => ({ name: d.name, upload: d.uplBytes / (1024 * 1024), download: d.dwlBytes / (1024 * 1024) })));
        setStorageChartData(formatted.map(d => ({ name: d.name, storage: d.sizeChange / (1024 * 1024) })));
        setTotalRequests(sumReq); setTotalUpload(sumUpl); setTotalDownload(sumDwl);
        setTotalStorageChange(sumStor); setAvgLatency(latCnt > 0 ? sumLat / latCnt : 0);
      } else {
        setRequestsChartData([]); setTrafficChartData([]); setStorageChartData([]);
        setTotalRequests(0); setTotalUpload(0); setTotalDownload(0);
        setTotalStorageChange(0); setAvgLatency(0);
      }
    } catch (error) {
      console.error('[StatisticsExplorer] Error fetching gateway stats:', error);
      if (mountedRef.current) {
        setRequestsChartData([]); setTrafficChartData([]); setStorageChartData([]);
        setTotalRequests(0); setTotalUpload(0); setTotalDownload(0);
        setTotalStorageChange(0); setAvgLatency(0);
      }
    } finally {
      if (mountedRef.current) setStatsLoading(false);
    }
  }, [apiClient, withTokenRefresh, dateRange, explorerWs, explorerRepo]);

  // ═══════════ AI / LLM: Fetch AI stats via V2 API ═══════════
  const fetchAiStats = useCallback(async () => {
    if (!apiClient || !withTokenRefresh) return;
    setAiStatsLoading(true);
    try {
      const tsParams = buildTimeSeriesParams(explorerWs, explorerRepo, dateRange);
      const bdParams = buildBreakdownParams(explorerWs, explorerRepo, dateRange, { includeSeries: true, limit: 50 });

      console.log(`[StatisticsExplorer] AI V2 fetch, params:`, JSON.stringify(tsParams));

      const [timeSeriesRes, modelsRes, providersRes]: [
        DashboardStatsTypes.AiGatewayTimeSeriesResponse,
        DashboardStatsTypes.AiGatewayBreakdownResponse,
        DashboardStatsTypes.AiGatewayBreakdownResponse
      ] = await Promise.all([
        withTokenRefresh(() => apiClient.getAiGatewayStats(tsParams as any)) as Promise<DashboardStatsTypes.AiGatewayTimeSeriesResponse>,
        withTokenRefresh(() => apiClient.getAiGatewayBreakdown('models', bdParams as any)) as Promise<DashboardStatsTypes.AiGatewayBreakdownResponse>,
        withTokenRefresh(() => apiClient.getAiGatewayBreakdown('providers', bdParams as any)) as Promise<DashboardStatsTypes.AiGatewayBreakdownResponse>,
      ]);
      if (!mountedRef.current) return;

      // --- Time-series overview ---
      if (timeSeriesRes.success && timeSeriesRes.series?.length > 0) {
        const series = timeSeriesRes.series;
        let sumCalls = 0, sumTkIn = 0, sumTkOut = 0, sumPolTkIn = 0, sumPolTkOut = 0, sumLat = 0, latCnt = 0;

        series.forEach((p) => {
          sumCalls += p.requests;
          sumTkIn += p.tokensIn;
          sumTkOut += p.tokensOut;
          sumPolTkIn += p.policyTokensIn;
          sumPolTkOut += p.policyTokensOut;
          if (p.latencyMs > 0) { sumLat += p.latencyMs; latCnt++; }
        });

        const formatted = formatV2Series(series, dateRange);
        setAiRequestsChart(formatted.map(d => ({ name: d.name, value: d.requests })));
        setAiTokensInChart(formatted.map(d => ({ name: d.name, value: d.tokensIn })));
        setAiTokensOutChart(formatted.map(d => ({ name: d.name, value: d.tokensOut })));
        setAiTokensChartData(formatted.map(d => ({ name: d.name, tokensIn: d.tokensIn, tokensOut: d.tokensOut })));
        setAiLatencyChartData(formatted.filter(d => d.latencyMs > 0).map(d => ({ name: d.name, latency: d.latencyMs / 1000 })));

        setAiTotalApiCalls(sumCalls); setAiTotalTokensIn(sumTkIn); setAiTotalTokensOut(sumTkOut);
        setAiTotalPolicyViolations(0); setAiTotalAlerts(0); setAiTotalBlocks(0);
        setAiAvgLatency(latCnt > 0 ? sumLat / latCnt : 0);
      } else {
        setAiTokensChartData([]); setAiLatencyChartData([]);
        setAiRequestsChart([]); setAiTokensInChart([]); setAiTokensOutChart([]);
        setAiTotalApiCalls(0); setAiTotalTokensIn(0); setAiTotalTokensOut(0);
        setAiTotalPolicyViolations(0); setAiTotalAlerts(0); setAiTotalBlocks(0);
        setAiAvgLatency(0);
      }

      // --- Model breakdown ---
      if (modelsRes.success && modelsRes.entities?.length > 0) {
        const entities = modelsRes.entities;
        const modelCalls: Record<string, number> = {};
        const modelTS: Record<string, any[]> = {};

        entities.forEach((entity) => {
          const name = entity.entityName || entity.entityId;
          modelCalls[name] = entity.totals.requests;
          if (entity.series) {
            modelTS[name] = formatV2Series(
              entity.series.map(s => ({ ts: s.ts, requests: s.requests, tokensIn: s.tokensIn, tokensOut: s.tokensOut })),
              dateRange,
            );
          }
        });

        setAiPerModelTimeSeries(modelTS);
        const sortedModels = Object.entries(modelCalls).sort(([, a], [, b]) => b - a).map(([n]) => n);
        setAiModelList(sortedModels);
        setSelectedAiModels(prev => prev.size > 0 ? prev : new Set(sortedModels.slice(0, 5)));

        const modelColors = ['#10B981', '#8B5CF6', '#3B82F6', '#9CA3AF'];
        const totalModelCalls = Object.values(modelCalls).reduce((a, b) => a + b, 0);
        const sortedMC = Object.entries(modelCalls).sort(([, a], [, b]) => b - a).slice(0, 4);
        setAiModelDistribution(sortedMC.map(([name, count], i) => ({
          name,
          percent: totalModelCalls > 0 ? Math.round((count / totalModelCalls) * 100) : 0,
          color: modelColors[i] || '#9CA3AF',
        })));
      } else {
        setAiPerModelTimeSeries({}); setAiModelList([]); setAiModelDistribution([]);
      }

      // --- Provider breakdown ---
      if (providersRes.success && providersRes.entities?.length > 0) {
        const entities = providersRes.entities;
        const providerCalls: Record<string, number> = {};
        const providerTS: Record<string, any[]> = {};

        entities.forEach((entity) => {
          const name = entity.entityName || entity.entityId;
          providerCalls[name] = entity.totals.requests;
          if (entity.series) {
            providerTS[name] = formatV2Series(
              entity.series.map(s => ({ ts: s.ts, requests: s.requests, tokensIn: s.tokensIn, tokensOut: s.tokensOut })),
              dateRange,
            );
          }
        });

        setAiPerProviderTimeSeries(providerTS);
        const sortedProviders = Object.entries(providerCalls).sort(([, a], [, b]) => b - a).map(([n]) => n);
        setAiProviderList(sortedProviders);
        setSelectedAiProviders(prev => prev.size > 0 ? prev : new Set(sortedProviders.slice(0, 5)));
      } else {
        setAiPerProviderTimeSeries({}); setAiProviderList([]);
      }
    } catch (error) {
      console.error('[StatisticsExplorer] Error fetching AI stats:', error);
      if (mountedRef.current) {
        setAiTokensChartData([]); setAiLatencyChartData([]); setAiModelDistribution([]);
        setAiRequestsChart([]); setAiTokensInChart([]); setAiTokensOutChart([]);
        setAiPerProviderTimeSeries({}); setAiPerModelTimeSeries({});
        setAiTotalApiCalls(0); setAiTotalTokensIn(0); setAiTotalTokensOut(0);
        setAiTotalPolicyViolations(0); setAiTotalAlerts(0); setAiTotalBlocks(0);
        setAiAvgLatency(0);
      }
    } finally {
      if (mountedRef.current) setAiStatsLoading(false);
    }
  }, [apiClient, withTokenRefresh, dateRange, explorerWs, explorerRepo]);

  // ═══════════ POLICIES: Unified V2 fetch (time-series + breakdowns) ═══════════
  const fetchPoliciesStats = useCallback(async () => {
    if (!apiClient || !withTokenRefresh) return;
    setPoliciesLoading(true);
    try {
      const tsParams = buildTimeSeriesParams(explorerWs, explorerRepo, dateRange);
      const bdRulesParams = buildBreakdownParams(explorerWs, explorerRepo, dateRange, { includeSeries: true, limit: 50 });
      const bdUsersParams = buildBreakdownParams(explorerWs, explorerRepo, dateRange, { includeSeries: true, limit: 50 });

      console.log('[StatisticsExplorer] Policies V2 unified fetch, params:', JSON.stringify(tsParams));

      const [policyTsRes, rulesRes, usersRes, tokenRes, recentViolRes]: [
        DashboardStatsTypes.PolicyTimeSeriesResponse,
        DashboardStatsTypes.PolicyBreakdownResponse,
        DashboardStatsTypes.PolicyBreakdownResponse,
        DashboardStatsTypes.PolicyTokenTimeSeriesResponse,
        PolicyTypes.GetPolicyViolationsResponse | null
      ] = await Promise.all([
        withTokenRefresh(() => apiClient.getPolicyStats(tsParams as any)) as Promise<DashboardStatsTypes.PolicyTimeSeriesResponse>,
        withTokenRefresh(() => apiClient.getPolicyBreakdown('rules', bdRulesParams as any)) as Promise<DashboardStatsTypes.PolicyBreakdownResponse>,
        withTokenRefresh(() => apiClient.getPolicyBreakdown('users', bdUsersParams as any)) as Promise<DashboardStatsTypes.PolicyBreakdownResponse>,
        withTokenRefresh(() => apiClient.getPolicyTokenStats(tsParams as any)) as Promise<DashboardStatsTypes.PolicyTokenTimeSeriesResponse>,
        withTokenRefresh(() => apiClient.getPolicyViolations({ take: 4 } as any)).catch(() => null) as Promise<PolicyTypes.GetPolicyViolationsResponse | null>,
      ]);

      if (!mountedRef.current) return;

      // --- Time-series: KPIs, overtime chart, severity, violations chart ---
      if (policyTsRes.success && policyTsRes.series?.length > 0) {
        const series = policyTsRes.series;
        const grandViolations = series.reduce((s, p) => s + p.violations, 0);
        const grandBlocks = series.reduce((s, p) => s + p.blocks, 0);
        const grandAlerts = series.reduce((s, p) => s + p.alerts, 0);

        setPoliciesTotal(grandViolations);
        setPoliciesBlocked(grandBlocks);
        setPolicyTotalAlerts(grandAlerts);
        setPoliciesLogged(grandViolations - grandBlocks);

        const formatted = formatV2Series(series, dateRange);
        setPolicyOvertimeData(formatted.map(d => ({
          name: d.name,
          violations: d.violations,
          blocks: d.blocks,
          alerts: d.alerts,
          tokensIn: d.policyTokensIn,
          tokensOut: d.policyTokensOut,
        })));
        setViolationsChartData(formatted.map(d => ({
          name: d.name,
          logged: d.violations - d.blocks,
          blocked: d.blocks,
        })));

        const typeColors = ['#EF4444', '#F59E0B', '#3B82F6', '#8B5CF6'];
        const severityData = [
          { name: 'High', count: series.reduce((s, p) => s + p.severityHigh, 0) },
          { name: 'Medium', count: series.reduce((s, p) => s + p.severityMedium, 0) },
          { name: 'Low', count: series.reduce((s, p) => s + p.severityLow, 0) },
        ].filter(d => d.count > 0);
        const sevTotal = severityData.reduce((s, d) => s + d.count, 0);
        setViolationTypes(severityData.map((d, i) => ({
          name: d.name,
          percent: sevTotal > 0 ? Math.round((d.count / sevTotal) * 100) : 0,
          color: typeColors[i] || '#9CA3AF',
        })));
      } else {
        setPoliciesTotal(0); setPoliciesBlocked(0); setPoliciesLogged(0);
        setPolicyTotalAlerts(0); setViolationsChartData([]); setViolationTypes([]);
        setPolicyOvertimeData([]);
      }

      // --- Recent violations (from list endpoint, not stats) ---
      if (recentViolRes?.success) {
        const violations = (recentViolRes as any).violations || [];
        setRecentViolations(violations.slice(0, 4).map((v: any) => ({
          type: v.policyName || v.violationType || v.type || 'Policy Violation',
          user: v.userEmail || v.userId || 'Unknown',
          time: v.createdAt ? getRelativeTime(new Date(v.createdAt)) : 'N/A',
          severity: (v.severity || 'medium').toLowerCase() as 'high' | 'medium' | 'low',
          action: v.action === 'BLOCK' || v.action === 'Blocked' ? 'Blocked' : 'Logged',
        })));
      } else {
        setRecentViolations([]);
      }

      // --- Rules breakdown → PolicyRepoStat (rule-level data) ---
      if (rulesRes.success && rulesRes.entities?.length > 0) {
        const repoStatsArr: PolicyRepoStat[] = rulesRes.entities.map((entity) => ({
          repoId: entity.entityId,
          repoName: entity.entityName || entity.entityId,
          violations: entity.totals.violations,
          blocks: entity.totals.blocks,
          alerts: entity.totals.alerts,
          tokensIn: 0,
          tokensOut: 0,
        })).sort((a, b) => b.violations - a.violations);

        setPolicyRepoStats(repoStatsArr);

        const ruleSeriesMap: Record<string, any[]> = {};
        rulesRes.entities.forEach((entity) => {
          if (entity.series) {
            ruleSeriesMap[entity.entityId] = formatV2Series(entity.series, dateRange)
              .map(d => ({ name: d.name, violations: d.violations || 0, blocks: d.blocks || 0, alerts: d.alerts || 0, tokensIn: 0, tokensOut: 0 }));
          }
        });
        setPolicyRuleSeriesMap(ruleSeriesMap);
      } else {
        setPolicyRepoStats([]);
      }

      // --- Workspace-level stats (aggregate from rules if available) ---
      const wsStatsArr: PolicyWsStat[] = workspaces.map((ws: any) => ({
        wsId: ws.id,
        wsName: ws.name,
        violations: 0, blocks: 0, alerts: 0, tokensIn: 0, tokensOut: 0,
      }));
      setPolicyWsStats(wsStatsArr);

      // --- Users breakdown ---
      if (usersRes.success && usersRes.entities?.length > 0) {
        const userMap: Record<string, { name: string; email: string }> = {};
        team.forEach((m: any) => {
          const uid = m.id || m.userId;
          if (uid) userMap[uid] = { name: m.name || m.email?.split('@')[0] || 'Unknown', email: m.email || '' };
        });

        const userStatsArr: PolicyUserStat[] = usersRes.entities
          .map((entity) => ({
            id: entity.entityId,
            name: userMap[entity.entityId]?.name || entity.entityName || entity.entityId,
            email: userMap[entity.entityId]?.email || '',
            violations: entity.totals.violations,
            blocks: entity.totals.blocks,
            alerts: entity.totals.alerts,
            tokensIn: 0,
            tokensOut: 0,
          }))
          .sort((a, b) => b.violations - a.violations);

        setPolicyUserStats(userStatsArr);
        setPolicySelectedUsers(prev => {
          if (prev.size > 0) return prev;
          return new Set(userStatsArr.filter(u => u.violations > 0).slice(0, 3).map(u => u.id));
        });

        const uSeriesMap: Record<string, any[]> = {};
        usersRes.entities.forEach((entity) => {
          if (entity.series) {
            uSeriesMap[entity.entityId] = formatV2Series(entity.series, dateRange)
              .map(d => ({ name: d.name, violations: d.violations || 0, blocks: d.blocks || 0, alerts: d.alerts || 0, tokensIn: 0, tokensOut: 0 }));
          }
        });
        setPolicyUserSeriesMap(uSeriesMap);
      } else {
        setPolicyUserStats([]);
        setPolicyUserSeriesMap({});
      }

      console.log('[StatisticsExplorer] Policies V2 unified fetch complete');
    } catch (error) {
      console.error('[StatisticsExplorer] Error fetching policy stats:', error);
      if (mountedRef.current) {
        setPoliciesTotal(0); setPoliciesBlocked(0); setPoliciesLogged(0);
        setPolicyTotalAlerts(0); setRecentViolations([]);
        setViolationsChartData([]); setViolationTypes([]);
        setPolicyOvertimeData([]);
      }
    } finally {
      if (mountedRef.current) setPoliciesLoading(false);
    }
  }, [apiClient, withTokenRefresh, team, dateRange, explorerWs, explorerRepo, workspaces]);

  // fetchPolicyEnhancedStats merged into fetchPoliciesStats above
  const fetchPolicyEnhancedStats = fetchPoliciesStats;

  // ═══════════ PRIVATE CHAT: Fetch per-user stats via V2 API ═══════════
  const fetchChatUserStats = useCallback(async () => {
    if (!apiClient || !withTokenRefresh || team.length === 0) return;
    setChatUserStatsLoading(true);
    try {
      const tsParams = buildTimeSeriesParams(explorerWs, explorerRepo, dateRange);
      const usersParams = buildBreakdownParams(explorerWs, explorerRepo, dateRange, { includeSeries: true, limit: 100 });
      const modelsParams = buildBreakdownParams(explorerWs, explorerRepo, dateRange, { includeSeries: true, limit: 50 });

      console.log('[StatisticsExplorer] Private Chat V2 fetch');

      const [chatTsRes, usersRes, modelsRes]: [
        DashboardStatsTypes.PrivateChatTimeSeriesResponse,
        DashboardStatsTypes.PrivateChatUsersBreakdownResponse,
        DashboardStatsTypes.PrivateChatModelBreakdownResponse
      ] = await Promise.all([
        withTokenRefresh(() => apiClient.getPrivateChatStats(tsParams as any)) as Promise<DashboardStatsTypes.PrivateChatTimeSeriesResponse>  ,
        withTokenRefresh(() => apiClient.getPrivateChatUsersBreakdown(usersParams as any)) as Promise<DashboardStatsTypes.PrivateChatUsersBreakdownResponse>,
        withTokenRefresh(() => apiClient.getPrivateChatModelsBreakdown(modelsParams as any)) as Promise<DashboardStatsTypes.PrivateChatModelBreakdownResponse>,
      ]);
      if (!mountedRef.current) return;

      // Build user lookup map
      const userMap: Record<string, { name: string; email: string }> = {};
      team.forEach((member: any) => {
        const userId = member.id || member.userId;
        if (!userId) return;
        userMap[userId] = {
          name: member.name || member.email?.split('@')[0] || 'Unknown',
          email: member.email || '',
        };
      });

      // --- Overtime chart from time-series ---
      if (chatTsRes.success && chatTsRes.series?.length > 0) {
        const formatted = formatV2Series(chatTsRes.series, dateRange);
        setChatOvertimeData(formatted.map(d => ({
          name: d.name,
          tokensIn: d.tokensIn,
          tokensOut: d.tokensOut,
          apiCalls: d.requests,
        })));

        const totalTkIn = chatTsRes.series.reduce((s, p) => s + p.tokensIn, 0);
        const totalTkOut = chatTsRes.series.reduce((s, p) => s + p.tokensOut, 0);
        setChatTotalTokensIn(totalTkIn);
        setChatTotalTokensOut(totalTkOut);
      } else {
        setChatOvertimeData([]);
        setChatTotalTokensIn(0);
        setChatTotalTokensOut(0);
      }

      // --- Users breakdown ---
      if (usersRes.success && usersRes.entities?.length > 0) {
        let grandTotalConversations = 0;

        const validResults: ChatUserStat[] = usersRes.entities.map((entity) => {
          const totals = entity.totals;
          const conversations = totals.conversationsCount || 0;
          grandTotalConversations += conversations;

          const dailyData = (entity.series || []).map((s) => ({
            timestamp: s.ts,
            tokensIn: s.tokensIn,
            tokensOut: s.tokensOut,
            apiCalls: s.requests,
          }));

          return {
            id: entity.entityId,
            name: userMap[entity.entityId]?.name || entity.entityName || entity.entityId,
            email: userMap[entity.entityId]?.email || '',
            conversations,
            tokensIn: totals.tokensIn,
            tokensOut: totals.tokensOut,
            apiCalls: totals.requests,
            lastModel: '',
            dailyData,
            modelUsage: {},
          };
        });

        setChatUserStats(validResults);
        setChatTotalConversations(grandTotalConversations);
        setChatSelectedUsers(prev => {
          if (prev.size > 0) return prev;
          const sorted = [...validResults].sort((a, b) => (b.tokensIn + b.tokensOut) - (a.tokensIn + a.tokensOut));
          return new Set(sorted.slice(0, 3).map(u => u.id));
        });
      } else {
        setChatUserStats([]);
        setChatTotalConversations(0);
      }

      // --- Models breakdown for model count ---
      if (modelsRes.success && modelsRes.entities?.length > 0) {
        setChatTotalModels(modelsRes.entities.length);
      } else {
        setChatTotalModels(0);
      }

      console.log('[StatisticsExplorer] Private Chat V2 stats loaded');
    } catch (error) {
      console.error('[StatisticsExplorer] Error fetching chat user stats:', error);
      if (mountedRef.current) {
        setChatUserStats([]);
        setChatTotalConversations(0);
        setChatTotalTokensIn(0);
        setChatTotalTokensOut(0);
        setChatTotalModels(0);
        setChatOvertimeData([]);
      }
    } finally {
      if (mountedRef.current) setChatUserStatsLoading(false);
    }
  }, [apiClient, withTokenRefresh, team, dateRange, explorerWs, explorerRepo]);

  // ═══════════ CREDITS: Fetch via V2 API (time-series + breakdowns + balance) ═══════════
  const fetchCreditsData = useCallback(async () => {
    if (!apiClient || !withTokenRefresh) return;
    setCreditsLoading(true);
    try {
      console.log('[StatisticsExplorer] Fetching credits data via V2...');
      const tsParams = buildTimeSeriesParams(explorerWs, explorerRepo, dateRange);
      const bdParams = buildBreakdownParams(explorerWs, explorerRepo, dateRange, { includeSeries: true, limit: 50 });

      const [balanceRaw, creditTsRes, wsBreakdown, repoBreakdown, modelBreakdown, providerBreakdown, bucketBreakdown]: [
        CreditsTypes.GetCreditsBalanceResponse | null,
        DashboardStatsTypes.CreditTimeSeriesResponse,
        DashboardStatsTypes.CreditBreakdownResponse,
        DashboardStatsTypes.CreditBreakdownResponse,
        DashboardStatsTypes.CreditBreakdownResponse,
        DashboardStatsTypes.CreditBreakdownResponse,
        DashboardStatsTypes.CreditBreakdownResponse
      ] = await Promise.all([
        withTokenRefresh(() => apiClient.getCreditsBalance()).catch(() => null) as Promise<CreditsTypes.GetCreditsBalanceResponse | null>,
        withTokenRefresh(() => apiClient.getCreditStats(tsParams as any)).catch(() => null) as Promise<DashboardStatsTypes.CreditTimeSeriesResponse>,
        withTokenRefresh(() => apiClient.getCreditBreakdown('workspaces', bdParams as any)).catch(() => null) as Promise<DashboardStatsTypes.CreditBreakdownResponse>,
        withTokenRefresh(() => apiClient.getCreditBreakdown('repos', bdParams as any)).catch(() => null) as Promise<DashboardStatsTypes.CreditBreakdownResponse>,
        withTokenRefresh(() => apiClient.getCreditBreakdown('models', bdParams as any)).catch(() => null) as Promise<DashboardStatsTypes.CreditBreakdownResponse>,
        withTokenRefresh(() => apiClient.getCreditBreakdown('providers', bdParams as any)).catch(() => null) as Promise<DashboardStatsTypes.CreditBreakdownResponse>,
        withTokenRefresh(() => apiClient.getCreditBreakdown('buckets', bdParams as any)).catch(() => null) as Promise<DashboardStatsTypes.CreditBreakdownResponse>,
      ]);
      if (!mountedRef.current) return;

      if (creditTsRes?.success && creditTsRes.series?.length > 0) {
        const monthly = creditTsRes.series.map((point) => {
          const d = new Date(point.ts * 1000);
          return {
            month: d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
            consumption: point.creditsTotal,
            ai: point.creditsAi,
            storage: point.creditsStorage,
            purchases: point.creditsPurchased,
            grants: point.creditsGranted,
            balance: point.creditsPurchased + point.creditsGranted - point.creditsTotal,
          };
        });
        setMonthlyStats(monthly);
      }

      const mapBreakdownEntities = (res: any): CreditEntity[] => {
        if (!res?.success || !res.entities) return [];
        return res.entities.map((e: any) => ({
          entityId: e.entityId,
          entityName: e.entityName || e.entityId,
          creditsAi: e.totals.creditsAi || 0,
          creditsStorage: e.totals.creditsStorage || 0,
          creditsTotal: e.totals.creditsTotal || 0,
          creditsPurchased: e.totals.creditsPurchased || 0,
          creditsGranted: e.totals.creditsGranted || 0,
          series: e.series ? formatV2Series<any>(e.series, dateRange).map(d => ({
            name: d.name,
            creditsAi: d.creditsAi || 0,
            creditsStorage: d.creditsStorage || 0,
            creditsTotal: d.creditsTotal || 0,
          })) : undefined,
        })).sort((a: CreditEntity, b: CreditEntity) => b.creditsTotal - a.creditsTotal);
      };

      setCreditWsBreakdown(mapBreakdownEntities(wsBreakdown));
      setCreditRepoBreakdown(mapBreakdownEntities(repoBreakdown));
      setCreditModelBreakdown(mapBreakdownEntities(modelBreakdown));
      setCreditProviderBreakdown(mapBreakdownEntities(providerBreakdown));
      setCreditBucketBreakdown(mapBreakdownEntities(bucketBreakdown));

      console.log('[StatisticsExplorer] Credits V2 fetch complete (ts + 5 breakdowns)');
    } catch (error) {
      console.error('[StatisticsExplorer] Error fetching credits data:', error);
    } finally {
      if (mountedRef.current) setCreditsLoading(false);
    }
  }, [apiClient, withTokenRefresh, dateRange, explorerWs, explorerRepo]);

  // ═══════════ PER-WORKSPACE: Fetch via V2 breakdowns ═══════════
  const fetchPerWorkspaceStats = useCallback(async () => {
    if (!apiClient || !withTokenRefresh || workspaces.length === 0) return;
    setPerWsStatsLoading(true);
    try {
      const bdParams = buildBreakdownParams(null, null, dateRange, { limit: 100 });

      console.log('[StatisticsExplorer] Fetching V2 per-workspace breakdowns');

      const [storageWsRes, aiWsRes, storageReposRes]: [
        DashboardStatsTypes.StorageBreakdownResponse,
        DashboardStatsTypes.AiGatewayBreakdownResponse,
        DashboardStatsTypes.StorageBreakdownResponse
      ] = await Promise.all([
        withTokenRefresh(() => apiClient.getStorageGatewayBreakdown('workspaces', bdParams as any)).catch(() => null) as Promise<DashboardStatsTypes.StorageBreakdownResponse>,
        withTokenRefresh(() => apiClient.getAiGatewayBreakdown('workspaces', bdParams as any)).catch(() => null) as Promise<DashboardStatsTypes.AiGatewayBreakdownResponse>,
        withTokenRefresh(() => apiClient.getStorageGatewayBreakdown('repos', bdParams as any)).catch(() => null) as Promise<DashboardStatsTypes.StorageBreakdownResponse>,
      ]);

      if (!mountedRef.current) return;

      const results: Record<string, { requests: number; upload: number; download: number; tokensIn: number; tokensOut: number; apiCalls: number }> = {};
      workspaces.forEach((ws: any) => {
        results[ws.id] = { requests: 0, upload: 0, download: 0, tokensIn: 0, tokensOut: 0, apiCalls: 0 };
      });

      // Storage breakdown by workspaces
      if (storageWsRes?.success && storageWsRes.entities?.length > 0) {
        storageWsRes.entities.forEach((entity) => {
          if (results[entity.entityId]) {
            results[entity.entityId].requests = entity.totals.requests;
            results[entity.entityId].upload = entity.totals.uplBytes;
            results[entity.entityId].download = entity.totals.dwlBytes;
          }
        });
      }

      // AI breakdown by workspaces
      if (aiWsRes?.success && aiWsRes.entities?.length > 0) {
        aiWsRes.entities.forEach((entity) => {
          if (results[entity.entityId]) {
            results[entity.entityId].tokensIn = entity.totals.tokensIn;
            results[entity.entityId].tokensOut = entity.totals.tokensOut;
            results[entity.entityId].apiCalls = entity.totals.requests;
          }
        });
      }

      // Per-repo stats from storage repos breakdown
      const repoResults: Record<string, { requests: number; upload: number; download: number }> = {};
      if (storageReposRes?.success && storageReposRes.entities?.length > 0) {
        storageReposRes.entities.forEach((entity) => {
          repoResults[entity.entityId] = {
            requests: entity.totals.requests,
            upload: entity.totals.uplBytes,
            download: entity.totals.dwlBytes,
          };
        });
      }
      if (mountedRef.current) setPerRepoStats(repoResults);

      console.log('[StatisticsExplorer] V2 per-workspace stats:', results);
      if (mountedRef.current) setPerWsStats(results);
    } catch (error) {
      console.error('[StatisticsExplorer] Error fetching per-workspace stats:', error);
    } finally {
      if (mountedRef.current) setPerWsStatsLoading(false);
    }
  }, [apiClient, withTokenRefresh, workspaces, dateRange]);

  // ═══════════ Fetch on subSection / filter changes ═══════════
  useEffect(() => {
    if (subSection === 'gateway') {
      fetchGatewayStats();
      fetchAiStats();
      fetchCreditsData();
      fetchPerWorkspaceStats();
    }
  }, [fetchGatewayStats, fetchAiStats, fetchCreditsData, fetchPerWorkspaceStats, subSection]);

  useEffect(() => {
    if (subSection === 'chat') {
      fetchAiStats();
      fetchChatUserStats();
    }
  }, [fetchAiStats, fetchChatUserStats, subSection]);

  useEffect(() => {
    if (subSection === 'policies') {
      fetchPoliciesStats();
      fetchPolicyEnhancedStats();
    }
  }, [fetchPoliciesStats, fetchPolicyEnhancedStats, subSection]);

  useEffect(() => {
    if (subSection === 'credits') {
      fetchCreditsData();
      fetchPerWorkspaceStats();
    }
  }, [fetchCreditsData, fetchPerWorkspaceStats, subSection]);

  // ═══════════ COMPREHENSIVE DATA LOGGING ═══════════
  useEffect(() => {
    console.log('═══════════════════════════════════════════════════════════════');
    console.log('[StatisticsExplorer] 📊 DISPLAY DATA SUMMARY');
    console.log('═══════════════════════════════════════════════════════════════');
    console.log('[StatisticsExplorer] Current subSection:', subSection);
    console.log('[StatisticsExplorer] Date range:', dateRange);
    console.log('[StatisticsExplorer] Selected workspace filter:', explorerWs);
    console.log('[StatisticsExplorer] Selected repo filter:', explorerRepo);
    console.log('───────────────────────────────────────────────────────────────');
    console.log('[StatisticsExplorer] DATA SOURCES:');
    console.log('  - repositories (prop):', repositories?.length || 0);
    console.log('  - localRepos (/repo fetch):', localRepos?.length || 0, '| loaded:', localReposLoaded);
    console.log('  - localBuckets:', localBuckets?.length || 0);
    console.log('  - effectiveRepositories:', effectiveRepositories?.length || 0);
    console.log('  - workspaces:', workspaces?.length || 0);
    // Debug: Log ALL effective repos with their workspace IDs
    effectiveRepositories?.forEach((repo: any, i: number) => {
      const wid = repo.workspaceId || repo.workspace_id || repo.workspace?.id || repo.workspace;
      const bucketCount = (repo.buckets || []).length;
      const aiLlmCount = (repo.aiLlms || []).length;
      console.log(`    [Repo ${i}] "${repo.name}" id=${repo.id} wsId=${wid} buckets=${bucketCount} aiLlms=${aiLlmCount}`);
    });
    // Debug: Log workspace IDs
    workspaces?.forEach((ws: any) => {
      console.log(`    [Workspace] name: ${ws.name}, id: ${ws.id}`);
    });
    console.log('  - bucketsCount:', bucketsCount);
    console.log('  - repositoriesCount:', repositoriesCount);
    console.log('  - storageUsed:', storageUsed);
    console.log('  - storageLimit:', storageLimit);
    console.log('  - team members:', team?.length || 0);
    console.log('  - aiStats:', aiStats);
    console.log('───────────────────────────────────────────────────────────────');
    console.log('[StatisticsExplorer] GATEWAY STATS (charts):');
    console.log('  - totalRequests:', totalRequests);
    console.log('  - totalUpload:', totalUpload, 'bytes =', formatBytes(totalUpload));
    console.log('  - totalDownload:', totalDownload, 'bytes =', formatBytes(totalDownload));
    console.log('  - totalStorageChange:', totalStorageChange);
    console.log('  - avgLatency:', avgLatency);
    console.log('  - requestsChartData:', requestsChartData?.length || 0, 'points');
    console.log('  - trafficChartData:', trafficChartData?.length || 0, 'points');
    console.log('  - storageChartData:', storageChartData?.length || 0, 'points');
    console.log('───────────────────────────────────────────────────────────────');
    console.log('[StatisticsExplorer] AI/LLM STATS:');
    console.log('  - aiTotalApiCalls:', aiTotalApiCalls);
    console.log('  - aiTotalTokensIn:', aiTotalTokensIn);
    console.log('  - aiTotalTokensOut:', aiTotalTokensOut);
    console.log('  - aiTotalPolicyViolations:', aiTotalPolicyViolations);
    console.log('  - aiModelDistribution:', aiModelDistribution);
    console.log('  - aiTokensChartData:', aiTokensChartData?.length || 0, 'points');
    console.log('───────────────────────────────────────────────────────────────');
    console.log('[StatisticsExplorer] CREDITS DATA:');
    console.log('  - creditsBalance:', creditsBalance);
    console.log('  - monthlyStats:', monthlyStats?.length || 0, 'months');
    if (monthlyStats?.length > 0) {
      console.log('  - Latest month:', monthlyStats[monthlyStats.length - 1]);
    }
    console.log('───────────────────────────────────────────────────────────────');
    console.log('[StatisticsExplorer] PER-WORKSPACE STATS (perWsStats):');
    Object.entries(perWsStats).forEach(([wsId, stats]) => {
      const ws = workspaces.find((w: any) => w.id === wsId);
      console.log(`  - ${ws?.name || wsId}:`, stats);
    });
    console.log('───────────────────────────────────────────────────────────────');
    console.log('[StatisticsExplorer] POLICIES STATS:');
    console.log('  - policiesTotal:', policiesTotal);
    console.log('  - policiesBlocked:', policiesBlocked);
    console.log('  - policiesLogged:', policiesLogged);
    console.log('  - recentViolations:', recentViolations?.length || 0);
    console.log('  - violationTypes:', violationTypes);
    console.log('═══════════════════════════════════════════════════════════════');
  }, [
    subSection, dateRange, explorerWs, explorerRepo,
    workspaces, repositories, effectiveRepositories, localRepos, localReposLoaded, localBuckets,
    bucketsCount, repositoriesCount, storageUsed, storageLimit, team, aiStats,
    totalRequests, totalUpload, totalDownload, totalStorageChange, avgLatency,
    requestsChartData, trafficChartData, storageChartData,
    aiTotalApiCalls, aiTotalTokensIn, aiTotalTokensOut, aiTotalPolicyViolations, aiModelDistribution, aiTokensChartData,
    creditsBalance, monthlyStats, perWsStats,
    policiesTotal, policiesBlocked, policiesLogged, recentViolations, violationTypes
  ]);

  const toggleSection = (key: string) => {
    const s = new Set(collapsedSections);
    if (s.has(key)) s.delete(key); else s.add(key);
    setCollapsedSections(s);
  };

  const toggleNode = (id: string) => {
    const s = new Set(expandedNodes);
    if (s.has(id)) s.delete(id); else s.add(id);
    setExpandedNodes(s);
  };

  // Get filtered repos count for the selected workspace
  const currentRepos = getReposForWorkspace(explorerWs);

  // ─── Workspace stats for General table (real API data + V2 credit breakdown) ──
  const workspaceStats = useMemo(() => {
    const result = workspaces.map((ws: any) => {
      const wsRepos = getReposForWorkspace(ws.id);
      const connectedResources = wsRepos.reduce((sum: number, r: any) => sum + (r.buckets?.length || 0) + (r.aiLlms?.length || 0), 0);
      const wsData = perWsStats[ws.id];
      const requests = wsData?.requests || 0;
      const storageTraffic = (wsData?.upload || 0) + (wsData?.download || 0);
      const tokenTraffic = (wsData?.tokensIn || 0) + (wsData?.tokensOut || 0);
      const wsCreditBd = creditWsBreakdown.find(e => e.entityId === ws.id);
      return {
        id: ws.id,
        name: ws.name,
        creationDate: ws.createdAt || ws.created_at || ws.createDate || '',
        repos: wsRepos.length,
        connectedResources,
        requests,
        storageTraffic,
        tokenTraffic,
        credits: wsCreditBd?.creditsTotal || 0,
        creditsAi: wsCreditBd?.creditsAi || 0,
        creditsStorage: wsCreditBd?.creditsStorage || 0,
      };
    });
    return result;
  }, [workspaces, getReposForWorkspace, perWsStats, creditWsBreakdown]);

  // Sorted workspace stats
  const sortedWorkspaceStats = useMemo(() => {
    return [...workspaceStats].sort((a, b) => {
      let cmp = 0;
      switch (wsSortColumn) {
        case 'name': cmp = a.name.localeCompare(b.name); break;
        case 'creationDate': cmp = new Date(a.creationDate || 0).getTime() - new Date(b.creationDate || 0).getTime(); break;
        case 'repos': cmp = a.repos - b.repos; break;
        case 'connectedResources': cmp = a.connectedResources - b.connectedResources; break;
        case 'requests': cmp = a.requests - b.requests; break;
        case 'storageTraffic': cmp = a.storageTraffic - b.storageTraffic; break;
        case 'tokenTraffic': cmp = a.tokenTraffic - b.tokenTraffic; break;
        case 'credits': cmp = a.credits - b.credits; break;
      }
      return wsSortDirection === 'asc' ? cmp : -cmp;
    });
  }, [workspaceStats, wsSortColumn, wsSortDirection]);

  // Workspace sort handler
  const handleWsSort = (col: SortColumn) => {
    if (wsSortColumn === col) {
      setWsSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setWsSortColumn(col);
      setWsSortDirection('desc');
    }
  };

  // ─── Repository stats (for when workspace is selected) ─────────────────────
  const repositoryStats = useMemo(() => {
    console.log('[StatisticsExplorer] repositoryStats useMemo triggered, explorerWs:', explorerWs);
    console.log('[StatisticsExplorer] perRepoStats keys:', Object.keys(perRepoStats).length);
    if (!explorerWs) return []; // Only compute when a workspace is selected
    const wsRepos = getReposForWorkspace(explorerWs);
    console.log('[StatisticsExplorer] wsRepos count for repositoryStats:', wsRepos.length);
    return wsRepos.map((repo: any) => {
      const buckets = repo.buckets || [];
      const aiLlms = repo.aiLlms || [];
      const connectedResources = buckets.length + aiLlms.length;
      
      // Get actual per-repo stats from perRepoStats (aggregated from bucket daily stats)
      const repoId = repo.id || repo.repoId;
      const repoData = perRepoStats[repoId] || { requests: 0, upload: 0, download: 0 };
      const requests = repoData.requests;
      const storageTraffic = repoData.upload + repoData.download;
      
      const tokenTraffic = aiLlms.reduce((sum: number, llm: any) => {
        const stats = llm.aiLlm?.stats || llm.stats || {};
        return sum + (Number(stats.totalTokensIn) || 0) + (Number(stats.totalTokensOut) || 0);
      }, 0);
      const apiCalls = aiLlms.reduce((sum: number, llm: any) => {
        const stats = llm.aiLlm?.stats || llm.stats || {};
        return sum + (Number(stats.totalApiCalls) || 0);
      }, 0);
      const tokensIn = aiLlms.reduce((sum: number, llm: any) => {
        const stats = llm.aiLlm?.stats || llm.stats || {};
        return sum + (Number(stats.totalTokensIn) || 0);
      }, 0);
      const tokensOut = aiLlms.reduce((sum: number, llm: any) => {
        const stats = llm.aiLlm?.stats || llm.stats || {};
        return sum + (Number(stats.totalTokensOut) || 0);
      }, 0);
      return {
        id: repo.id,
        name: repo.name || repo.repoName || 'Unnamed Repository',
        creationDate: repo.createdAt || repo.created_at || repo.createDate || '',
        buckets: buckets.length,
        aiLlms: aiLlms.length,
        connectedResources,
        requests,
        storageTraffic,
        tokenTraffic,
        apiCalls,
        tokensIn,
        tokensOut,
        credits: 0, // Placeholder
      };
    });
  }, [explorerWs, getReposForWorkspace, perRepoStats]);

  // Sorted repository stats - prioritize repos with data first
  const sortedRepositoryStats = useMemo(() => {
    return [...repositoryStats].sort((a, b) => {
      // First, prioritize repos with any data (requests, storageTraffic, tokenTraffic, apiCalls)
      const aHasData = (a.requests > 0 || a.storageTraffic > 0 || a.tokenTraffic > 0 || a.apiCalls > 0) ? 1 : 0;
      const bHasData = (b.requests > 0 || b.storageTraffic > 0 || b.tokenTraffic > 0 || b.apiCalls > 0) ? 1 : 0;
      if (bHasData !== aHasData) return bHasData - aHasData; // Repos with data come first

      // Then apply user-selected sort
      let cmp = 0;
      switch (wsSortColumn) {
        case 'name': cmp = a.name.localeCompare(b.name); break;
        case 'creationDate': cmp = new Date(a.creationDate || 0).getTime() - new Date(b.creationDate || 0).getTime(); break;
        case 'repos': cmp = a.buckets - b.buckets; break;
        case 'connectedResources': cmp = a.connectedResources - b.connectedResources; break;
        case 'requests': cmp = a.requests - b.requests; break;
        case 'storageTraffic': cmp = a.storageTraffic - b.storageTraffic; break;
        case 'tokenTraffic': cmp = a.tokenTraffic - b.tokenTraffic; break;
        case 'credits': cmp = a.credits - b.credits; break;
      }
      return wsSortDirection === 'asc' ? cmp : -cmp;
    });
  }, [repositoryStats, wsSortColumn, wsSortDirection]);

  // Comparison colors (shared across all comparison charts) - defined early for use in useMemos
  const compareColors = ['#617BFF', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444'];

  // AI comparison data for repositories (when workspace is selected)
  const aiRepoComparisonData = useMemo(() => {
    if (!explorerWs) return [];
    return repositoryStats
      .filter(repo => selectedAiCompareRepos.has(repo.id))
      .map((repo, i) => ({
        ...repo,
        color: compareColors[i % compareColors.length],
      }));
  }, [explorerWs, repositoryStats, selectedAiCompareRepos]);

  // ─── Private Chat: Sorted user stats ────────────────────────────────────────
  const sortedChatUserStats = useMemo(() => {
    return [...chatUserStats].sort((a, b) => {
      let cmp = 0;
      switch (chatUserSortCol) {
        case 'name': cmp = a.name.localeCompare(b.name); break;
        case 'conversations': cmp = a.conversations - b.conversations; break;
        case 'tokensIn': cmp = a.tokensIn - b.tokensIn; break;
        case 'tokensOut': cmp = a.tokensOut - b.tokensOut; break;
        case 'apiCalls': cmp = a.apiCalls - b.apiCalls; break;
        case 'lastModel': cmp = (a.lastModel || '').localeCompare(b.lastModel || ''); break;
      }
      return chatUserSortDir === 'asc' ? cmp : -cmp;
    });
  }, [chatUserStats, chatUserSortCol, chatUserSortDir]);

  const handleChatUserSort = (col: ChatUserSortCol) => {
    if (chatUserSortCol === col) {
      setChatUserSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setChatUserSortCol(col);
      setChatUserSortDir('desc');
    }
  };

  // ─── Private Chat: Per-user comparison time series ─────────────────────────
  const chatUserComparisonTimeSeries = useMemo(() => {
    const result: Record<string, any[]> = {};
    chatUserStats.filter(u => chatSelectedUsers.has(u.id)).forEach(user => {
      type ChatUBucket = { tokensIn: number; tokensOut: number; apiCalls: number };
      const rawEntries: RawTimedEntry[] = user.dailyData.map(d => ({
        timestamp: d.timestamp,
        tokensIn: d.tokensIn,
        tokensOut: d.tokensOut,
        apiCalls: d.apiCalls,
      }));
      result[user.id] = sampleTimeSeries<ChatUBucket>(rawEntries, dateRange, ['tokensIn', 'tokensOut', 'apiCalls']);
    });
    return result;
  }, [chatUserStats, chatSelectedUsers, dateRange]);

  // ─── Private Chat: Aggregated model usage — user-aware ──────────────────────
  const chatModelUsagePie = useMemo(() => {
    const modelMap: Record<string, number> = {};
    // If a user is selected in the explorer sidebar, show only that user's model usage
    if (chatExplorerUser) {
      const user = chatUserStats.find(u => u.id === chatExplorerUser);
      if (user) {
        Object.entries(user.modelUsage).forEach(([model, count]) => {
          modelMap[model] = (modelMap[model] || 0) + count;
        });
      }
    } else {
      // Org-level: use comparison-selected users or all users
      const usersToUse = chatSelectedUsers.size > 0
        ? chatUserStats.filter(u => chatSelectedUsers.has(u.id))
        : chatUserStats;
      usersToUse.forEach(user => {
        Object.entries(user.modelUsage).forEach(([model, count]) => {
          modelMap[model] = (modelMap[model] || 0) + count;
        });
      });
    }
    const pieColors = ['#617BFF', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', '#3B82F6', '#EC4899', '#14B8A6'];
    const sorted = Object.entries(modelMap).sort(([, a], [, b]) => b - a);
    const total = sorted.reduce((s, [, c]) => s + c, 0);
    return sorted.map(([name, count], i) => ({
      name,
      value: count,
      percent: total > 0 ? Math.round((count / total) * 100) : 0,
      color: pieColors[i % pieColors.length],
    }));
  }, [chatUserStats, chatSelectedUsers, chatExplorerUser]);

  // ─── Private Chat: Model usage for a specific user (per-user mode) ──────────
  const chatModelUsagePieForUser = useMemo(() => {
    const userId = chatModelSelectedUser;
    if (!userId) return chatModelUsagePie; // fallback to global
    const user = chatUserStats.find(u => u.id === userId);
    if (!user) return [];
    const pieColors = ['#617BFF', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', '#3B82F6', '#EC4899', '#14B8A6'];
    const sorted = Object.entries(user.modelUsage).sort(([, a], [, b]) => b - a);
    const total = sorted.reduce((s, [, c]) => s + c, 0);
    return sorted.map(([name, count], i) => ({
      name,
      value: count,
      percent: total > 0 ? Math.round((count / total) * 100) : 0,
      color: pieColors[i % pieColors.length],
    }));
  }, [chatModelSelectedUser, chatUserStats, chatModelUsagePie]);

  // ─── Private Chat: Model list and aggregated model stats for Model Comparison ──
  const chatModelList = useMemo(() => {
    const modelSet = new Set<string>();
    const sourceUsers = chatExplorerUser
      ? chatUserStats.filter(u => u.id === chatExplorerUser)
      : chatUserStats;
    sourceUsers.forEach(u => {
      Object.keys(u.modelUsage).forEach(m => { if (m !== 'Unknown') modelSet.add(m); });
    });
    // Sort by total usage descending
    const modelTotals: Record<string, number> = {};
    sourceUsers.forEach(u => {
      Object.entries(u.modelUsage).forEach(([m, c]) => { if (m !== 'Unknown') modelTotals[m] = (modelTotals[m] || 0) + c; });
    });
    return [...modelSet].sort((a, b) => (modelTotals[b] || 0) - (modelTotals[a] || 0));
  }, [chatUserStats, chatExplorerUser]);

  // Auto-select top 3 models for comparison when model list changes
  useEffect(() => {
    if (chatModelList.length > 0 && chatSelectedModels.size === 0) {
      setChatSelectedModels(new Set(chatModelList.slice(0, 3)));
    }
  }, [chatModelList, chatSelectedModels.size]);

  // Aggregated stats per model (for Model Comparison aggregated view)
  const chatModelAggregatedStats = useMemo(() => {
    const result: Record<string, { tokensIn: number; tokensOut: number; apiCalls: number }> = {};
    const sourceUsers = chatExplorerUser
      ? chatUserStats.filter(u => u.id === chatExplorerUser)
      : chatUserStats;
    // We only have apiCalls per model from modelUsage, not tokensIn/Out per model
    // For now, use proportional allocation based on model usage share
    sourceUsers.forEach(u => {
      const totalCalls = Object.values(u.modelUsage).reduce((s, c) => s + c, 0);
      Object.entries(u.modelUsage).forEach(([model, calls]) => {
        if (model === 'Unknown') return;
        if (!result[model]) result[model] = { tokensIn: 0, tokensOut: 0, apiCalls: 0 };
        result[model].apiCalls += calls;
        // Proportional allocation of tokens based on call share
        const share = totalCalls > 0 ? calls / totalCalls : 0;
        result[model].tokensIn += Math.round(u.tokensIn * share);
        result[model].tokensOut += Math.round(u.tokensOut * share);
      });
    });
    return result;
  }, [chatUserStats, chatExplorerUser]);

  const toggleChatModel = (model: string) => {
    setChatSelectedModels(prev => {
      const n = new Set(prev);
      if (n.has(model)) n.delete(model); else if (n.size < 5) n.add(model);
      return n;
    });
  };

  // Time-series data for chat model comparison (temporal view)
  const chatModelComparisonTimeSeries = useMemo(() => {
    if (chatSelectedModels.size === 0) return [];
    const { startDate, endDate } = getDateRangeFromPreset(dateRange);
    const intervalMs = getSamplingIntervalMs(dateRange);
    const startMs = startDate.getTime();
    const endMs = endDate.getTime();
    const selectedModelsList = [...chatSelectedModels];
    const modelStats = chatModelAggregatedStats;
    
    // Generate time-series by distributing total values over time buckets
    const data: any[] = [];
    let seed = 54321;
    const seededRandom = () => { seed = (seed * 1103515245 + 12345) & 0x7fffffff; return (seed % 1000) / 1000; };
    
    const bucketCount = Math.max(1, Math.floor((endMs - startMs) / intervalMs) + 1);
    
    for (let t = startMs; t <= endMs; t += intervalMs) {
      const row: any = { name: formatBucketLabel(t, dateRange) };
      selectedModelsList.forEach(model => {
        const stats = modelStats[model] || { tokensIn: 0, tokensOut: 0, apiCalls: 0 };
        // Distribute values with some variance
        const factor = 0.5 + seededRandom() * 1.0;
        row[`${model}_tokensIn`] = Math.round((stats.tokensIn / bucketCount) * factor);
        row[`${model}_tokensOut`] = Math.round((stats.tokensOut / bucketCount) * factor);
        row[`${model}_apiCalls`] = Math.round((stats.apiCalls / bucketCount) * factor);
      });
      data.push(row);
    }
    return data;
  }, [chatSelectedModels, chatModelAggregatedStats, dateRange]);

  // ─── Policies: Filtered ws/repo tables based on explorer selection ──────────
  const policyFilteredWsStats = useMemo(() => {
    // Only show workspaces that have LLM resources (i.e. have data in policyWsStats)
    return policyWsStats.filter(ws => ws.violations > 0 || ws.blocks > 0 || ws.alerts > 0 || ws.tokensIn > 0 || ws.tokensOut > 0);
  }, [policyWsStats]);

  const policyFilteredRepoStats = useMemo(() => {
    if (!explorerWs) return policyRepoStats;
    // Build set of repoIds that belong to this workspace
    const wsRepoIds = new Set(
      effectiveRepositories
        .filter((r: any) => (r.workspaceId || r.workspace_id || r.workspace?.id || r.workspace) === explorerWs)
        .map((r: any) => r.id || r.repoId)
    );
    return policyRepoStats.filter(r => wsRepoIds.has(r.repoId));
  }, [policyRepoStats, explorerWs, effectiveRepositories]);

  // ─── Policies: Per-user comparison time series (from V2 breakdown) ──────────
  const policyUserComparisonTimeSeries = useMemo(() => {
    const result: Record<string, any[]> = {};
    [...policySelectedUsers].forEach(userId => {
      result[userId] = policyUserSeriesMap[userId] || [];
    });
    return result;
  }, [policyUserSeriesMap, policySelectedUsers]);

  // ─── Policies: Workspace comparison time series (not supported in V2 policy breakdown — use KPIs only) ──
  const policyWsComparisonTimeSeries = useMemo(() => {
    const empty = { violations: [] as any[], blocks: [] as any[], alerts: [] as any[], tokensIn: [] as any[], tokensOut: [] as any[] };
    if (policySelectedCompareWs.size === 0) return empty;
    const selectedWsList = policyFilteredWsStats.filter(ws => policySelectedCompareWs.has(ws.wsId));
    if (selectedWsList.length === 0) return empty;
    const metrics = ['violations', 'blocks', 'alerts', 'tokensIn', 'tokensOut'] as const;
    const result: Record<string, any[]> = {};
    const refSeries = policyOvertimeData.length > 0 ? policyOvertimeData : [{ name: '-' }];
    metrics.forEach(metric => {
      result[metric] = refSeries.map((bucket: any) => {
        const row: any = { name: bucket.name };
        selectedWsList.forEach(ws => { row[ws.wsName] = 0; });
        return row;
      });
    });
    return result;
  }, [policySelectedCompareWs, policyFilteredWsStats, policyOvertimeData]);

  // ─── Policies: Rule comparison time series (from V2 breakdown) ────────────
  const policyRepoComparisonTimeSeries = useMemo(() => {
    const empty = { violations: [] as any[], blocks: [] as any[], alerts: [] as any[], tokensIn: [] as any[], tokensOut: [] as any[] };
    if (policySelectedCompareRepos.size === 0) return empty;
    const selectedRules = policyFilteredRepoStats.filter(r => policySelectedCompareRepos.has(r.repoId));
    if (selectedRules.length === 0) return empty;
    const metrics = ['violations', 'blocks', 'alerts', 'tokensIn', 'tokensOut'] as const;
    const result: Record<string, any[]> = {};
    metrics.forEach(metric => {
      const firstRuleSeries = policyRuleSeriesMap[selectedRules[0].repoId] || [];
      result[metric] = firstRuleSeries.map((bucket: any, i: number) => {
        const row: any = { name: bucket.name };
        selectedRules.forEach(rule => {
          const rBucket = (policyRuleSeriesMap[rule.repoId] || [])[i];
          row[rule.repoName] = rBucket?.[metric] || 0;
        });
        return row;
      });
    });
    return result;
  }, [policySelectedCompareRepos, policyFilteredRepoStats, policyRuleSeriesMap]);

  // ─── Policies: Workspaces with LLM resources (for explorer filtering) ──────
  const policyLlmWorkspaces = useMemo(() => {
    const wsIdsWithLlm = new Set<string>();
    effectiveRepositories.forEach((repo: any) => {
      if ((repo.aiLlms || []).length > 0) {
        const wid = repo.workspaceId || repo.workspace_id || repo.workspace?.id || repo.workspace;
        if (wid) wsIdsWithLlm.add(wid);
      }
    });
    return workspaces.filter((ws: any) => wsIdsWithLlm.has(ws.id));
  }, [workspaces, effectiveRepositories]);

  // ─── Policies: Repos with LLM resources for selected workspace ─────────────
  const policyLlmRepos = useMemo(() => {
    if (!explorerWs) return [];
    return effectiveRepositories.filter((repo: any) => {
      const wid = repo.workspaceId || repo.workspace_id || repo.workspace?.id || repo.workspace;
      return wid === explorerWs && (repo.aiLlms || []).length > 0;
    });
  }, [explorerWs, effectiveRepositories]);

  // Credits stats from real API data (creditsBalance + monthlyStats), filtered by dateRange
  const creditsStats = useMemo(() => {
    // Filter monthlyStats by selected date range
    const { startDate: rangeStart, endDate: rangeEnd } = getDateRangeFromPreset(dateRange);
    const filtered = monthlyStats.filter(m => {
      const mDate = new Date(m.month);
      return mDate >= rangeStart && mDate <= rangeEnd;
    });

    if (filtered.length > 0) {
      const consumptions = filtered.map(m => ({
        name: new Date(m.month).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        credits: m.consumption,
      }));
      const totalUsed = consumptions.reduce((s, d) => s + d.credits, 0);
      const avg = totalUsed / consumptions.length;
      let highest = -Infinity, highestDate = '—', lowest = Infinity, lowestDate = '—';
      consumptions.forEach(d => {
        if (d.credits > highest) { highest = d.credits; highestDate = d.name; }
        if (d.credits < lowest) { lowest = d.credits; lowestDate = d.name; }
      });
      if (!isFinite(highest)) highest = 0;
      if (!isFinite(lowest)) lowest = 0;
      return { total: totalUsed, avg, highest, highestDate, lowest, lowestDate };
    }

    // Fallback to balance data if no monthly stats in range
    const totalUsed = creditsBalance
      ? (creditsBalance.creditBalance - creditsBalance.realtimeBalance)
      : 0;
    return { total: totalUsed, avg: 0, highest: 0, highestDate: '—', lowest: 0, lowestDate: '—' };
  }, [creditsBalance, monthlyStats, dateRange]);

  // Toggle workspace in compare set (max 5)
  const toggleCompareWs = (wsId: string) => {
    const s = new Set(selectedCompareWs);
    if (s.has(wsId)) { s.delete(wsId); } else if (s.size < 5) { s.add(wsId); }
    setSelectedCompareWs(s);
  };

  // Toggle AI/Storage compare workspace (max 5)
  const toggleAiCompareWs = (wsId: string) => {
    const s = new Set(selectedAiCompareWs);
    if (s.has(wsId)) { s.delete(wsId); } else if (s.size < 5) { s.add(wsId); }
    setSelectedAiCompareWs(s);
  };
  const toggleStorageCompareWs = (wsId: string) => {
    const s = new Set(selectedStorageCompareWs);
    if (s.has(wsId)) { s.delete(wsId); } else if (s.size < 5) { s.add(wsId); }
    setSelectedStorageCompareWs(s);
  };

  // Toggle credits repo comparison (org-level, max 5)
  const toggleCreditsCompareRepo = (repoId: string) => {
    const s = new Set(selectedCreditsCompareRepos);
    if (s.has(repoId)) { s.delete(repoId); } else if (s.size < 5) { s.add(repoId); }
    setSelectedCreditsCompareRepos(s);
  };

  // Toggle repository comparison (max 5)
  const toggleAiCompareRepo = (repoId: string) => {
    const s = new Set(selectedAiCompareRepos);
    if (s.has(repoId)) { s.delete(repoId); } else if (s.size < 5) { s.add(repoId); }
    setSelectedAiCompareRepos(s);
  };
  const toggleStorageCompareRepo = (repoId: string) => {
    const s = new Set(selectedStorageCompareRepos);
    if (s.has(repoId)) { s.delete(repoId); } else if (s.size < 5) { s.add(repoId); }
    setSelectedStorageCompareRepos(s);
  };

  // Toggle AI provider/model comparison (max 5)
  const toggleAiProvider = (p: string) => {
    const s = new Set(selectedAiProviders);
    if (s.has(p)) s.delete(p); else if (s.size < 5) s.add(p);
    setSelectedAiProviders(s);
  };
  const toggleAiModel = (m: string) => {
    const s = new Set(selectedAiModels);
    if (s.has(m)) s.delete(m); else if (s.size < 5) s.add(m);
    setSelectedAiModels(s);
  };
  // Toggle storage provider/bucket comparison (max 5)
  const toggleStorageProvider = (p: string) => {
    const s = new Set(selectedStorageProviders);
    if (s.has(p)) s.delete(p); else if (s.size < 5) s.add(p);
    setSelectedStorageProviders(s);
  };
  const toggleStorageBucket = (b: string) => {
    const s = new Set(selectedStorageBuckets);
    if (s.has(b)) s.delete(b); else if (s.size < 5) s.add(b);
    setSelectedStorageBuckets(s);
  };
  // Toggle credits comparison selectors (max 5)
  const toggleCreditsProvider = (p: string) => {
    const s = new Set(selectedCreditsProviders);
    if (s.has(p)) s.delete(p); else if (s.size < 5) s.add(p);
    setSelectedCreditsProviders(s);
  };
  const toggleCreditsModel = (m: string) => {
    const s = new Set(selectedCreditsModels);
    if (s.has(m)) s.delete(m); else if (s.size < 5) s.add(m);
    setSelectedCreditsModels(s);
  };
  const toggleCreditsBucket = (b: string) => {
    const s = new Set(selectedCreditsBuckets);
    if (s.has(b)) s.delete(b); else if (s.size < 5) s.add(b);
    setSelectedCreditsBuckets(s);
  };

  // Auto-select top 3 entities from V2 breakdowns on first render of credits tab
  useEffect(() => {
    if (subSection === 'credits') {
      setSelectedCreditsProviders(prev => {
        if (prev.size > 0) return prev;
        return new Set(creditProviderBreakdown.slice(0, 3).map(e => e.entityName));
      });
      setSelectedCreditsModels(prev => {
        if (prev.size > 0) return prev;
        return new Set(creditModelBreakdown.slice(0, 3).map(e => e.entityName));
      });
      setSelectedCreditsBuckets(prev => {
        if (prev.size > 0) return prev;
        return new Set(creditBucketBreakdown.slice(0, 3).map(e => e.entityName));
      });
    }
  }, [subSection, creditProviderBreakdown, creditModelBreakdown, creditBucketBreakdown]);

  // Credits time-series from V2 getCreditStats (real data)
  const creditsTimeSeriesData = useMemo(() => {
    return monthlyStats.map(m => ({
      name: m.month,
      consumption: m.consumption,
      ai: (m as any).ai || 0,
      storage: (m as any).storage || 0,
    }));
  }, [monthlyStats]);

  // Credits workspace comparison from V2 breakdown
  const creditsWsComparisonTimeSeries = useMemo(() => {
    const selectedWsIds = new Set([...selectedCompareWs]);
    const selected = creditWsBreakdown.filter(e => selectedWsIds.has(e.entityId));
    if (selected.length === 0) return { total: [], ai: [], storage: [] };

    const buildSeries = (metric: 'creditsTotal' | 'creditsAi' | 'creditsStorage') => {
      const firstSeries = selected[0]?.series || [];
      return firstSeries.map((bucket: any, i: number) => {
        const row: any = { name: bucket.name };
        selected.forEach(e => {
          row[e.entityName] = (e.series || [])[i]?.[metric] || 0;
        });
        return row;
      });
    };

    return {
      total: buildSeries('creditsTotal'),
      ai: buildSeries('creditsAi'),
      storage: buildSeries('creditsStorage'),
    };
  }, [selectedCompareWs, creditWsBreakdown]);

  // All repos across org (from V2 credit breakdown)
  const allOrgRepoStats = useMemo(() => {
    return creditRepoBreakdown.map((e, _i) => ({
      id: e.entityId,
      name: e.entityName,
      wsId: '',
      wsName: '',
      creditsAi: e.creditsAi,
      creditsSt: e.creditsStorage,
      creditsTotal: e.creditsTotal,
      storageTraffic: 0,
      tokensIn: 0,
      tokensOut: 0,
    }));
  }, [creditRepoBreakdown]);

  // Credits repo comparison time-series (from V2 breakdown)
  const creditsRepoComparisonTimeSeries = useMemo(() => {
    const selected = creditRepoBreakdown.filter(e => selectedCreditsCompareRepos.has(e.entityId));
    if (selected.length === 0) return { total: [] as any[], ai: [] as any[], storage: [] as any[] };
    const buildSeries = (metric: 'creditsTotal' | 'creditsAi' | 'creditsStorage') => {
      const firstSeries = selected[0]?.series || [];
      return firstSeries.map((bucket: any, i: number) => {
        const row: any = { name: bucket.name };
        selected.forEach(e => { row[e.entityName] = (e.series || [])[i]?.[metric] || 0; });
        return row;
      });
    };
    return { total: buildSeries('creditsTotal'), ai: buildSeries('creditsAi'), storage: buildSeries('creditsStorage') };
  }, [creditRepoBreakdown, selectedCreditsCompareRepos]);

  // Credits provider aggregated data (from V2 breakdown)
  const creditsProviderData = useMemo(() => {
    return creditProviderBreakdown
      .filter(e => selectedCreditsProviders.has(e.entityName))
      .map((e, i) => ({
        name: e.entityName,
        total: e.creditsTotal,
        ai: e.creditsAi,
        storage: e.creditsStorage,
        color: compareColors[i % compareColors.length],
      }));
  }, [selectedCreditsProviders, creditProviderBreakdown]);

  // Credits Provider time-series (from V2 breakdown)
  const creditsProviderTimeSeries = useMemo(() => {
    const selected = creditProviderBreakdown.filter(e => selectedCreditsProviders.has(e.entityName));
    if (selected.length === 0) return [];
    const firstSeries = selected[0]?.series || [];
    return firstSeries.map((bucket: any, i: number) => {
      const row: any = { name: bucket.name };
      selected.forEach(e => { row[e.entityName] = (e.series || [])[i]?.creditsTotal || 0; });
      return row;
    });
  }, [selectedCreditsProviders, creditProviderBreakdown]);

  // Credits model aggregated data (from V2 breakdown)
  const creditsModelData = useMemo(() => {
    return creditModelBreakdown
      .filter(e => selectedCreditsModels.has(e.entityName))
      .map((e, i) => ({
        name: e.entityName,
        total: e.creditsTotal,
        ai: e.creditsAi,
        storage: e.creditsStorage,
        color: compareColors[i % compareColors.length],
      }));
  }, [selectedCreditsModels, creditModelBreakdown]);

  // Credits Model time-series (from V2 breakdown)
  const creditsModelTimeSeries = useMemo(() => {
    const selected = creditModelBreakdown.filter(e => selectedCreditsModels.has(e.entityName));
    if (selected.length === 0) return [];
    const firstSeries = selected[0]?.series || [];
    return firstSeries.map((bucket: any, i: number) => {
      const row: any = { name: bucket.name };
      selected.forEach(e => { row[e.entityName] = (e.series || [])[i]?.creditsTotal || 0; });
      return row;
    });
  }, [selectedCreditsModels, creditModelBreakdown]);

  // Credits bucket aggregated data (from V2 breakdown)
  const creditsBucketData = useMemo(() => {
    return creditBucketBreakdown
      .filter(e => selectedCreditsBuckets.has(e.entityName))
      .map((e, i) => ({
        name: e.entityName,
        total: e.creditsStorage,
        color: compareColors[i % compareColors.length],
      }));
  }, [selectedCreditsBuckets, creditBucketBreakdown]);

  // Build merged comparison time series data for multi-line charts
  const buildComparisonChartData = (
    selectedItems: Set<string>,
    timeSeriesMap: Record<string, any[]>,
    metric: string,
  ): any[] => {
    const items = [...selectedItems];
    if (items.length === 0) return [];
    // Use the first item's time series as the base for names
    const base = timeSeriesMap[items[0]] || [];
    return base.map((point: any, idx: number) => {
      const row: any = { name: point.name };
      items.forEach(item => {
        const ts = timeSeriesMap[item] || [];
        row[item] = ts[idx]?.[metric] || 0;
      });
      return row;
    });
  };

  // Comparison data
  const comparisonData = useMemo(() => {
    return workspaceStats
      .filter(ws => selectedCompareWs.has(ws.id))
      .map((ws, i) => ({ ...ws, color: compareColors[i % compareColors.length] }));
  }, [workspaceStats, selectedCompareWs]);

  // AI comparison data (aggregated totals per workspace)
  const aiComparisonData = useMemo(() => {
    return workspaceStats
      .filter(ws => selectedAiCompareWs.has(ws.id))
      .map((ws, i) => {
        const wsData = perWsStats[ws.id];
        return {
          ...ws,
          color: compareColors[i % compareColors.length],
          tokensIn: wsData?.tokensIn || 0,
          tokensOut: wsData?.tokensOut || 0,
          apiCalls: wsData?.apiCalls || 0,
          policyTokens: (wsData?.tokensIn || 0) + (wsData?.tokensOut || 0),
        };
      });
  }, [workspaceStats, selectedAiCompareWs, perWsStats]);

  // AI comparison time-series data for line charts (distribute overview data by workspace proportions)
  const aiWsComparisonTimeSeries = useMemo(() => {
    const selected = workspaceStats.filter(ws => selectedAiCompareWs.has(ws.id));
    if (selected.length === 0) return { requests: [], tokensIn: [], tokensOut: [] };
    
    // Calculate total per-workspace stats
    const totalApiCalls = selected.reduce((s, ws) => s + (perWsStats[ws.id]?.apiCalls || 0), 0);
    const totalTkIn = selected.reduce((s, ws) => s + (perWsStats[ws.id]?.tokensIn || 0), 0);
    const totalTkOut = selected.reduce((s, ws) => s + (perWsStats[ws.id]?.tokensOut || 0), 0);
    
    // Check if overview data has meaningful values
    const overviewHasRequests = aiRequestsChart.length > 0 && aiRequestsChart.some(p => (p.value || 0) > 0);
    const overviewHasTkIn = aiTokensInChart.length > 0 && aiTokensInChart.some(p => (p.value || 0) > 0);
    const overviewHasTkOut = aiTokensOutChart.length > 0 && aiTokensOutChart.some(p => (p.value || 0) > 0);
    
    // Build time series from perWsStats when overview data is empty but per-workspace data exists
    const buildFromPerWsStats = (metric: 'requests' | 'tokensIn' | 'tokensOut') => {
      const { startDate, endDate } = getDateRangeFromPreset(dateRange);
      const intervalMs = getSamplingIntervalMs(dateRange);
      const startMs = startDate.getTime();
      const endMs = endDate.getTime();
      const numBuckets = Math.ceil((endMs - startMs) / intervalMs);
      
      const data: any[] = [];
      for (let t = startMs; t <= endMs; t += intervalMs) {
        const row: any = { name: formatBucketLabel(t, dateRange) };
        selected.forEach((ws) => {
          const wsData = perWsStats[ws.id];
          const wsTotal = metric === 'requests' ? (wsData?.apiCalls || 0) : 
                          metric === 'tokensIn' ? (wsData?.tokensIn || 0) : (wsData?.tokensOut || 0);
          // Distribute total evenly across time buckets
          row[ws.name] = Math.round(wsTotal / numBuckets);
        });
        data.push(row);
      }
      return data;
    };
    
    const buildSeries = (baseData: any[], metric: 'requests' | 'tokensIn' | 'tokensOut', total: number, hasOverviewData: boolean) => {
      // If no overview data but we have per-workspace data, build from perWsStats
      if (!hasOverviewData && total > 0) {
        return buildFromPerWsStats(metric);
      }
      
      if (baseData.length === 0) return [];
      
      return baseData.map((point: any) => {
        const row: any = { name: point.name };
        selected.forEach((ws) => {
          const wsData = perWsStats[ws.id];
          const wsValue = metric === 'requests' ? (wsData?.apiCalls || 0) : 
                          metric === 'tokensIn' ? (wsData?.tokensIn || 0) : (wsData?.tokensOut || 0);
          const proportion = total > 0 ? wsValue / total : 1 / selected.length;
          row[ws.name] = Math.round((point.value || 0) * proportion);
        });
        return row;
      });
    };
    
    return {
      requests: buildSeries(aiRequestsChart, 'requests', totalApiCalls, overviewHasRequests),
      tokensIn: buildSeries(aiTokensInChart, 'tokensIn', totalTkIn, overviewHasTkIn),
      tokensOut: buildSeries(aiTokensOutChart, 'tokensOut', totalTkOut, overviewHasTkOut),
    };
  }, [workspaceStats, selectedAiCompareWs, perWsStats, aiRequestsChart, aiTokensInChart, aiTokensOutChart, dateRange]);

  // Storage comparison data (aggregated totals per workspace)
  const storageComparisonData = useMemo(() => {
    const result = workspaceStats
      .filter(ws => selectedStorageCompareWs.has(ws.id))
      .map((ws, i) => {
        const wsData = perWsStats[ws.id];
        return {
          ...ws,
          color: compareColors[i % compareColors.length],
          upload: wsData?.upload || 0,
          download: wsData?.download || 0,
          requests: wsData?.requests || 0,
        };
      });
    console.log('[StatisticsExplorer] Storage comparison data:', result);
    return result;
  }, [workspaceStats, selectedStorageCompareWs, perWsStats]);

  // Storage comparison time-series data for line charts (distribute overview data by workspace proportions)
  const storageWsComparisonTimeSeries = useMemo(() => {
    const selected = workspaceStats.filter(ws => selectedStorageCompareWs.has(ws.id));
    if (selected.length === 0) return { requests: [], upload: [], download: [] };
    
    // Calculate total per-workspace stats
    const totalRequests = selected.reduce((s, ws) => s + (perWsStats[ws.id]?.requests || 0), 0);
    const totalUpload = selected.reduce((s, ws) => s + (perWsStats[ws.id]?.upload || 0), 0);
    const totalDownload = selected.reduce((s, ws) => s + (perWsStats[ws.id]?.download || 0), 0);
    
    // Check if overview data has meaningful values
    const overviewHasRequests = requestsChartData.length > 0 && requestsChartData.some(p => (p.requests || 0) > 0);
    const overviewHasUpload = trafficChartData.length > 0 && trafficChartData.some(p => (p.upload || 0) > 0);
    const overviewHasDownload = trafficChartData.length > 0 && trafficChartData.some(p => (p.download || 0) > 0);
    
    // Build time series from perWsStats when overview data is empty but per-workspace data exists
    const buildFromPerWsStats = (metric: 'requests' | 'upload' | 'download') => {
      const { startDate, endDate } = getDateRangeFromPreset(dateRange);
      const intervalMs = getSamplingIntervalMs(dateRange);
      const startMs = startDate.getTime();
      const endMs = endDate.getTime();
      const numBuckets = Math.ceil((endMs - startMs) / intervalMs);
      
      const data: any[] = [];
      for (let t = startMs; t <= endMs; t += intervalMs) {
        const row: any = { name: formatBucketLabel(t, dateRange) };
        selected.forEach((ws) => {
          const wsData = perWsStats[ws.id];
          const wsTotal = wsData?.[metric] || 0;
          // Distribute total evenly across time buckets
          const valuePerBucket = metric === 'requests' 
            ? Math.round(wsTotal / numBuckets * 100) / 100
            : Math.round(wsTotal / numBuckets / (1024 * 1024) * 100) / 100; // Convert bytes to MB for upload/download
          row[ws.name] = valuePerBucket;
        });
        data.push(row);
      }
      return data;
    };
    
    const buildSeries = (baseData: any[], valueKey: string, metric: 'requests' | 'upload' | 'download', total: number, hasOverviewData: boolean) => {
      // If no overview data but we have per-workspace data, build from perWsStats
      if (!hasOverviewData && total > 0) {
        return buildFromPerWsStats(metric);
      }
      
      if (baseData.length === 0) return [];
      
      return baseData.map((point: any) => {
        const row: any = { name: point.name };
        selected.forEach((ws) => {
          const wsData = perWsStats[ws.id];
          const wsValue = wsData?.[metric] || 0;
          const proportion = total > 0 ? wsValue / total : 1 / selected.length;
          const baseValue = point[valueKey] || 0;
          row[ws.name] = Math.round(baseValue * proportion * 100) / 100;
        });
        return row;
      });
    };
    
    return {
      requests: buildSeries(requestsChartData, 'requests', 'requests', totalRequests, overviewHasRequests),
      upload: buildSeries(trafficChartData, 'upload', 'upload', totalUpload, overviewHasUpload),
      download: buildSeries(trafficChartData, 'download', 'download', totalDownload, overviewHasDownload),
    };
  }, [workspaceStats, selectedStorageCompareWs, perWsStats, requestsChartData, trafficChartData, dateRange]);

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* ═══════════ GATEWAY ═══════════ */}
      {subSection === 'gateway' && (
        <>
        {/* Page Header */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#101828' }}>Gateway Telemetry</Typography>
          <Typography sx={{ fontSize: '13px', color: '#667085', mt: 0.25 }}>Monitor storage and AI/LLM traffic, requests, latency, and model usage across your gateway</Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 0 }}>
          {/* Left Explorer Sidebar - Sticky, viewport-limited, vertical collapse (bottom→top) */}
          <Box ref={sidebarRef} sx={{
            width: explorerCollapsed ? 28 : 240,
            minWidth: explorerCollapsed ? 28 : 240,
            borderRight: explorerCollapsed ? 'none' : '1px solid #E9EAEB',
            pr: explorerCollapsed ? 0 : 2,
            mr: explorerCollapsed ? 0.5 : 3,
            position: 'sticky', top: 16, alignSelf: 'flex-start',
            maxHeight: 'calc(100vh - 32px)', overflow: 'hidden',
            bgcolor: 'white', zIndex: 5,
            transition: 'width 0.25s ease, min-width 0.25s ease, padding 0.25s ease, margin 0.25s ease',
          }}>
            {/* Explorer header - always visible */}
            {explorerCollapsed ? (
              /* Collapsed: only show a small arrow button */
              <Box
                onClick={() => setExplorerCollapsed(false)}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  py: 0.5, cursor: 'pointer',
                  '&:hover .explorer-toggle': { bgcolor: '#E9EAEB' },
                }}
              >
                <Box
                  className="explorer-toggle"
                  sx={{
                    width: 24, height: 24, borderRadius: '5px', bgcolor: '#F3F4F6',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.15s',
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#667085" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 6 15 12 9 18" />
                  </svg>
                </Box>
              </Box>
            ) : (
              /* Expanded: show header with text + collapse arrow */
              <Box
                onClick={() => setExplorerCollapsed(true)}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  mb: 2, cursor: 'pointer', py: 0.5,
                  '&:hover .explorer-toggle': { bgcolor: '#E9EAEB' },
                }}
              >
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                  Explorer
                </Typography>
                <Box
                  className="explorer-toggle"
                  sx={{
                    width: 24, height: 24, borderRadius: '5px', bgcolor: '#F3F4F6',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.15s',
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#667085" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </Box>
              </Box>
            )}

            {/* Tree content - collapses vertically (bottom→top) */}
            <Box sx={{
              maxHeight: explorerCollapsed ? 0 : 'calc(100vh - 220px)',
              overflow: explorerCollapsed ? 'hidden' : 'auto',
              opacity: explorerCollapsed ? 0 : 1,
              transition: 'max-height 0.25s ease, opacity 0.2s ease',
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-thumb': { bgcolor: '#D0D5DD', borderRadius: 2 },
            }}>

            {/* Org root */}
            <Box
              onClick={() => { setExplorerWs(null); setExplorerRepo(null); }}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1, borderRadius: '8px', cursor: 'pointer', mb: 0.75,
                bgcolor: !explorerWs ? '#F0F2FF' : 'transparent',
                border: !explorerWs ? '1px solid #D6DEFF' : '1px solid transparent',
                transition: 'all 0.15s ease',
                '&:hover': { bgcolor: !explorerWs ? '#F0F2FF' : '#F9FAFB', border: !explorerWs ? '1px solid #D6DEFF' : '1px solid #E9EAEB' },
              }}
            >
              <Box sx={{ width: 28, height: 28, borderRadius: '6px', bgcolor: !explorerWs ? '#617BFF' : '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.15s' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={!explorerWs ? 'white' : '#667085'} strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18" /></svg>
              </Box>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: !explorerWs ? '#3B4ECC' : '#344054', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {orgName || 'Organization'}
              </Typography>
            </Box>

            {/* Workspace nodes */}
            {workspaces.map((ws: any, wsIdx: number) => {
              const isWsActive = explorerWs === ws.id && !explorerRepo;
              const isWsExpanded = expandedNodes.has(ws.id);
              return (
                <Box key={ws.id} sx={{ position: 'relative' }}>
                  {/* Tree connector line */}
                  <Box sx={{ position: 'absolute', left: 18, top: 0, bottom: isWsExpanded ? 0 : 'auto', width: '1px', bgcolor: '#E9EAEB', zIndex: 0 }} />
                  <Box
                    onClick={() => { toggleNode(ws.id); setExplorerWs(ws.id); setExplorerRepo(null); }}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1, pl: 3.5, pr: 1, py: 0.75, borderRadius: '8px', cursor: 'pointer', mb: 0.25,
                      position: 'relative', zIndex: 1,
                      bgcolor: isWsActive ? '#F0F2FF' : 'transparent',
                      transition: 'all 0.12s ease',
                      '&:hover': { bgcolor: isWsActive ? '#F0F2FF' : '#F9FAFB' },
                    }}
                  >
                    <Box sx={{ width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="10" height="10" viewBox="0 0 12 12" fill="none" style={{ transform: isWsExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s ease' }}>
                        <path d="M4 2L8 6L4 10" stroke={isWsActive ? '#617BFF' : '#9CA3AF'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </Box>
                    <Box sx={{ width: 24, height: 24, borderRadius: '5px', bgcolor: isWsActive ? '#E8ECFF' : '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.15s' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={isWsActive ? '#617BFF' : '#667085'} strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></svg>
                    </Box>
                    <Typography sx={{ fontSize: '12px', fontWeight: isWsActive ? 600 : 500, color: isWsActive ? '#3B4ECC' : '#344054', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                      {ws.name}
                    </Typography>
                  </Box>
                  {/* Repo children - filtered by workspace */}
                  {isWsExpanded && (() => {
                    const wsRepos = getReposForWorkspace(ws.id);
                    return (
                    <Box sx={{ position: 'relative' }}>
                      {wsRepos.length === 0 && (
                        <Box sx={{ pl: 6.5, py: 1 }}>
                          <Typography sx={{ fontSize: '11px', color: '#9CA3AF', fontStyle: 'italic' }}>No repositories</Typography>
                        </Box>
                      )}
                      {wsRepos.map((repo: any, repoIdx: number) => {
                        const isRepoActive = explorerRepo === repo.id;
                        const isLast = repoIdx === wsRepos.length - 1;
                        return (
                          <Box key={repo.id} sx={{ position: 'relative' }}>
                            {/* Horizontal branch line */}
                            <Box sx={{ position: 'absolute', left: 32, top: '50%', width: 12, height: '1px', bgcolor: '#E9EAEB', zIndex: 0 }} />
                            {/* Vertical line continuation */}
                            {!isLast && <Box sx={{ position: 'absolute', left: 32, top: 0, bottom: 0, width: '1px', bgcolor: '#E9EAEB', zIndex: 0 }} />}
                            {isLast && <Box sx={{ position: 'absolute', left: 32, top: 0, bottom: '50%', width: '1px', bgcolor: '#E9EAEB', zIndex: 0 }} />}
                            <Box
                              onClick={() => { setExplorerWs(ws.id); setExplorerRepo(repo.id); }}
                              sx={{
                                display: 'flex', alignItems: 'center', gap: 1, pl: 6.5, pr: 1, py: 0.5, borderRadius: '6px', cursor: 'pointer', mb: 0.25,
                                position: 'relative', zIndex: 1,
                                bgcolor: isRepoActive ? '#F0F2FF' : 'transparent',
                                transition: 'all 0.12s ease',
                                '&:hover': { bgcolor: isRepoActive ? '#F0F2FF' : '#F9FAFB' },
                              }}
                            >
                              <Box sx={{ width: 20, height: 20, borderRadius: '4px', bgcolor: isRepoActive ? '#E8ECFF' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={isRepoActive ? '#617BFF' : '#9CA3AF'} strokeWidth="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></svg>
                              </Box>
                              <Typography sx={{ fontSize: '11.5px', fontWeight: isRepoActive ? 600 : 400, color: isRepoActive ? '#3B4ECC' : '#535862', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {repo.name}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                    );
                  })()}
                </Box>
              );
            })}
            {workspaces.length === 0 && (
              <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '12px', color: '#9CA3AF' }}>No workspaces found</Typography>
              </Box>
            )}
            </Box>{/* close tree content */}
          </Box>{/* close explorer sidebar */}

          {/* Right content area */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Sticky header: breadcrumb + date filter (stays visible on scroll, like explorer) */}
            <Box sx={{
              position: 'sticky', top: 16, zIndex: 4,
              bgcolor: 'white', pb: 1, mb: 1,
            }}>
            {/* Breadcrumb */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2, px: 0.5 }}>
              <Box
                onClick={() => { setExplorerWs(null); setExplorerRepo(null); }}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', '&:hover .bc-text': { textDecoration: 'underline' } }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18" /></svg>
                <Typography className="bc-text" sx={{ fontSize: '13px', color: '#617BFF', fontWeight: 500 }}>
                  {orgName || 'Organization'}
                </Typography>
              </Box>
              {explorerWs && (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D0D5DD" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                  <Box
                    onClick={() => setExplorerRepo(null)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: explorerRepo ? 'pointer' : 'default', '&:hover .bc-text': explorerRepo ? { textDecoration: 'underline' } : {} }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={explorerRepo ? '#617BFF' : '#344054'} strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></svg>
                    <Typography className="bc-text" sx={{ fontSize: '13px', color: explorerRepo ? '#617BFF' : '#101828', fontWeight: explorerRepo ? 500 : 600 }}>
                      {workspaces.find((ws: any) => ws.id === explorerWs)?.name || 'Workspace'}
                    </Typography>
                  </Box>
                </>
              )}
              {explorerRepo && (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D0D5DD" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#344054" strokeWidth="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></svg>
                    <Typography sx={{ fontSize: '13px', color: '#101828', fontWeight: 600 }}>
                      {effectiveRepositories.find((r: any) => r.id === explorerRepo)?.name || 'Repository'}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>

            {/* Date range filter */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <Box sx={{ display: 'flex', gap: 0.5, bgcolor: '#F3F4F6', borderRadius: '8px', p: '3px' }}>
                {(['24h', '7d', '30d', '90d', '180d', '365d'] as const).map((r) => (
                  <Button
                    key={r}
                    size="small"
                    onClick={() => setDateRange(r)}
                    sx={{
                      textTransform: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, px: 1.5, py: 0.25, minWidth: 0,
                      bgcolor: dateRange === r ? 'white' : 'transparent',
                      color: dateRange === r ? '#101828' : '#667085',
                      boxShadow: dateRange === r ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                      '&:hover': { bgcolor: dateRange === r ? 'white' : 'rgba(0,0,0,0.04)' },
                    }}
                  >
                    {r}
                  </Button>
                ))}
              </Box>
              {explorerWs && !explorerRepo && (
                <Chip label={`${currentRepos.length} Repositories`} size="small" sx={{ bgcolor: '#F0F2FF', color: '#617BFF', fontSize: '11px', fontWeight: 500, height: 28 }} />
              )}
              {statsLoading && (
                <CircularProgress size={14} sx={{ color: '#617BFF' }} />
              )}
            </Box>
            </Box>{/* close sticky header */}

            {/* ── General Section ── */}
            <Box sx={{ mt: 2, mb: 3 }}>
              <SectionHeader title="General" subtitle="Overview of organization usage: credits, requests, storage and token traffic" collapsed={collapsedSections.has('general')} onToggle={() => toggleSection('general')} />
              {!collapsedSections.has('general') && (
                <>
                  {/* 4 KPI Cards */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
                    <KpiCard label="Credits Consumed" value={fmtCredits(creditsStats.total)} color="#617BFF" loading={statsLoading} />
                    <KpiCard label="Number of Requests" value={fmtNum(totalRequests)} color="#10B981" loading={statsLoading} />
                    <KpiCard label="Storage Traffic" value={formatBytes(totalUpload + totalDownload)} color="#3B82F6" loading={statsLoading} />
                    <KpiCard label="Token Traffic" value={fmtNum((aiTotalTokensIn || aiStats.totalTokensIn) + (aiTotalTokensOut || aiStats.totalTokensOut))} color="#F59E0B" loading={aiStatsLoading} />
                  </Box>

                  {/* Workspaces Table (when at org level) OR Repositories Table (when workspace selected) */}
                  {!explorerWs ? (
                    // Organization level: Show Workspaces table
                    <Box sx={{ bgcolor: 'white', borderRadius: '10px', border: '1px solid #E9EAEB', overflow: 'hidden' }}>
                      <Box sx={{ p: 2, borderBottom: '1px solid #E9EAEB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828' }}>Workspaces</Typography>
                        <Chip label={`${Math.min(sortedWorkspaceStats.length, 10)} of ${workspaces.length}`} size="small" sx={{ bgcolor: '#F0F2FF', color: '#617BFF', fontSize: '11px', fontWeight: 500, height: 24 }} />
                      </Box>
                      <Box sx={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
                          <thead>
                            <tr style={{ backgroundColor: '#F9FAFB' }}>
                              {([
                                { key: 'name' as SortColumn, label: 'Workspace Name', align: 'left' as const },
                                { key: 'creationDate' as SortColumn, label: 'Creation Date', align: 'left' as const },
                                { key: 'repos' as SortColumn, label: 'Repositories', align: 'right' as const },
                                { key: 'connectedResources' as SortColumn, label: 'Connected Resources', align: 'right' as const },
                                { key: 'requests' as SortColumn, label: 'Requests', align: 'right' as const },
                                { key: 'storageTraffic' as SortColumn, label: 'Storage Traffic', align: 'right' as const },
                                { key: 'tokenTraffic' as SortColumn, label: 'Token Traffic', align: 'right' as const },
                                { key: 'credits' as SortColumn, label: 'Credits Consumed', align: 'right' as const },
                              ]).map(col => (
                                <th
                                  key={col.key}
                                  onClick={() => handleWsSort(col.key)}
                                  style={{
                                    padding: '10px 12px', textAlign: col.align, fontSize: '11px', fontWeight: 600,
                                    color: wsSortColumn === col.key ? '#617BFF' : '#667085', cursor: 'pointer', userSelect: 'none',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {col.label}
                                  {wsSortColumn === col.key && (
                                    <span style={{ marginLeft: 4, fontSize: '10px' }}>{wsSortDirection === 'asc' ? '▲' : '▼'}</span>
                                  )}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {sortedWorkspaceStats.slice(0, 10).map((ws, idx) => (
                              <tr key={ws.id} style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#FAFBFC', transition: 'background 0.1s' }}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#F5F7FF')}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = idx % 2 === 0 ? 'white' : '#FAFBFC')}
                              >
                                <td style={{ padding: '10px 12px', fontSize: '12px', fontWeight: 500, color: '#101828' }}>{ws.name}</td>
                                <td style={{ padding: '10px 12px', fontSize: '12px', color: '#667085' }}>{ws.creationDate ? new Date(ws.creationDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</td>
                                <td style={{ padding: '10px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{ws.repos}</td>
                                <td style={{ padding: '10px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{ws.connectedResources}</td>
                                <td style={{ padding: '10px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{fmtNum(ws.requests)}</td>
                                <td style={{ padding: '10px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{formatBytes(ws.storageTraffic)}</td>
                                <td style={{ padding: '10px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{fmtNum(ws.tokenTraffic)}</td>
                                <td style={{ padding: '10px 12px', fontSize: '12px', color: '#344054', textAlign: 'right', fontWeight: 600 }}>{fmtCredits(ws.credits)}</td>
                              </tr>
                            ))}
                            {sortedWorkspaceStats.length === 0 && (
                              <tr><td colSpan={8} style={{ padding: '24px', textAlign: 'center', fontSize: '12px', color: '#9CA3AF' }}>No workspaces found</td></tr>
                            )}
                          </tbody>
                        </table>
                      </Box>
                    </Box>
                  ) : !explorerRepo ? (
                    // Workspace level: Show Repositories table
                    <Box sx={{ bgcolor: 'white', borderRadius: '10px', border: '1px solid #E9EAEB', overflow: 'hidden' }}>
                      <Box sx={{ p: 2, borderBottom: '1px solid #E9EAEB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828' }}>Repositories</Typography>
                        <Chip label={`${Math.min(sortedRepositoryStats.length, 10)} of ${repositoryStats.length}`} size="small" sx={{ bgcolor: '#F0F2FF', color: '#617BFF', fontSize: '11px', fontWeight: 500, height: 24 }} />
                      </Box>
                      <Box sx={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
                          <thead>
                            <tr style={{ backgroundColor: '#F9FAFB' }}>
                              {([
                                { key: 'name' as SortColumn, label: 'Repository Name', align: 'left' as const },
                                { key: 'creationDate' as SortColumn, label: 'Creation Date', align: 'left' as const },
                                { key: 'repos' as SortColumn, label: 'Buckets', align: 'right' as const },
                                { key: 'connectedResources' as SortColumn, label: 'AI LLMs', align: 'right' as const },
                                { key: 'requests' as SortColumn, label: 'API Calls', align: 'right' as const },
                                { key: 'storageTraffic' as SortColumn, label: 'Storage Traffic', align: 'right' as const },
                                { key: 'tokenTraffic' as SortColumn, label: 'Token Traffic', align: 'right' as const },
                                { key: 'credits' as SortColumn, label: 'Credits Consumed', align: 'right' as const },
                              ]).map(col => (
                                <th
                                  key={col.key}
                                  onClick={() => handleWsSort(col.key)}
                                  style={{
                                    padding: '10px 12px', textAlign: col.align, fontSize: '11px', fontWeight: 600,
                                    color: wsSortColumn === col.key ? '#617BFF' : '#667085', cursor: 'pointer', userSelect: 'none',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {col.label}
                                  {wsSortColumn === col.key && (
                                    <span style={{ marginLeft: 4, fontSize: '10px' }}>{wsSortDirection === 'asc' ? '▲' : '▼'}</span>
                                  )}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {sortedRepositoryStats.map((repo, idx) => (
                              <tr key={repo.id} style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#FAFBFC', transition: 'background 0.1s' }}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#F5F7FF')}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = idx % 2 === 0 ? 'white' : '#FAFBFC')}
                              >
                                <td style={{ padding: '10px 12px', fontSize: '12px', fontWeight: 500, color: '#101828' }}>{repo.name}</td>
                                <td style={{ padding: '10px 12px', fontSize: '12px', color: '#667085' }}>{repo.creationDate ? new Date(repo.creationDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</td>
                                <td style={{ padding: '10px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{repo.buckets}</td>
                                <td style={{ padding: '10px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{repo.aiLlms}</td>
                                <td style={{ padding: '10px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{fmtNum(repo.apiCalls)}</td>
                                <td style={{ padding: '10px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{formatBytes(repo.storageTraffic)}</td>
                                <td style={{ padding: '10px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{fmtNum(repo.tokenTraffic)}</td>
                                <td style={{ padding: '10px 12px', fontSize: '12px', color: '#344054', textAlign: 'right', fontWeight: 600 }}>{fmtCredits(repo.credits)}</td>
                              </tr>
                            ))}
                            {sortedRepositoryStats.length === 0 && (
                              <tr><td colSpan={8} style={{ padding: '24px', textAlign: 'center', fontSize: '12px', color: '#9CA3AF' }}>No repositories found in this workspace</td></tr>
                            )}
                          </tbody>
                        </table>
                      </Box>
                    </Box>
                  ) : null /* Repository level: No table needed */}
                </>
              )}
            </Box>
          </Box>{/* close right content area */}
        </Box>{/* close sidebar flex container */}

            {/* ── AI LLM Section (full width when past sidebar, aligned with content otherwise) ── */}
            <Box ref={aiLlmSectionRef} sx={{
              mb: 3,
              ml: pastSidebar ? 0 : (explorerCollapsed ? '32px' : '268px'),
              transition: 'margin-left 0.3s ease',
            }}>
              <SectionHeader title="AI LLM" subtitle="AI Gateway statistics: API calls, tokens, latency and model usage" collapsed={collapsedSections.has('llm')} onToggle={() => toggleSection('llm')} />
              {!collapsedSections.has('llm') && (
                <>
                  {/* 4 KPIs */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
                    <KpiCard label="Number of Requests" value={fmtNum(aiTotalApiCalls || aiStats.totalApiCalls)} color="#617BFF" loading={aiStatsLoading} />
                    <KpiCard label="Tokens In" value={fmtNum(aiTotalTokensIn || aiStats.totalTokensIn)} color="#10B981" loading={aiStatsLoading} />
                    <KpiCard label="Tokens Out" value={fmtNum(aiTotalTokensOut || aiStats.totalTokensOut)} color="#3B82F6" loading={aiStatsLoading} />
                    <KpiCard label="Models / Resources" value={String(aiModelDistribution.length || 0)} color="#8B5CF6" loading={aiStatsLoading} />
                  </Box>

                  {/* A) Overview: 3 full-width charts */}
                  <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#667085', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.5 }}>Overview</Typography>

                  {/* AI/LLM overview charts with Line/Bar toggle */}
                  {([
                    { key: 'requests', label: 'Requests over time', color: '#617BFF', gradId: 'clrAiReq', data: aiRequestsChart, dataKey: 'value' },
                    { key: 'tokensIn', label: 'Token In over time', color: '#10B981', gradId: 'clrTkIn2', data: aiTokensInChart, dataKey: 'value' },
                    { key: 'tokensOut', label: 'Token Out over time', color: '#3B82F6', gradId: 'clrTkOut2', data: aiTokensOutChart, dataKey: 'value' },
                  ] as const).map(({ key, label, color, gradId, data, dataKey }) => {
                    const mode = aiOverviewChartType[key] || 'line';
                    return (
                      <Box key={key} sx={{ bgcolor: 'white', borderRadius: '10px', border: '1px solid #E9EAEB', p: 2.5, mb: 2.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '13px', color: '#101828' }}>{label}</Typography>
                            {aiStatsLoading && <CircularProgress size={14} sx={{ color: '#617BFF' }} />}
                          </Box>
                          <Box sx={{ display: 'flex', bgcolor: '#F3F4F6', borderRadius: '6px', p: 0.25 }}>
                            <Box onClick={() => setAiOverviewChartType(prev => ({ ...prev, [key]: 'line' }))}
                              sx={{ px: 1, py: 0.5, borderRadius: '5px', cursor: 'pointer', bgcolor: mode === 'line' ? 'white' : 'transparent', boxShadow: mode === 'line' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={mode === 'line' ? color : '#9CA3AF'} strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>
                            </Box>
                            <Box onClick={() => setAiOverviewChartType(prev => ({ ...prev, [key]: 'bar' }))}
                              sx={{ px: 1, py: 0.5, borderRadius: '5px', cursor: 'pointer', bgcolor: mode === 'bar' ? 'white' : 'transparent', boxShadow: mode === 'bar' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={mode === 'bar' ? color : '#9CA3AF'} strokeWidth="2"><rect x="3" y="12" width="4" height="9" /><rect x="10" y="6" width="4" height="15" /><rect x="17" y="3" width="4" height="18" /></svg>
                            </Box>
                          </Box>
                        </Box>
                        <Box sx={{ height: CHART_HEIGHT, width: '100%' }}>
                          {data.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                              {mode === 'line' ? (
                                <AreaChart data={data} margin={{ right: 20 }}>
                                  <defs><linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={color} stopOpacity={0.3} /><stop offset="95%" stopColor={color} stopOpacity={0} /></linearGradient></defs>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                  <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(data.length / 8) - 1)} />
                                  <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                  <RechartsTooltip content={<TradingViewTooltip color={color} valueFormatter={fmtNum} />} />
                                  <Area type="monotone" dataKey={dataKey} stroke={color} fill={`url(#${gradId})`} strokeWidth={2} name={label} />
                                </AreaChart>
                              ) : (
                                <BarChart data={data} margin={{ right: 20 }}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                  <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(data.length / 8) - 1)} />
                                  <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                  <RechartsTooltip content={<TradingViewTooltip color={color} valueFormatter={fmtNum} />} />
                                  <Bar dataKey={dataKey} fill={color} radius={[4, 4, 0, 0]} name={label} />
                                </BarChart>
                              )}
                            </ResponsiveContainer>
                          ) : <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography sx={{ fontSize: '12px', color: '#9CA3AF' }}>{aiStatsLoading ? 'Loading...' : 'No data'}</Typography></Box>}
                        </Box>
                      </Box>
                    );
                  })}

                  {/* B) Comparisons */}
                  <Box ref={comparisonsSectionRef}>
                    <SectionHeader title="Comparisons" subtitle="Compare API calls, tokens, and latency across providers, workspaces, and models" collapsed={collapsedSections.has('llmComparisons')} onToggle={() => toggleSection('llmComparisons')} />
                  </Box>
                  {!collapsedSections.has('llmComparisons') && (<>

                  {/* B.1) Provider Comparison */}
                  {aiProviderList.length > 0 && (
                    <Box sx={{ bgcolor: 'white', borderRadius: '10px', border: '1px solid #E9EAEB', overflow: 'hidden', mb: 2.5 }}>
                      <Box sx={{ p: 2, borderBottom: '1px solid #E9EAEB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828' }}>Provider Comparison</Typography>
                          <Typography sx={{ fontSize: '11px', color: '#9CA3AF', mt: 0.25 }}>Compare AI/LLM usage across providers</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ p: 2, borderBottom: '1px solid #E9EAEB' }}>
                        <MultiSelectDropdown
                          label="Select providers"
                          options={aiProviderList}
                          selected={selectedAiProviders}
                          onToggle={toggleAiProvider}
                          maxItems={5}
                          colors={compareColors}
                        />
                      </Box>
                      {selectedAiProviders.size > 0 ? (
                        <Box sx={{ p: 2.5 }}>
                          {['requests', 'tokensIn', 'tokensOut'].map((metric) => {
                            const data = buildComparisonChartData(selectedAiProviders, aiPerProviderTimeSeries, metric);
                            const label = metric === 'requests' ? 'Requests' : metric === 'tokensIn' ? 'Token In' : 'Token Out';
                            const viewMode = aiProviderViewMode[metric] || 'aggregated';
                            const chartType = aiProviderChartType[metric] || 'line';
                            // Compute aggregated data for bar chart
                            const aggregatedData = [...selectedAiProviders].map((prov, i) => {
                              const ts = aiPerProviderTimeSeries[prov] || [];
                              const total = ts.reduce((s: number, d: any) => s + (d[metric] || 0), 0);
                              return { name: prov, value: total, fill: compareColors[i % compareColors.length] };
                            });
                            return (
                              <Box key={metric} sx={{ mb: 2.5, bgcolor: '#FAFBFC', borderRadius: '8px', p: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                  <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#344054' }}>{label}</Typography>
                                  <ChartViewModeSelector 
                                    viewMode={viewMode} 
                                    onViewModeChange={(mode) => setAiProviderViewMode(prev => ({ ...prev, [metric]: mode }))} 
                                    chartType={chartType} 
                                    onChartTypeChange={(type) => setAiProviderChartType(prev => ({ ...prev, [metric]: type }))} 
                                  />
                                </Box>
                                <Box sx={{ height: CHART_HEIGHT }}>
                                  {viewMode === 'aggregated' ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                      <BarChart data={aggregatedData} margin={{ right: 20, left: 20 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                        <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} />
                                        <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                        <RechartsTooltip contentStyle={chartTooltipStyle} formatter={(v: number) => [fmtNum(v), label]} />
                                        <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                                          {aggregatedData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.fill} />
                                          ))}
                                        </Bar>
                                      </BarChart>
                                    </ResponsiveContainer>
                                  ) : (
                                    <ResponsiveContainer width="100%" height="100%">
                                      {chartType === 'line' ? (
                                        <LineChart data={data} margin={{ right: 20 }}>
                                          <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                          <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(data.length / 8) - 1)} />
                                          <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                          <RechartsTooltip content={<TradingViewTooltip valueFormatter={fmtNum} />} />
                                          {[...selectedAiProviders].map((prov, i) => (
                                            <Line key={prov} type="monotone" dataKey={prov} stroke={compareColors[i % compareColors.length]} strokeWidth={2} dot={false} name={prov} />
                                          ))}
                                        </LineChart>
                                      ) : (
                                        <BarChart data={data} margin={{ right: 20 }}>
                                          <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                          <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(data.length / 8) - 1)} />
                                          <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                          <RechartsTooltip contentStyle={chartTooltipStyle} />
                                          {[...selectedAiProviders].map((prov, i) => (
                                            <Bar key={prov} dataKey={prov} fill={compareColors[i % compareColors.length]} radius={[4, 4, 0, 0]} name={prov} />
                                          ))}
                                        </BarChart>
                                      )}
                                    </ResponsiveContainer>
                                  )}
                                </Box>
                              </Box>
                            );
                          })}
                          {/* Recap table */}
                          <Box sx={{ overflowX: 'auto', mt: 1 }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead><tr style={{ backgroundColor: '#F9FAFB' }}>
                                <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Provider</th>
                                <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Requests</th>
                                <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Tokens In</th>
                                <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Tokens Out</th>
                              </tr></thead>
                              <tbody>
                                {[...selectedAiProviders].map((prov, idx) => {
                                  const ts = aiPerProviderTimeSeries[prov] || [];
                                  const totReq = ts.reduce((s: number, d: any) => s + (d.requests || 0), 0);
                                  const totIn = ts.reduce((s: number, d: any) => s + (d.tokensIn || 0), 0);
                                  const totOut = ts.reduce((s: number, d: any) => s + (d.tokensOut || 0), 0);
                                  return (
                                    <tr key={prov} style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#FAFBFC' }}>
                                      <td style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 500, color: '#101828' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: compareColors[idx % compareColors.length], flexShrink: 0 }} />
                                          {prov}
                                        </Box>
                                      </td>
                                      <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right', fontWeight: 600 }}>{fmtNum(totReq)}</td>
                                      <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{fmtNum(totIn)}</td>
                                      <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{fmtNum(totOut)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </Box>
                        </Box>
                      ) : (
                        <Box sx={{ p: 4, textAlign: 'center' }}><Typography sx={{ fontSize: '13px', color: '#9CA3AF' }}>Select providers above to compare</Typography></Box>
                      )}
                    </Box>
                  )}

                  {/* B.2) Workspace/Repository Comparison - based on hierarchy level */}
                  {/* Organization level: Workspace Comparison */}
                  {/* Workspace level: Repository Comparison */}
                  {/* Repository level: No comparison (hidden) */}
                  {!explorerRepo && (
                    <Box sx={{ bgcolor: 'white', borderRadius: '10px', border: '1px solid #E9EAEB', overflow: 'hidden', mb: 2.5 }}>
                      <Box sx={{ p: 2, borderBottom: '1px solid #E9EAEB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828' }}>
                            {!explorerWs ? 'Workspace Comparison' : 'Repository Comparison'}
                          </Typography>
                          <Typography sx={{ fontSize: '11px', color: '#9CA3AF', mt: 0.25 }}>
                            {!explorerWs 
                              ? 'Compare AI/LLM requests and tokens across workspaces' 
                              : 'Compare AI/LLM requests and tokens across repositories'}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ p: 2, borderBottom: '1px solid #E9EAEB' }}>
                        {!explorerWs ? (
                          // Organization level: Select workspaces
                          <MultiSelectDropdown
                            label="Select workspaces"
                            options={workspaces.map((ws: any) => ws.name)}
                            selected={new Set(workspaces.filter((ws: any) => selectedAiCompareWs.has(ws.id)).map((ws: any) => ws.name))}
                            onToggle={(wsName: string) => {
                              const ws = workspaces.find((w: any) => w.name === wsName);
                              if (ws) toggleAiCompareWs(ws.id);
                            }}
                            maxItems={5}
                            colors={compareColors}
                          />
                        ) : (
                          // Workspace level: Select repositories
                          <MultiSelectDropdown
                            label="Select repositories"
                            options={repositoryStats.map((repo: any) => repo.name)}
                            selected={new Set(repositoryStats.filter((repo: any) => selectedAiCompareRepos.has(repo.id)).map((repo: any) => repo.name))}
                            onToggle={(repoName: string) => {
                              const repo = repositoryStats.find((r: any) => r.name === repoName);
                              if (repo) toggleAiCompareRepo(repo.id);
                            }}
                            maxItems={5}
                            colors={compareColors}
                          />
                        )}
                      </Box>
                      {!explorerWs ? (
                        // Organization level: Workspace comparison content
                        aiComparisonData.length > 0 ? (
                          <Box sx={{ p: 2.5 }}>
                            {/* 3 charts with Aggregated/Temporal toggle: Requests, Tokens In, Tokens Out */}
                            {(['requests', 'tokensIn', 'tokensOut'] as const).map((metric) => {
                              const label = metric === 'requests' ? 'Requests' : metric === 'tokensIn' ? 'Token In' : 'Token Out';
                              const data = aiWsComparisonTimeSeries[metric] || [];
                              const selectedWs = workspaceStats.filter(ws => selectedAiCompareWs.has(ws.id));
                              const viewMode = aiWsViewMode[metric] || 'aggregated';
                              const chartType = aiWsChartType[metric] || 'line';
                              // Compute aggregated data for bar chart
                              const aggregatedData = selectedWs.map((ws, i) => {
                                const val = metric === 'requests' ? ws.requests : metric === 'tokensIn' ? (perWsStats[ws.id]?.tokensIn || 0) : (perWsStats[ws.id]?.tokensOut || 0);
                                return { name: ws.name, value: val, fill: compareColors[i % compareColors.length] };
                              });
                              return (
                                <Box key={metric} sx={{ mb: 2.5, bgcolor: '#FAFBFC', borderRadius: '8px', p: 2 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#344054' }}>{label}</Typography>
                                    <ChartViewModeSelector 
                                      viewMode={viewMode} 
                                      onViewModeChange={(mode) => setAiWsViewMode(prev => ({ ...prev, [metric]: mode }))} 
                                      chartType={chartType} 
                                      onChartTypeChange={(type) => setAiWsChartType(prev => ({ ...prev, [metric]: type }))} 
                                    />
                                  </Box>
                                  <Box sx={{ height: CHART_HEIGHT }}>
                                    {viewMode === 'aggregated' ? (
                                      <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={aggregatedData} margin={{ right: 20, left: 20 }}>
                                          <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                          <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} />
                                          <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                          <RechartsTooltip contentStyle={chartTooltipStyle} formatter={(v: number) => [fmtNum(v), label]} />
                                          <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                                            {aggregatedData.map((entry, index) => (
                                              <Cell key={`cell-${index}`} fill={entry.fill} />
                                            ))}
                                          </Bar>
                                        </BarChart>
                                      </ResponsiveContainer>
                                    ) : (
                                      <ResponsiveContainer width="100%" height="100%">
                                        {chartType === 'line' ? (
                                          <LineChart data={data} margin={{ right: 20 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                            <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(data.length / 8) - 1)} />
                                            <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                            <RechartsTooltip content={<TradingViewTooltip valueFormatter={fmtNum} />} />
                                            {selectedWs.map((ws, i) => (
                                              <Line key={ws.id} type="monotone" dataKey={ws.name} stroke={compareColors[i % compareColors.length]} strokeWidth={2} dot={false} name={ws.name} />
                                            ))}
                                          </LineChart>
                                        ) : (
                                          <BarChart data={data} margin={{ right: 20 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                            <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(data.length / 8) - 1)} />
                                            <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                            <RechartsTooltip contentStyle={chartTooltipStyle} />
                                            {selectedWs.map((ws, i) => (
                                              <Bar key={ws.id} dataKey={ws.name} fill={compareColors[i % compareColors.length]} radius={[4, 4, 0, 0]} name={ws.name} />
                                            ))}
                                          </BarChart>
                                        )}
                                      </ResponsiveContainer>
                                    )}
                                  </Box>
                                </Box>
                              );
                            })}
                            {/* Recap table */}
                            <Box sx={{ overflowX: 'auto', mt: 1 }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead><tr style={{ backgroundColor: '#F9FAFB' }}>
                                  <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Workspace</th>
                                  <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Requests</th>
                                  <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Tokens In</th>
                                  <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Tokens Out</th>
                                  <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Rank</th>
                                </tr></thead>
                                <tbody>
                                  {[...aiComparisonData].sort((a, b) => b.apiCalls - a.apiCalls).map((ws, idx) => (
                                    <tr key={ws.id} style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#FAFBFC' }}>
                                      <td style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 500, color: '#101828' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: ws.color, flexShrink: 0 }} />
                                          {ws.name}
                                        </Box>
                                      </td>
                                      <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right', fontWeight: 600 }}>{fmtNum(ws.apiCalls)}</td>
                                      <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{fmtNum(ws.tokensIn)}</td>
                                      <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{fmtNum(ws.tokensOut)}</td>
                                      <td style={{ padding: '8px 12px', fontSize: '12px', color: '#617BFF', textAlign: 'right', fontWeight: 600 }}>#{idx + 1}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </Box>
                          </Box>
                        ) : (
                          <Box sx={{ p: 4, textAlign: 'center' }}><Typography sx={{ fontSize: '13px', color: '#9CA3AF' }}>Select workspaces above to compare AI/LLM usage</Typography></Box>
                        )
                      ) : (
                        // Workspace level: Repository comparison content
                        aiRepoComparisonData.length > 0 ? (
                          <Box sx={{ p: 2.5 }}>
                            {/* 3 charts with Aggregated/Temporal toggle: Requests, Tokens In, Tokens Out */}
                            {(['requests', 'tokensIn', 'tokensOut'] as const).map((metric) => {
                              const label = metric === 'requests' ? 'API Calls' : metric === 'tokensIn' ? 'Token In' : 'Token Out';
                              const selectedRepos = repositoryStats.filter(repo => selectedAiCompareRepos.has(repo.id));
                              const viewMode = aiRepoViewMode[metric] || 'aggregated';
                              const chartType = aiRepoChartType[metric] || 'line';
                              // Compute aggregated data for bar chart
                              const aggregatedData = selectedRepos.map((repo, i) => {
                                const val = metric === 'requests' ? repo.apiCalls : metric === 'tokensIn' ? repo.tokensIn : repo.tokensOut;
                                return { name: repo.name, value: val, fill: compareColors[i % compareColors.length] };
                              });
                              return (
                                <Box key={metric} sx={{ mb: 2.5, bgcolor: '#FAFBFC', borderRadius: '8px', p: 2 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#344054' }}>{label}</Typography>
                                    <ChartViewModeSelector 
                                      viewMode={viewMode} 
                                      onViewModeChange={(mode) => setAiRepoViewMode(prev => ({ ...prev, [metric]: mode }))} 
                                      chartType={chartType} 
                                      onChartTypeChange={(type) => setAiRepoChartType(prev => ({ ...prev, [metric]: type }))} 
                                    />
                                  </Box>
                                  <Box sx={{ height: CHART_HEIGHT }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                      <BarChart data={aggregatedData} margin={{ right: 20, left: 20 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                        <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} />
                                        <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                        <RechartsTooltip contentStyle={chartTooltipStyle} formatter={(v: number) => [fmtNum(v), label]} />
                                        <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                                          {aggregatedData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.fill} />
                                          ))}
                                        </Bar>
                                      </BarChart>
                                    </ResponsiveContainer>
                                  </Box>
                                </Box>
                              );
                            })}
                            {/* Recap table */}
                            <Box sx={{ overflowX: 'auto', mt: 1 }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead><tr style={{ backgroundColor: '#F9FAFB' }}>
                                  <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Repository</th>
                                  <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>API Calls</th>
                                  <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Tokens In</th>
                                  <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Tokens Out</th>
                                  <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Rank</th>
                                </tr></thead>
                                <tbody>
                                  {[...aiRepoComparisonData].sort((a, b) => b.apiCalls - a.apiCalls).map((repo, idx) => (
                                    <tr key={repo.id} style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#FAFBFC' }}>
                                      <td style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 500, color: '#101828' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: repo.color, flexShrink: 0 }} />
                                          {repo.name}
                                        </Box>
                                      </td>
                                      <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right', fontWeight: 600 }}>{fmtNum(repo.apiCalls)}</td>
                                      <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{fmtNum(repo.tokensIn)}</td>
                                      <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{fmtNum(repo.tokensOut)}</td>
                                      <td style={{ padding: '8px 12px', fontSize: '12px', color: '#617BFF', textAlign: 'right', fontWeight: 600 }}>#{idx + 1}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </Box>
                          </Box>
                        ) : (
                          <Box sx={{ p: 4, textAlign: 'center' }}><Typography sx={{ fontSize: '13px', color: '#9CA3AF' }}>Select repositories above to compare AI/LLM usage</Typography></Box>
                        )
                      )}
                    </Box>
                  )}

                  {/* B.3) Model Comparison */}
                  {aiModelList.length > 0 && (
                    <Box sx={{ bgcolor: 'white', borderRadius: '10px', border: '1px solid #E9EAEB', overflow: 'hidden', mb: 2.5 }}>
                      <Box sx={{ p: 2, borderBottom: '1px solid #E9EAEB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828' }}>Model Comparison</Typography>
                          <Typography sx={{ fontSize: '11px', color: '#9CA3AF', mt: 0.25 }}>Compare AI/LLM usage across models</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ p: 2, borderBottom: '1px solid #E9EAEB' }}>
                        <MultiSelectDropdown
                          label="Select models"
                          options={aiModelList}
                          selected={selectedAiModels}
                          onToggle={toggleAiModel}
                          maxItems={5}
                          colors={compareColors}
                        />
                      </Box>
                      {selectedAiModels.size > 0 ? (
                        <Box sx={{ p: 2.5 }}>
                          {['requests', 'tokensIn', 'tokensOut'].map((metric) => {
                            const data = buildComparisonChartData(selectedAiModels, aiPerModelTimeSeries, metric);
                            const label = metric === 'requests' ? 'Requests' : metric === 'tokensIn' ? 'Token In' : 'Token Out';
                            const viewMode = aiModelViewMode[metric] || 'aggregated';
                            const chartType = aiModelChartType[metric] || 'line';
                            // Compute aggregated data for bar chart
                            const aggregatedData = [...selectedAiModels].map((mod, i) => {
                              const ts = aiPerModelTimeSeries[mod] || [];
                              const total = ts.reduce((s: number, d: any) => s + (d[metric] || 0), 0);
                              return { name: mod, value: total, fill: compareColors[i % compareColors.length] };
                            });
                            return (
                              <Box key={metric} sx={{ mb: 2.5, bgcolor: '#FAFBFC', borderRadius: '8px', p: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                  <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#344054' }}>{label}</Typography>
                                  <ChartViewModeSelector 
                                    viewMode={viewMode} 
                                    onViewModeChange={(mode) => setAiModelViewMode(prev => ({ ...prev, [metric]: mode }))} 
                                    chartType={chartType} 
                                    onChartTypeChange={(type) => setAiModelChartType(prev => ({ ...prev, [metric]: type }))} 
                                  />
                                </Box>
                                <Box sx={{ height: CHART_HEIGHT }}>
                                  {viewMode === 'aggregated' ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                      <BarChart data={aggregatedData} margin={{ right: 20, left: 20 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                        <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} />
                                        <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                        <RechartsTooltip contentStyle={chartTooltipStyle} formatter={(v: number) => [fmtNum(v), label]} />
                                        <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                                          {aggregatedData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.fill} />
                                          ))}
                                        </Bar>
                                      </BarChart>
                                    </ResponsiveContainer>
                                  ) : (
                                    <ResponsiveContainer width="100%" height="100%">
                                      {chartType === 'line' ? (
                                        <LineChart data={data} margin={{ right: 20 }}>
                                          <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                          <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(data.length / 8) - 1)} />
                                          <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                          <RechartsTooltip content={<TradingViewTooltip valueFormatter={fmtNum} />} />
                                          {[...selectedAiModels].map((mod, i) => (
                                            <Line key={mod} type="monotone" dataKey={mod} stroke={compareColors[i % compareColors.length]} strokeWidth={2} dot={false} name={mod} />
                                          ))}
                                        </LineChart>
                                      ) : (
                                        <BarChart data={data} margin={{ right: 20 }}>
                                          <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                          <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(data.length / 8) - 1)} />
                                          <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                          <RechartsTooltip contentStyle={chartTooltipStyle} />
                                          {[...selectedAiModels].map((mod, i) => (
                                            <Bar key={mod} dataKey={mod} fill={compareColors[i % compareColors.length]} radius={[4, 4, 0, 0]} name={mod} />
                                          ))}
                                        </BarChart>
                                      )}
                                    </ResponsiveContainer>
                                  )}
                                </Box>
                              </Box>
                            );
                          })}
                          {/* Recap table */}
                          <Box sx={{ overflowX: 'auto', mt: 1 }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead><tr style={{ backgroundColor: '#F9FAFB' }}>
                                <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Model</th>
                                <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Requests</th>
                                <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Tokens In</th>
                                <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Tokens Out</th>
                              </tr></thead>
                              <tbody>
                                {[...selectedAiModels].map((mod, idx) => {
                                  const ts = aiPerModelTimeSeries[mod] || [];
                                  const totReq = ts.reduce((s: number, d: any) => s + (d.requests || 0), 0);
                                  const totIn = ts.reduce((s: number, d: any) => s + (d.tokensIn || 0), 0);
                                  const totOut = ts.reduce((s: number, d: any) => s + (d.tokensOut || 0), 0);
                                  return (
                                    <tr key={mod} style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#FAFBFC' }}>
                                      <td style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 500, color: '#101828' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: compareColors[idx % compareColors.length], flexShrink: 0 }} />
                                          {mod}
                                        </Box>
                                      </td>
                                      <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right', fontWeight: 600 }}>{fmtNum(totReq)}</td>
                                      <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{fmtNum(totIn)}</td>
                                      <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{fmtNum(totOut)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </Box>
                        </Box>
                      ) : (
                        <Box sx={{ p: 4, textAlign: 'center' }}><Typography sx={{ fontSize: '13px', color: '#9CA3AF' }}>Select models above to compare</Typography></Box>
                      )}
                    </Box>
                  )}
                  </>)}
                </>
              )}
            </Box>

            {/* ── Cloud Storage Section (full width when past sidebar) ── */}
            <Box sx={{
              mb: 3,
              ml: pastSidebar ? 0 : (explorerCollapsed ? '32px' : '268px'),
              transition: 'margin-left 0.3s ease',
            }}>
              <SectionHeader title="Cloud Storage" subtitle="Storage Gateway statistics: upload, download, requests and storage changes" collapsed={collapsedSections.has('storage')} onToggle={() => toggleSection('storage')} />
              {!collapsedSections.has('storage') && (
                <>
                  {/* 4 KPIs */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
                    <KpiCard label="Resources" value={String(bucketsCount + repositoriesCount)} color="#8B5CF6" loading={false} />
                    <KpiCard label="Upload" value={formatBytes(totalUpload)} color="#10B981" loading={statsLoading} />
                    <KpiCard label="Download" value={formatBytes(totalDownload)} color="#3B82F6" loading={statsLoading} />
                    <KpiCard label="Requests" value={fmtNum(totalRequests)} color="#617BFF" loading={statsLoading} />
                  </Box>

                  {/* A) Overview: 4 full-width charts with aggregated/temporal toggle */}
                  <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#667085', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.5 }}>Overview</Typography>

                  {/* Requests Chart */}
                  <EnhancedChartCard 
                    title="Requests" 
                    loading={statsLoading}
                    viewMode={gwRequestsViewMode}
                    onViewModeChange={setGwRequestsViewMode}
                    chartType={gwRequestsChartType}
                    onChartTypeChange={setGwRequestsChartType}
                  >
                    {gwRequestsViewMode === 'aggregated' ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={[{ name: 'Total', requests: totalRequests }]} margin={{ right: 20, left: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                          <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} />
                          <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                          <RechartsTooltip contentStyle={chartTooltipStyle} formatter={(v: number) => [fmtNum(v), 'Requests']} />
                          <Bar dataKey="requests" fill="#617BFF" radius={[6, 6, 0, 0]} name="Requests" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : requestsChartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        {gwRequestsChartType === 'line' ? (
                          <AreaChart data={requestsChartData} margin={{ right: 20 }}>
                            <defs><linearGradient id="clrStReq" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#617BFF" stopOpacity={0.3} /><stop offset="95%" stopColor="#617BFF" stopOpacity={0} /></linearGradient></defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                            <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(requestsChartData.length / 8) - 1)} />
                            <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                            <RechartsTooltip content={<TradingViewTooltip color="#617BFF" valueFormatter={fmtNum} />} />
                            <Area type="monotone" dataKey="requests" stroke="#617BFF" fill="url(#clrStReq)" strokeWidth={2} name="Requests" />
                          </AreaChart>
                        ) : (
                          <BarChart data={requestsChartData} margin={{ right: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                            <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(requestsChartData.length / 8) - 1)} />
                            <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                            <RechartsTooltip content={<TradingViewTooltip color="#617BFF" valueFormatter={fmtNum} />} />
                            <Bar dataKey="requests" fill="#617BFF" radius={[4, 4, 0, 0]} name="Requests" />
                          </BarChart>
                        )}
                      </ResponsiveContainer>
                    ) : <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography sx={{ fontSize: '12px', color: '#9CA3AF' }}>{statsLoading ? 'Loading...' : 'No data'}</Typography></Box>}
                  </EnhancedChartCard>

                  {/* Traffic Chart (Upload + Download) */}
                  <EnhancedChartCard 
                    title="Traffic (Upload / Download)" 
                    loading={statsLoading}
                    viewMode={gwTrafficViewMode}
                    onViewModeChange={setGwTrafficViewMode}
                    chartType={gwTrafficChartType}
                    onChartTypeChange={setGwTrafficChartType}
                  >
                    {gwTrafficViewMode === 'aggregated' ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={[
                          { name: 'Upload', value: totalUpload / (1024 * 1024), fill: '#10B981' },
                          { name: 'Download', value: totalDownload / (1024 * 1024), fill: '#3B82F6' },
                        ]} margin={{ right: 20, left: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                          <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} />
                          <YAxis tick={axisTick} axisLine={false} tickLine={false} unit=" MB" />
                          <RechartsTooltip contentStyle={chartTooltipStyle} formatter={(v: number) => [`${v.toFixed(2)} MB`, '']} />
                          <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                            {[
                              { name: 'Upload', fill: '#10B981' },
                              { name: 'Download', fill: '#3B82F6' },
                            ].map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : trafficChartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        {gwTrafficChartType === 'line' ? (
                          <AreaChart data={trafficChartData} margin={{ right: 20 }}>
                            <defs>
                              <linearGradient id="clrStUp" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10B981" stopOpacity={0.3} /><stop offset="95%" stopColor="#10B981" stopOpacity={0} /></linearGradient>
                              <linearGradient id="clrStDl" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} /><stop offset="95%" stopColor="#3B82F6" stopOpacity={0} /></linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                            <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(trafficChartData.length / 8) - 1)} />
                            <YAxis tick={axisTick} axisLine={false} tickLine={false} unit=" MB" />
                            <RechartsTooltip content={<TradingViewTooltip color="#10B981" valueFormatter={(v: number) => `${v.toFixed(2)} MB`} />} />
                            <Area type="monotone" dataKey="upload" stroke="#10B981" fill="url(#clrStUp)" strokeWidth={2} name="Upload" />
                            <Area type="monotone" dataKey="download" stroke="#3B82F6" fill="url(#clrStDl)" strokeWidth={2} name="Download" />
                          </AreaChart>
                        ) : (
                          <BarChart data={trafficChartData} margin={{ right: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                            <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(trafficChartData.length / 8) - 1)} />
                            <YAxis tick={axisTick} axisLine={false} tickLine={false} unit=" MB" />
                            <RechartsTooltip contentStyle={chartTooltipStyle} />
                            <Bar dataKey="upload" fill="#10B981" radius={[4, 4, 0, 0]} name="Upload" />
                            <Bar dataKey="download" fill="#3B82F6" radius={[4, 4, 0, 0]} name="Download" />
                          </BarChart>
                        )}
                      </ResponsiveContainer>
                    ) : <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography sx={{ fontSize: '12px', color: '#9CA3AF' }}>{statsLoading ? 'Loading...' : 'No data'}</Typography></Box>}
                  </EnhancedChartCard>

                  {/* Storage Change Chart */}
                  <EnhancedChartCard 
                    title="Storage Change" 
                    loading={statsLoading}
                    viewMode={gwStorageViewMode}
                    onViewModeChange={setGwStorageViewMode}
                    chartType={gwStorageChartType}
                    onChartTypeChange={setGwStorageChartType}
                  >
                    {gwStorageViewMode === 'aggregated' ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={[{ name: 'Net Change', storage: totalStorageChange / (1024 * 1024) }]} margin={{ right: 20, left: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                          <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} />
                          <YAxis tick={axisTick} axisLine={false} tickLine={false} unit=" MB" />
                          <RechartsTooltip contentStyle={chartTooltipStyle} formatter={(v: number) => [`${v.toFixed(2)} MB`, 'Change']} />
                          <Bar dataKey="storage" fill="#8B5CF6" radius={[6, 6, 0, 0]} name="Change" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : storageChartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        {gwStorageChartType === 'line' ? (
                          <AreaChart data={storageChartData} margin={{ right: 20 }}>
                            <defs><linearGradient id="clrStCh" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3} /><stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} /></linearGradient></defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                            <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(storageChartData.length / 8) - 1)} />
                            <YAxis tick={axisTick} axisLine={false} tickLine={false} unit=" MB" />
                            <RechartsTooltip content={<TradingViewTooltip color="#8B5CF6" valueFormatter={(v: number) => `${v.toFixed(2)} MB`} />} />
                            <Area type="monotone" dataKey="storage" stroke="#8B5CF6" fill="url(#clrStCh)" strokeWidth={2} name="Change" />
                          </AreaChart>
                        ) : (
                          <BarChart data={storageChartData} margin={{ right: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                            <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(storageChartData.length / 8) - 1)} />
                            <YAxis tick={axisTick} axisLine={false} tickLine={false} unit=" MB" />
                            <RechartsTooltip content={<TradingViewTooltip color="#8B5CF6" valueFormatter={(v: number) => `${v.toFixed(2)} MB`} />} />
                            <Bar dataKey="storage" fill="#8B5CF6" radius={[4, 4, 0, 0]} name="Change" />
                          </BarChart>
                        )}
                      </ResponsiveContainer>
                    ) : <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography sx={{ fontSize: '12px', color: '#9CA3AF' }}>{statsLoading ? 'Loading...' : 'No data'}</Typography></Box>}
                  </EnhancedChartCard>

                  {/* B) Comparisons */}
                  <Box ref={comparisonsSectionRef}>
                    <SectionHeader title="Comparisons" subtitle="Compare upload, download, and storage across workspaces and repositories" collapsed={collapsedSections.has('storageComparisons')} onToggle={() => toggleSection('storageComparisons')} />
                  </Box>
                  {!collapsedSections.has('storageComparisons') && (<>

                  {/* B.1) Workspace/Repository Comparison - based on hierarchy level */}
                  {/* Organization level: Workspace Comparison */}
                  {/* Workspace level: Repository Comparison */}
                  {/* Repository level: No comparison (hidden) */}
                  {!explorerRepo && (
                    <Box sx={{ bgcolor: 'white', borderRadius: '10px', border: '1px solid #E9EAEB', overflow: 'hidden', mb: 2.5 }}>
                      <Box sx={{ p: 2, borderBottom: '1px solid #E9EAEB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828' }}>
                            {!explorerWs ? 'Workspace Comparison' : 'Repository Comparison'}
                          </Typography>
                          <Typography sx={{ fontSize: '11px', color: '#9CA3AF', mt: 0.25 }}>
                            {!explorerWs 
                              ? 'Compare upload, download, requests across workspaces' 
                              : 'Compare storage usage across repositories'}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ p: 2, borderBottom: '1px solid #E9EAEB' }}>
                        {!explorerWs ? (
                          // Organization level: Select workspaces
                          <MultiSelectDropdown
                            label="Select workspaces"
                            options={workspaces.map((ws: any) => ws.name)}
                            selected={new Set([...selectedStorageCompareWs].map(id => workspaces.find((ws: any) => ws.id === id)?.name).filter(Boolean) as string[])}
                            onToggle={(name) => {
                              const ws = workspaces.find((w: any) => w.name === name);
                              if (ws) toggleStorageCompareWs(ws.id);
                            }}
                            maxItems={5}
                            colors={compareColors}
                          />
                        ) : (
                          // Workspace level: Select repositories
                          <MultiSelectDropdown
                            label="Select repositories"
                            options={repositoryStats.map((repo: any) => repo.name)}
                            selected={new Set(repositoryStats.filter((repo: any) => selectedStorageCompareRepos.has(repo.id)).map((repo: any) => repo.name))}
                            onToggle={(repoName: string) => {
                              const repo = repositoryStats.find((r: any) => r.name === repoName);
                              if (repo) toggleStorageCompareRepo(repo.id);
                            }}
                            maxItems={5}
                            colors={compareColors}
                          />
                        )}
                      </Box>
                      {!explorerWs ? (
                        // Organization level: Workspace comparison content
                        storageComparisonData.length > 0 ? (
                          <Box sx={{ p: 2.5 }}>
                            {/* 3 per-metric line charts */}
                            {([
                              { key: 'requests' as const, label: 'Requests', fmt: fmtNum },
                              { key: 'upload' as const, label: 'Upload', fmt: formatBytes },
                              { key: 'download' as const, label: 'Download', fmt: formatBytes },
                            ]).map(({ key, label, fmt }) => {
                              const data = storageWsComparisonTimeSeries[key] || [];
                              const selectedWs = workspaceStats.filter(ws => selectedStorageCompareWs.has(ws.id));
                              return (
                                <Box key={key} sx={{ mb: 2.5 }}>
                                  <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#344054', mb: 1 }}>{label}</Typography>
                                  <Box sx={{ height: CHART_HEIGHT }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                      <LineChart data={data} margin={{ right: 20 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                        <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(data.length / 8) - 1)} />
                                        <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                        <RechartsTooltip content={<TradingViewTooltip valueFormatter={fmt} />} />
                                        {selectedWs.map((ws, i) => (
                                          <Line key={ws.id} type="monotone" dataKey={ws.name} stroke={compareColors[i % compareColors.length]} strokeWidth={2} dot={false} name={ws.name} />
                                        ))}
                                      </LineChart>
                                    </ResponsiveContainer>
                                  </Box>
                                </Box>
                              );
                            })}
                            {/* Recap table */}
                            <Box sx={{ overflowX: 'auto', mt: 1 }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#F9FAFB' }}>
                                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Workspace</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Upload</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Download</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Requests</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Total Traffic</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Rank</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {[...storageComparisonData].sort((a, b) => (b.upload + b.download) - (a.upload + a.download)).map((ws, idx) => (
                                    <tr key={ws.id} style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#FAFBFC' }}>
                                      <td style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 500, color: '#101828' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: ws.color, flexShrink: 0 }} />
                                          {ws.name}
                                        </Box>
                                      </td>
                                      <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{formatBytes(ws.upload)}</td>
                                      <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{formatBytes(ws.download)}</td>
                                      <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{fmtNum(ws.requests)}</td>
                                      <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right', fontWeight: 600 }}>{formatBytes(ws.upload + ws.download)}</td>
                                      <td style={{ padding: '8px 12px', fontSize: '12px', color: '#617BFF', textAlign: 'right', fontWeight: 600 }}>#{idx + 1}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </Box>
                          </Box>
                        ) : (
                          <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography sx={{ fontSize: '13px', color: '#9CA3AF' }}>Select workspaces above to compare storage usage</Typography>
                          </Box>
                        )
                      ) : (
                        // Workspace level: Repository comparison content
                        selectedStorageCompareRepos.size > 0 ? (
                          <Box sx={{ p: 2.5 }}>
                            {/* Aggregated bar charts for repositories */}
                            {([
                              { key: 'buckets' as const, label: 'Buckets', fmt: fmtNum },
                              { key: 'storageTraffic' as const, label: 'Storage Traffic', fmt: formatBytes },
                            ]).map(({ key, label, fmt }) => {
                              const selectedRepos = repositoryStats.filter(repo => selectedStorageCompareRepos.has(repo.id));
                              const aggregatedData = selectedRepos.map((repo, i) => ({
                                name: repo.name,
                                value: repo[key] || 0,
                                fill: compareColors[i % compareColors.length],
                              }));
                              return (
                                <Box key={key} sx={{ mb: 2.5 }}>
                                  <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#344054', mb: 1 }}>{label}</Typography>
                                  <Box sx={{ height: CHART_HEIGHT }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                      <BarChart data={aggregatedData} margin={{ right: 20, left: 20 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                        <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} />
                                        <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                        <RechartsTooltip contentStyle={chartTooltipStyle} formatter={(v: number) => [fmt(v), label]} />
                                        <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                                          {aggregatedData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.fill} />
                                          ))}
                                        </Bar>
                                      </BarChart>
                                    </ResponsiveContainer>
                                  </Box>
                                </Box>
                              );
                            })}
                            {/* Recap table */}
                            <Box sx={{ overflowX: 'auto', mt: 1 }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#F9FAFB' }}>
                                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Repository</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Buckets</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>AI LLMs</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Storage Traffic</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Rank</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {[...repositoryStats.filter(repo => selectedStorageCompareRepos.has(repo.id))].sort((a, b) => b.buckets - a.buckets).map((repo, idx) => (
                                    <tr key={repo.id} style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#FAFBFC' }}>
                                      <td style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 500, color: '#101828' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: compareColors[idx % compareColors.length], flexShrink: 0 }} />
                                          {repo.name}
                                        </Box>
                                      </td>
                                      <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{repo.buckets}</td>
                                      <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{repo.aiLlms}</td>
                                      <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right', fontWeight: 600 }}>{formatBytes(repo.storageTraffic)}</td>
                                      <td style={{ padding: '8px 12px', fontSize: '12px', color: '#617BFF', textAlign: 'right', fontWeight: 600 }}>#{idx + 1}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </Box>
                          </Box>
                        ) : (
                          <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography sx={{ fontSize: '13px', color: '#9CA3AF' }}>Select repositories above to compare storage usage</Typography>
                          </Box>
                        )
                      )}
                    </Box>
                  )}

                  {/* Provider Comparison */}
                  <Box sx={{ bgcolor: 'white', borderRadius: '10px', border: '1px solid #E9EAEB', overflow: 'hidden', mb: 2.5 }}>
                    <Box sx={{ p: 2, borderBottom: '1px solid #E9EAEB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828' }}>Provider Comparison</Typography>
                        <Typography sx={{ fontSize: '11px', color: '#9CA3AF', mt: 0.25 }}>Compare storage usage across cloud providers</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ p: 2, borderBottom: '1px solid #E9EAEB' }}>
                      <MultiSelectDropdown
                        label="Select providers"
                        options={['AWS S3', 'Google Cloud', 'Azure Blob', 'Backblaze B2', 'Wasabi']}
                        selected={selectedStorageProviders}
                        onToggle={toggleStorageProvider}
                        maxItems={5}
                        colors={compareColors}
                      />
                    </Box>
                    {selectedStorageProviders.size > 0 ? (
                      <Box sx={{ p: 2.5 }}>
                        {(['upload', 'download', 'requests'] as const).map((metric) => {
                          const label = metric === 'upload' ? 'Upload' : metric === 'download' ? 'Download' : 'Requests';
                          const fmt = metric === 'requests' ? fmtNum : formatBytes;
                          // Generate mockup time-series data for providers
                          const providerData = (() => {
                            const { startDate, endDate } = getDateRangeFromPreset(dateRange);
                            const intervalMs = getSamplingIntervalMs(dateRange);
                            const providers = [...selectedStorageProviders];
                            const data: any[] = [];
                            let seed = 12345 + (metric === 'upload' ? 0 : metric === 'download' ? 1000 : 2000);
                            const seededRandom = () => { seed = (seed * 1103515245 + 12345) & 0x7fffffff; return (seed % 1000) / 1000; };
                            for (let t = startDate.getTime(); t <= endDate.getTime(); t += intervalMs) {
                              const row: any = { name: formatBucketLabel(t, dateRange) };
                              providers.forEach((prov, i) => {
                                const base = metric === 'requests' ? 20 + seededRandom() * 100 : 0.5 + seededRandom() * 5;
                                row[prov] = Math.round(base * (1 + i * 0.2) * 100) / 100;
                              });
                              data.push(row);
                            }
                            return data;
                          })();
                          return (
                            <Box key={metric} sx={{ mb: 2.5 }}>
                              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#344054', mb: 1 }}>{label}</Typography>
                              <Box sx={{ height: CHART_HEIGHT }}>
                                <ResponsiveContainer width="100%" height="100%">
                                  <LineChart data={providerData} margin={{ right: 20 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                    <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(providerData.length / 8) - 1)} />
                                    <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                    <RechartsTooltip content={<TradingViewTooltip valueFormatter={fmt} />} />
                                    {[...selectedStorageProviders].map((prov, i) => (
                                      <Line key={prov} type="monotone" dataKey={prov} stroke={compareColors[i % compareColors.length]} strokeWidth={2} dot={false} name={prov} />
                                    ))}
                                  </LineChart>
                                </ResponsiveContainer>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '13px', color: '#9CA3AF' }}>Select providers above to compare storage usage</Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Bucket Comparison */}
                  <Box sx={{ bgcolor: 'white', borderRadius: '10px', border: '1px solid #E9EAEB', overflow: 'hidden', mb: 2.5 }}>
                    <Box sx={{ p: 2, borderBottom: '1px solid #E9EAEB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828' }}>Bucket Comparison</Typography>
                        <Typography sx={{ fontSize: '11px', color: '#9CA3AF', mt: 0.25 }}>Compare storage usage across buckets</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ p: 2, borderBottom: '1px solid #E9EAEB' }}>
                      <MultiSelectDropdown
                        label="Select buckets"
                        options={['bucket-prod', 'bucket-staging', 'bucket-backup', 'bucket-assets', 'bucket-logs']}
                        selected={selectedStorageBuckets}
                        onToggle={toggleStorageBucket}
                        maxItems={5}
                        colors={compareColors}
                      />
                    </Box>
                    {selectedStorageBuckets.size > 0 ? (
                      <Box sx={{ p: 2.5 }}>
                        {(['upload', 'download', 'requests'] as const).map((metric) => {
                          const label = metric === 'upload' ? 'Upload' : metric === 'download' ? 'Download' : 'Requests';
                          const fmt = metric === 'requests' ? fmtNum : formatBytes;
                          // Generate mockup time-series data for buckets
                          const bucketData = (() => {
                            const { startDate, endDate } = getDateRangeFromPreset(dateRange);
                            const intervalMs = getSamplingIntervalMs(dateRange);
                            const selectedBuckets = [...selectedStorageBuckets];
                            const data: any[] = [];
                            let seed = 67890 + (metric === 'upload' ? 0 : metric === 'download' ? 1000 : 2000);
                            const seededRandom = () => { seed = (seed * 1103515245 + 12345) & 0x7fffffff; return (seed % 1000) / 1000; };
                            for (let t = startDate.getTime(); t <= endDate.getTime(); t += intervalMs) {
                              const row: any = { name: formatBucketLabel(t, dateRange) };
                              selectedBuckets.forEach((bucket, i) => {
                                const base = metric === 'requests' ? 10 + seededRandom() * 80 : 0.2 + seededRandom() * 3;
                                row[bucket] = Math.round(base * (1 + i * 0.15) * 100) / 100;
                              });
                              data.push(row);
                            }
                            return data;
                          })();
                          return (
                            <Box key={metric} sx={{ mb: 2.5 }}>
                              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#344054', mb: 1 }}>{label}</Typography>
                              <Box sx={{ height: CHART_HEIGHT }}>
                                <ResponsiveContainer width="100%" height="100%">
                                  <LineChart data={bucketData} margin={{ right: 20 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                    <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(bucketData.length / 8) - 1)} />
                                    <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                    <RechartsTooltip content={<TradingViewTooltip valueFormatter={fmt} />} />
                                    {[...selectedStorageBuckets].map((bucket, i) => (
                                      <Line key={bucket} type="monotone" dataKey={bucket} stroke={compareColors[i % compareColors.length]} strokeWidth={2} dot={false} name={bucket} />
                                    ))}
                                  </LineChart>
                                </ResponsiveContainer>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '13px', color: '#9CA3AF' }}>Select buckets above to compare storage usage</Typography>
                      </Box>
                    )}
                  </Box>
                  </>)}
                </>
              )}
            </Box>
        </>
      )}

      {/* ═══════════ PRIVATE CHAT ═══════════ */}
      {subSection === 'chat' && (
        <>
        {/* Page Header */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#101828' }}>Private Chat Telemetry</Typography>
          <Typography sx={{ fontSize: '13px', color: '#667085', mt: 0.25 }}>Track conversations, token usage, and model activity across your organization's private chat</Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 0 }}>
          {/* Left Explorer Sidebar: Organisation → Users */}
          <Box ref={sidebarRef} sx={{
            width: explorerCollapsed ? 28 : 240,
            minWidth: explorerCollapsed ? 28 : 240,
            borderRight: explorerCollapsed ? 'none' : '1px solid #E9EAEB',
            pr: explorerCollapsed ? 0 : 2,
            mr: explorerCollapsed ? 0.5 : 3,
            position: 'sticky', top: 16, alignSelf: 'flex-start',
            maxHeight: 'calc(100vh - 32px)', overflow: 'hidden',
            bgcolor: 'white', zIndex: 5,
            transition: 'width 0.25s ease, min-width 0.25s ease, padding 0.25s ease, margin 0.25s ease',
          }}>
            {explorerCollapsed ? (
              <Box onClick={() => setExplorerCollapsed(false)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 0.5, cursor: 'pointer', '&:hover .explorer-toggle': { bgcolor: '#E9EAEB' } }}>
                <Box className="explorer-toggle" sx={{ width: 24, height: 24, borderRadius: '5px', bgcolor: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#667085" strokeWidth="2"><polyline points="9 6 15 12 9 18" /></svg>
                </Box>
              </Box>
            ) : (
              <Box onClick={() => setExplorerCollapsed(true)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, cursor: 'pointer', py: 0.5, '&:hover .explorer-toggle': { bgcolor: '#E9EAEB' } }}>
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Explorer</Typography>
                <Box className="explorer-toggle" sx={{ width: 24, height: 24, borderRadius: '5px', bgcolor: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#667085" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
                </Box>
              </Box>
            )}
            <Box sx={{ maxHeight: explorerCollapsed ? 0 : 'calc(100vh - 220px)', overflow: explorerCollapsed ? 'hidden' : 'auto', opacity: explorerCollapsed ? 0 : 1, transition: 'max-height 0.25s ease, opacity 0.2s ease', '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { bgcolor: '#D0D5DD', borderRadius: 2 } }}>
              {/* Org root */}
              <Box onClick={() => { setChatExplorerUser(null); }} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1, borderRadius: '8px', cursor: 'pointer', mb: 0.75, bgcolor: !chatExplorerUser ? '#F0F2FF' : 'transparent', border: !chatExplorerUser ? '1px solid #D6DEFF' : '1px solid transparent', transition: 'all 0.15s ease', '&:hover': { bgcolor: !chatExplorerUser ? '#F0F2FF' : '#F9FAFB' } }}>
                <Box sx={{ width: 28, height: 28, borderRadius: '6px', bgcolor: !chatExplorerUser ? '#617BFF' : '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={!chatExplorerUser ? 'white' : '#667085'} strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18" /></svg>
                </Box>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: !chatExplorerUser ? '#3B4ECC' : '#344054', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{orgName || 'Organization'}</Typography>
              </Box>
              {/* User nodes */}
              {team.map((member: any) => {
                const userId = member.id || member.userId;
                const isActive = chatExplorerUser === userId;
                const displayName = member.name || member.email?.split('@')[0] || 'Unknown';
                return (
                  <Box key={userId} onClick={() => { setChatExplorerUser(userId); }} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 0.75, borderRadius: '6px', cursor: 'pointer', mb: 0.25, ml: 1.5, bgcolor: isActive ? '#F0F2FF' : 'transparent', '&:hover': { bgcolor: isActive ? '#F0F2FF' : '#F9FAFB' } }}>
                    <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: isActive ? '#617BFF' : '#E9EAEB', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Typography sx={{ fontSize: '9px', fontWeight: 700, color: isActive ? 'white' : '#667085', textTransform: 'uppercase' }}>{(displayName[0] || '?')}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '12px', fontWeight: isActive ? 600 : 400, color: isActive ? '#3B4ECC' : '#535862', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayName}</Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Right content area */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* ── Sticky header: breadcrumb + date filter (Gateway-aligned) ── */}
            <Box sx={{ position: 'sticky', top: 16, zIndex: 4, bgcolor: 'white', pb: 1, mb: 2 }}>
              {/* Breadcrumb */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2, px: 0.5 }}>
                <Box onClick={() => { setChatExplorerUser(null); }} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', '&:hover .bc-text': { textDecoration: 'underline' } }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18" /></svg>
                  <Typography className="bc-text" sx={{ fontSize: '13px', color: '#617BFF', fontWeight: 500 }}>{orgName || 'Organization'}</Typography>
                </Box>
                {chatExplorerUser && (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D0D5DD" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#344054" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                      <Typography sx={{ fontSize: '13px', color: '#101828', fontWeight: 600 }}>{(team.find((m: any) => (m.id || m.userId) === chatExplorerUser) as any)?.name || (team.find((m: any) => (m.id || m.userId) === chatExplorerUser) as any)?.email?.split('@')[0] || 'User'}</Typography>
                    </Box>
                  </>
                )}
              </Box>
              {/* Date range filter — identical to Gateway */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Box sx={{ display: 'flex', gap: 0.5, bgcolor: '#F3F4F6', borderRadius: '8px', p: '3px' }}>
                  {(['24h', '7d', '30d', '90d', '180d', '365d'] as const).map((r) => (
                    <Button key={r} size="small" onClick={() => setDateRange(r)} sx={{
                      textTransform: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, px: 1.5, py: 0.25, minWidth: 0,
                      bgcolor: dateRange === r ? 'white' : 'transparent',
                      color: dateRange === r ? '#101828' : '#667085',
                      boxShadow: dateRange === r ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                      '&:hover': { bgcolor: dateRange === r ? 'white' : 'rgba(0,0,0,0.04)' },
                    }}>{r}</Button>
                  ))}
                </Box>
                {chatUserStatsLoading && <CircularProgress size={14} sx={{ color: '#617BFF' }} />}
              </Box>
            </Box>

            {/* KPI Row */}
            {(() => {
              // If a user is selected in explorer, show their stats; otherwise org-level
              const selectedUser = chatExplorerUser ? chatUserStats.find(u => u.id === chatExplorerUser) : null;
              const kpiConversations = selectedUser ? selectedUser.conversations : chatTotalConversations;
              const kpiTokensIn = selectedUser ? selectedUser.tokensIn : chatTotalTokensIn;
              const kpiTokensOut = selectedUser ? selectedUser.tokensOut : chatTotalTokensOut;
              const kpiModels = selectedUser ? Object.keys(selectedUser.modelUsage).filter(m => m !== 'Unknown').length : chatTotalModels;
              return (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
                  <KpiCard label="Total Conversations" value={fmtNum(kpiConversations)} color="#617BFF" loading={chatUserStatsLoading} />
                  <KpiCard label="Tokens In" value={fmtNum(kpiTokensIn)} color="#10B981" loading={chatUserStatsLoading} />
                  <KpiCard label="Tokens Out" value={fmtNum(kpiTokensOut)} color="#F59E0B" loading={chatUserStatsLoading} />
                  <KpiCard label="Models Used" value={String(kpiModels)} color="#8B5CF6" loading={chatUserStatsLoading} />
                </Box>
              );
            })()}

            {/* ── Overview charts: one per metric with Line/Bar toggle (Gateway pattern) ── */}
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#667085', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.5 }}>Overview</Typography>

            {(() => {
              // If a user is selected in the explorer, show their daily data; otherwise org-level
              let chartData = chatOvertimeData;
              if (chatExplorerUser) {
                const selectedUser = chatUserStats.find(u => u.id === chatExplorerUser);
                if (selectedUser && selectedUser.dailyData.length > 0) {
                  type ChatUOT = { tokensIn: number; tokensOut: number; apiCalls: number };
                  const rawEntries: RawTimedEntry[] = selectedUser.dailyData.map(d => ({
                    timestamp: d.timestamp,
                    tokensIn: d.tokensIn,
                    tokensOut: d.tokensOut,
                    apiCalls: d.apiCalls,
                  }));
                  chartData = sampleTimeSeries<ChatUOT>(rawEntries, dateRange, ['tokensIn', 'tokensOut', 'apiCalls']);
                } else {
                  chartData = [];
                }
              }
              const metrics = [
                { key: 'tokensIn', label: 'Tokens In', color: '#617BFF', gradId: 'clrChatTokIn' },
                { key: 'tokensOut', label: 'Tokens Out', color: '#10B981', gradId: 'clrChatTokOut' },
                { key: 'apiCalls', label: 'API Calls', color: '#F59E0B', gradId: 'clrChatApi' },
              ];
              // Compute aggregated totals for single-bar view
              const aggTotals: Record<string, number> = {};
              chartData.forEach(d => { metrics.forEach(m => { aggTotals[m.key] = (aggTotals[m.key] || 0) + (Number(d[m.key]) || 0); }); });

              return metrics.map(({ key, label, color, gradId }) => {
                const mode = chatOverviewChartType[key] || 'line';
                const vMode = chatOverviewViewMode[key] || 'temporal';
                return (
                  <EnhancedChartCard
                    key={key}
                    title={label}
                    loading={chatUserStatsLoading}
                    viewMode={vMode}
                    onViewModeChange={(m) => setChatOverviewViewMode(prev => ({ ...prev, [key]: m }))}
                    chartType={mode}
                    onChartTypeChange={(t) => setChatOverviewChartType(prev => ({ ...prev, [key]: t }))}
                  >
                    {vMode === 'aggregated' ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={[{ name: 'Total', [key]: aggTotals[key] || 0 }]} margin={{ right: 20, left: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                          <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} />
                          <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                          <RechartsTooltip contentStyle={{ borderRadius: 8, border: '1px solid #E9EAEB', fontSize: 12 }} formatter={(v: number) => [fmtNum(v), label]} />
                          <Bar dataKey={key} fill={color} radius={[6, 6, 0, 0]} name={label} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : chartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        {mode === 'line' ? (
                          <AreaChart data={chartData} margin={{ right: 20 }}>
                            <defs><linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={color} stopOpacity={0.3} /><stop offset="95%" stopColor={color} stopOpacity={0} /></linearGradient></defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                            <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(chartData.length / 8) - 1)} />
                            <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                            <RechartsTooltip content={<TradingViewTooltip color={color} valueFormatter={(v: number) => fmtNum(v)} />} />
                            <Area type="monotone" dataKey={key} stroke={color} fill={`url(#${gradId})`} strokeWidth={2} name={label} />
                          </AreaChart>
                        ) : (
                          <BarChart data={chartData} margin={{ right: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                            <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(chartData.length / 8) - 1)} />
                            <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                            <RechartsTooltip content={<TradingViewTooltip color={color} valueFormatter={(v: number) => fmtNum(v)} />} />
                            <Bar dataKey={key} fill={color} radius={[4, 4, 0, 0]} name={label} />
                          </BarChart>
                        )}
                      </ResponsiveContainer>
                    ) : <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography sx={{ fontSize: '12px', color: '#9CA3AF' }}>{chatUserStatsLoading ? 'Loading...' : 'No data'}</Typography></Box>}
                  </EnhancedChartCard>
                );
              });
            })()}

            {/* ─── Users Table (read-only data table) - Only show at org level ─── */}
            {!chatExplorerUser && (
            <Box sx={{ bgcolor: 'white', borderRadius: '10px', border: '1px solid #E9EAEB', overflow: 'hidden', mb: 3 }}>
              <Box sx={{ p: 2, borderBottom: '1px solid #E9EAEB' }}>
                <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828' }}>Users</Typography>
                <Typography sx={{ fontSize: '11px', color: '#9CA3AF', mt: 0.25 }}>Usage breakdown per user</Typography>
              </Box>
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #E9EAEB', background: '#FAFBFC' }}>
                      {([
                        { key: 'name' as ChatUserSortCol, label: 'User' },
                        { key: 'conversations' as ChatUserSortCol, label: '#Conversations' },
                        { key: 'tokensIn' as ChatUserSortCol, label: 'Tokens In' },
                        { key: 'tokensOut' as ChatUserSortCol, label: 'Tokens Out' },
                        { key: 'apiCalls' as ChatUserSortCol, label: '#Requests' },
                        { key: 'lastModel' as ChatUserSortCol, label: 'Last Model' },
                      ]).map(col => (
                        <th
                          key={col.key}
                          onClick={() => handleChatUserSort(col.key)}
                          style={{ padding: '10px 12px', textAlign: col.key === 'name' ? 'left' : 'right', fontWeight: 600, color: chatUserSortCol === col.key ? '#617BFF' : '#667085', fontSize: '11px', cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }}
                        >
                          {col.label}
                          {chatUserSortCol === col.key && (
                            <span style={{ marginLeft: 4 }}>{chatUserSortDir === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {chatUserStatsLoading ? (
                      <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center' }}><CircularProgress size={20} sx={{ color: '#617BFF' }} /></td></tr>
                    ) : sortedChatUserStats.length === 0 ? (
                      <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#9CA3AF' }}>No user data available</td></tr>
                    ) : sortedChatUserStats.map((user, idx) => (
                        <tr key={user.id} style={{ borderBottom: '1px solid #F2F4F7', background: idx % 2 === 0 ? 'white' : '#FAFBFC' }}>
                          <td style={{ padding: '10px 12px', fontWeight: 500, color: '#101828' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: '#E9EAEB', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#667085', textTransform: 'uppercase' }}>{(user.name[0] || '?')}</Typography>
                              </Box>
                              <Box>
                                <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#101828', lineHeight: 1.2 }}>{user.name}</Typography>
                                <Typography sx={{ fontSize: '10px', color: '#9CA3AF', lineHeight: 1.2 }}>{user.email}</Typography>
                              </Box>
                            </Box>
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', color: '#344054' }}>{fmtNum(user.conversations)}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', color: '#344054' }}>{fmtNum(user.tokensIn)}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', color: '#344054' }}>{fmtNum(user.tokensOut)}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', color: '#344054' }}>{fmtNum(user.apiCalls)}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                            {user.lastModel ? (
                              <Chip label={user.lastModel} size="small" sx={{ fontSize: '10px', height: 22, bgcolor: '#F0F2FF', color: '#617BFF', fontWeight: 500 }} />
                            ) : (
                              <Typography sx={{ fontSize: '11px', color: '#9CA3AF' }}>—</Typography>
                            )}
                          </td>
                        </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </Box>
            )}

            {/* ─── Comparisons (Gateway-aligned) ─── - Only show at org level */}
            {!chatExplorerUser && (
            <>
            <Box ref={comparisonsSectionRef}>
              <SectionHeader title="Comparisons" subtitle="Compare token usage and requests across users" collapsed={chatCollapsedSections.has('comparisons')} onToggle={() => toggleChatSection('comparisons')} />
            </Box>
            {!chatCollapsedSections.has('comparisons') && (<>

            {/* User Comparison — Gateway pattern: MultiSelectDropdown + full-width stacked charts */}
            <Box sx={{ bgcolor: 'white', borderRadius: '10px', border: '1px solid #E9EAEB', overflow: 'hidden', mb: 2.5 }}>
              <Box sx={{ p: 2, borderBottom: '1px solid #E9EAEB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828' }}>User Comparison</Typography>
                  <Typography sx={{ fontSize: '11px', color: '#9CA3AF', mt: 0.25 }}>Compare token usage and requests across users</Typography>
                </Box>
              </Box>
              <Box sx={{ p: 2, borderBottom: '1px solid #E9EAEB' }}>
                <MultiSelectDropdown
                  label="Select users"
                  options={chatUserStats.map(u => u.name)}
                  selected={new Set(chatUserStats.filter(u => chatSelectedUsers.has(u.id)).map(u => u.name))}
                  onToggle={(userName: string) => {
                    const user = chatUserStats.find(u => u.name === userName);
                    if (user) {
                      setChatSelectedUsers(prev => { const n = new Set(prev); if (n.has(user.id)) n.delete(user.id); else if (n.size < 5) n.add(user.id); return n; });
                    }
                  }}
                  maxItems={5}
                  colors={compareColors}
                />
              </Box>
              {chatSelectedUsers.size > 0 ? (
                <Box sx={{ p: 2.5 }}>
                  {/* Per-metric comparison charts with Aggregated/Temporal toggle (Gateway pattern) */}
                  {(['tokensIn', 'tokensOut', 'apiCalls'] as const).map((metric) => {
                    const labels: Record<string, string> = { tokensIn: 'Tokens In', tokensOut: 'Tokens Out', apiCalls: 'Requests / API Calls' };
                    const label = labels[metric];
                    const viewMode = chatCompareViewMode[metric] || 'aggregated';
                    const chartType = chatCompareChartType[metric] || 'line';
                    const selectedUserIds = [...chatSelectedUsers];
                    const selectedUsers = chatUserStats.filter(u => chatSelectedUsers.has(u.id));
                    // Aggregated data for bar chart
                    const aggregatedData = selectedUsers.map((user, i) => {
                      const val = metric === 'tokensIn' ? user.tokensIn : metric === 'tokensOut' ? user.tokensOut : user.apiCalls;
                      return { name: user.name, value: val, fill: compareColors[i % compareColors.length] };
                    });
                    // Temporal data for line/bar chart
                    const firstUserId = selectedUserIds[0];
                    const baseData = chatUserComparisonTimeSeries[firstUserId] || [];
                    const mergedData = baseData.map((d: any, idx: number) => {
                      const row: any = { name: d.name };
                      selectedUserIds.forEach(uid => {
                        const uData = chatUserComparisonTimeSeries[uid];
                        const userName = chatUserStats.find(x => x.id === uid)?.name || uid;
                        row[userName] = uData?.[idx]?.[metric] || 0;
                      });
                      return row;
                    });
                    return (
                      <Box key={metric} sx={{ mb: 2.5, bgcolor: '#FAFBFC', borderRadius: '8px', p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#344054' }}>{label}</Typography>
                          <ChartViewModeSelector 
                            viewMode={viewMode} 
                            onViewModeChange={(mode) => setChatCompareViewMode(prev => ({ ...prev, [metric]: mode }))} 
                            chartType={chartType} 
                            onChartTypeChange={(type) => setChatCompareChartType(prev => ({ ...prev, [metric]: type }))} 
                          />
                        </Box>
                        <Box sx={{ height: CHART_HEIGHT }}>
                          {viewMode === 'aggregated' ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={aggregatedData} margin={{ right: 20, left: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} />
                                <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                <RechartsTooltip contentStyle={chartTooltipStyle} formatter={(v: number) => [fmtNum(v), label]} />
                                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                                  {aggregatedData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.fill} />
                                  ))}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          ) : (
                            <ResponsiveContainer width="100%" height="100%">
                              {chartType === 'line' ? (
                                <LineChart data={mergedData} margin={{ right: 20 }}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                  <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(mergedData.length / 8) - 1)} />
                                  <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                  <RechartsTooltip content={<TradingViewTooltip valueFormatter={fmtNum} />} />
                                  {selectedUsers.map((user, i) => (
                                    <Line key={user.id} type="monotone" dataKey={user.name} stroke={compareColors[i % compareColors.length]} strokeWidth={2} dot={false} name={user.name} />
                                  ))}
                                </LineChart>
                              ) : (
                                <BarChart data={mergedData} margin={{ right: 20 }}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                  <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(mergedData.length / 8) - 1)} />
                                  <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                  <RechartsTooltip contentStyle={chartTooltipStyle} />
                                  {selectedUsers.map((user, i) => (
                                    <Bar key={user.id} dataKey={user.name} fill={compareColors[i % compareColors.length]} radius={[4, 4, 0, 0]} name={user.name} />
                                  ))}
                                </BarChart>
                              )}
                            </ResponsiveContainer>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                  {/* Recap table */}
                  <Box sx={{ overflowX: 'auto', mt: 1 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead><tr style={{ backgroundColor: '#F9FAFB' }}>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: '#667085' }}>User</th>
                        <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Conversations</th>
                        <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Tokens In</th>
                        <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Tokens Out</th>
                        <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Requests</th>
                        <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Rank</th>
                      </tr></thead>
                      <tbody>
                        {[...chatSelectedUsers].map((uid, idx) => {
                          const user = chatUserStats.find(u => u.id === uid);
                          if (!user) return null;
                          return (
                            <tr key={uid} style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#FAFBFC' }}>
                              <td style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 500, color: '#101828' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: compareColors[idx % compareColors.length], flexShrink: 0 }} />
                                  {user.name}
                                </Box>
                              </td>
                              <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right', fontWeight: 600 }}>{fmtNum(user.conversations)}</td>
                              <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{fmtNum(user.tokensIn)}</td>
                              <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{fmtNum(user.tokensOut)}</td>
                              <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{fmtNum(user.apiCalls)}</td>
                              <td style={{ padding: '8px 12px', fontSize: '12px', color: '#617BFF', textAlign: 'right', fontWeight: 600 }}>#{idx + 1}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ p: 4, textAlign: 'center' }}><Typography sx={{ fontSize: '13px', color: '#9CA3AF' }}>Select users above to compare token usage and requests</Typography></Box>
              )}
            </Box>

            {/* ─── Model Comparison ─── */}
            {chatModelList.length > 0 && (
            <Box sx={{ bgcolor: 'white', borderRadius: '10px', border: '1px solid #E9EAEB', overflow: 'hidden', mb: 2.5 }}>
              <Box sx={{ p: 2, borderBottom: '1px solid #E9EAEB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828' }}>Model Comparison</Typography>
                  <Typography sx={{ fontSize: '11px', color: '#9CA3AF', mt: 0.25 }}>Compare token usage and requests across models</Typography>
                </Box>
              </Box>
              <Box sx={{ p: 2, borderBottom: '1px solid #E9EAEB' }}>
                <MultiSelectDropdown
                  label="Select models"
                  options={chatModelList}
                  selected={chatSelectedModels}
                  onToggle={toggleChatModel}
                  maxItems={5}
                  colors={compareColors}
                />
              </Box>
              {chatSelectedModels.size > 0 ? (
                <Box sx={{ p: 2.5 }}>
                  {/* Per-metric comparison charts with Aggregated/Temporal toggle (Gateway pattern) */}
                  {(['tokensIn', 'tokensOut', 'apiCalls'] as const).map((metric) => {
                    const labels: Record<string, string> = { tokensIn: 'Tokens In', tokensOut: 'Tokens Out', apiCalls: 'Requests / API Calls' };
                    const label = labels[metric];
                    const viewMode = chatModelCompareViewMode[metric] || 'aggregated';
                    const chartType = chatModelCompareChartType[metric] || 'line';
                    const selectedModelsList = [...chatSelectedModels];
                    // Aggregated data for bar chart
                    const aggregatedData = selectedModelsList.map((model, i) => {
                      const stats = chatModelAggregatedStats[model] || { tokensIn: 0, tokensOut: 0, apiCalls: 0 };
                      const val = metric === 'tokensIn' ? stats.tokensIn : metric === 'tokensOut' ? stats.tokensOut : stats.apiCalls;
                      return { name: model, value: val, fill: compareColors[i % compareColors.length] };
                    });
                    return (
                      <Box key={metric} sx={{ mb: 2.5, bgcolor: '#FAFBFC', borderRadius: '8px', p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#344054' }}>{label}</Typography>
                          <ChartViewModeSelector 
                            viewMode={viewMode} 
                            onViewModeChange={(mode) => setChatModelCompareViewMode(prev => ({ ...prev, [metric]: mode }))} 
                            chartType={chartType} 
                            onChartTypeChange={(type) => setChatModelCompareChartType(prev => ({ ...prev, [metric]: type }))} 
                          />
                        </Box>
                        <Box sx={{ height: CHART_HEIGHT }}>
                          {viewMode === 'aggregated' ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={aggregatedData} margin={{ right: 20, left: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} />
                                <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                <RechartsTooltip contentStyle={chartTooltipStyle} formatter={(v: number) => [fmtNum(v), label]} />
                                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                                  {aggregatedData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.fill} />
                                  ))}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          ) : (
                            <ResponsiveContainer width="100%" height="100%">
                              {chartType === 'line' ? (
                                <LineChart data={chatModelComparisonTimeSeries} margin={{ right: 20 }}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                  <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(chatModelComparisonTimeSeries.length / 8) - 1)} />
                                  <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                  <RechartsTooltip content={<TradingViewTooltip valueFormatter={fmtNum} />} />
                                  {selectedModelsList.map((model, i) => (
                                    <Line key={model} type="monotone" dataKey={`${model}_${metric}`} stroke={compareColors[i % compareColors.length]} strokeWidth={2} dot={false} name={model} />
                                  ))}
                                </LineChart>
                              ) : (
                                <BarChart data={chatModelComparisonTimeSeries} margin={{ right: 20 }}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                  <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(chatModelComparisonTimeSeries.length / 8) - 1)} />
                                  <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                  <RechartsTooltip contentStyle={chartTooltipStyle} />
                                  {selectedModelsList.map((model, i) => (
                                    <Bar key={model} dataKey={`${model}_${metric}`} fill={compareColors[i % compareColors.length]} radius={[4, 4, 0, 0]} name={model} />
                                  ))}
                                </BarChart>
                              )}
                            </ResponsiveContainer>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                  {/* Recap table */}
                  <Box sx={{ overflowX: 'auto', mt: 1 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead><tr style={{ backgroundColor: '#F9FAFB' }}>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Model</th>
                        <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Tokens In</th>
                        <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Tokens Out</th>
                        <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Requests</th>
                        <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Rank</th>
                      </tr></thead>
                      <tbody>
                        {[...chatSelectedModels].map((model, idx) => {
                          const stats = chatModelAggregatedStats[model] || { tokensIn: 0, tokensOut: 0, apiCalls: 0 };
                          return (
                            <tr key={model} style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#FAFBFC' }}>
                              <td style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 500, color: '#101828' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: compareColors[idx % compareColors.length], flexShrink: 0 }} />
                                  {model}
                                </Box>
                              </td>
                              <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{fmtNum(stats.tokensIn)}</td>
                              <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{fmtNum(stats.tokensOut)}</td>
                              <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right', fontWeight: 600 }}>{fmtNum(stats.apiCalls)}</td>
                              <td style={{ padding: '8px 12px', fontSize: '12px', color: '#617BFF', textAlign: 'right', fontWeight: 600 }}>#{idx + 1}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ p: 4, textAlign: 'center' }}><Typography sx={{ fontSize: '13px', color: '#9CA3AF' }}>Select models above to compare token usage and requests</Typography></Box>
              )}
            </Box>
            )}

            {/* ─── Model Usage & Breakdown (single full-width block, Gateway-style) ─── */}
            <Box sx={{ bgcolor: 'white', borderRadius: '10px', border: '1px solid #E9EAEB', p: 2.5, mb: 2.5 }}>
              {/* Header with Global / Per User toggle */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828' }}>Model Usage & Breakdown</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {/* Global / Per User toggle */}
                  <Box sx={{ display: 'flex', bgcolor: '#F3F4F6', borderRadius: '6px', p: 0.25 }}>
                    <Box onClick={() => { setChatModelViewMode('global'); setChatModelSelectedUser(null); }}
                      sx={{ px: 1.5, py: 0.5, borderRadius: '5px', cursor: 'pointer', bgcolor: chatModelViewMode === 'global' ? 'white' : 'transparent', boxShadow: chatModelViewMode === 'global' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.15s' }}>
                      <Typography sx={{ fontSize: '11px', fontWeight: 500, color: chatModelViewMode === 'global' ? '#101828' : '#667085' }}>Aggregated</Typography>
                    </Box>
                    <Box onClick={() => setChatModelViewMode('perUser')}
                      sx={{ px: 1.5, py: 0.5, borderRadius: '5px', cursor: 'pointer', bgcolor: chatModelViewMode === 'perUser' ? 'white' : 'transparent', boxShadow: chatModelViewMode === 'perUser' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.15s' }}>
                      <Typography sx={{ fontSize: '11px', fontWeight: 500, color: chatModelViewMode === 'perUser' ? '#101828' : '#667085' }}>Per User</Typography>
                    </Box>
                  </Box>
                  {/* User selector (only in per-user mode) */}
                  {chatModelViewMode === 'perUser' && (
                    <Box component="select" onChange={(e: any) => setChatModelSelectedUser(e.target.value || null)} value={chatModelSelectedUser || ''}
                      sx={{ fontSize: '12px', border: '1px solid #E9EAEB', borderRadius: '6px', px: 1, py: 0.5, color: '#344054', bgcolor: 'white', outline: 'none', cursor: 'pointer', '&:focus': { borderColor: '#617BFF' } }}>
                      <option value="">Select a user…</option>
                      {chatUserStats.map(u => (
                        <option key={u.id} value={u.id}>{u.name}</option>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
              {/* Content: Pie left + Breakdown right */}
              {(() => {
                const pieData = chatModelViewMode === 'perUser' ? chatModelUsagePieForUser : chatModelUsagePie;
                return pieData.length > 0 ? (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '280px 1fr' }, gap: 3, alignItems: 'start' }}>
                    {/* Pie Chart */}
                    <Box sx={{ height: 240 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2} dataKey="value" nameKey="name">
                            {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                          </Pie>
                          <RechartsTooltip
                            formatter={(value: any, name: any) => [`${fmtNum(Number(value))} calls (${pieData.find(m => m.name === name)?.percent || 0}%)`, name]}
                            contentStyle={chartTooltipStyle}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                    {/* Breakdown bars */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, pt: 0.5 }}>
                      {pieData.map((m, i) => (
                        <Box key={i}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: m.color }} />
                              <Typography sx={{ fontSize: '12px', color: '#344054' }}>{m.name}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography sx={{ fontSize: '11px', color: '#667085' }}>{fmtNum(m.value)} calls</Typography>
                              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#101828' }}>{m.percent}%</Typography>
                            </Box>
                          </Box>
                          <Box sx={{ height: 5, bgcolor: '#F3F4F6', borderRadius: 3 }}>
                            <Box sx={{ height: '100%', width: `${m.percent}%`, bgcolor: m.color, borderRadius: 3, transition: 'width 0.3s' }} />
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '12px', color: '#9CA3AF' }}>
                      {chatModelViewMode === 'perUser' && !chatModelSelectedUser ? 'Select a user to view their model usage' : 'No model usage data'}
                    </Typography>
                  </Box>
                );
              })()}
            </Box>
            </>)}
            </>
            )}
          </Box>{/* close right content area */}
        </Box>{/* close flex container */}
        </>
      )}

      {/* ═══════════ POLICIES ═══════════ */}
      {subSection === 'policies' && (() => {
        // V2 API already returns scoped data via buildTimeSeriesParams (scope + id),
        // so policyOvertimeData is always correctly scoped to the current explorer selection.
        const polChartData = policyOvertimeData;
        // KPI values
        let kpiViolations = policiesTotal, kpiBlocked = policiesBlocked, kpiAlerts = policyTotalAlerts, kpiLogged = policiesLogged;
        if (explorerRepo) { const r = policyRepoStats.find(r => r.repoId === explorerRepo); if (r) { kpiViolations = r.violations; kpiBlocked = r.blocks; kpiAlerts = r.alerts; kpiLogged = r.violations - r.blocks; } }
        else if (explorerWs) { const w = policyWsStats.find(w => w.wsId === explorerWs); if (w) { kpiViolations = w.violations; kpiBlocked = w.blocks; kpiAlerts = w.alerts; kpiLogged = w.violations - w.blocks; } }

        return (
        <>
        {/* Page Header */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#101828' }}>Policy Enforcement</Typography>
          <Typography sx={{ fontSize: '13px', color: '#667085', mt: 0.25 }}>Monitor policy violations, blocks, alerts, and compliance across workspaces and repositories</Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 0 }}>
          {/* Left Explorer Sidebar — filtered to ws/repos with LLM resources */}
          <Box ref={sidebarRef} sx={{
            width: explorerCollapsed ? 28 : 240,
            minWidth: explorerCollapsed ? 28 : 240,
            borderRight: explorerCollapsed ? 'none' : '1px solid #E9EAEB',
            pr: explorerCollapsed ? 0 : 2,
            mr: explorerCollapsed ? 0.5 : 3,
            position: 'sticky', top: 16, alignSelf: 'flex-start',
            maxHeight: 'calc(100vh - 32px)', overflow: 'hidden',
            bgcolor: 'white', zIndex: 5,
            transition: 'width 0.25s ease, min-width 0.25s ease, padding 0.25s ease, margin 0.25s ease',
          }}>
            {explorerCollapsed ? (
              <Box onClick={() => setExplorerCollapsed(false)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 0.5, cursor: 'pointer', '&:hover .explorer-toggle': { bgcolor: '#E9EAEB' } }}>
                <Box className="explorer-toggle" sx={{ width: 24, height: 24, borderRadius: '5px', bgcolor: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#667085" strokeWidth="2"><polyline points="9 6 15 12 9 18" /></svg>
                </Box>
              </Box>
            ) : (
              <Box onClick={() => setExplorerCollapsed(true)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, cursor: 'pointer', py: 0.5, '&:hover .explorer-toggle': { bgcolor: '#E9EAEB' } }}>
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Explorer</Typography>
                <Box className="explorer-toggle" sx={{ width: 24, height: 24, borderRadius: '5px', bgcolor: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#667085" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
                </Box>
              </Box>
            )}
            <Box sx={{ maxHeight: explorerCollapsed ? 0 : 'calc(100vh - 220px)', overflow: explorerCollapsed ? 'hidden' : 'auto', opacity: explorerCollapsed ? 0 : 1, transition: 'max-height 0.25s ease, opacity 0.2s ease', '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { bgcolor: '#D0D5DD', borderRadius: 2 } }}>
              {/* Org root */}
              <Box onClick={() => { setExplorerWs(null); setExplorerRepo(null); }} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1, borderRadius: '8px', cursor: 'pointer', mb: 0.75, bgcolor: !explorerWs ? '#F0F2FF' : 'transparent', border: !explorerWs ? '1px solid #D6DEFF' : '1px solid transparent', transition: 'all 0.15s ease', '&:hover': { bgcolor: !explorerWs ? '#F0F2FF' : '#F9FAFB' } }}>
                <Box sx={{ width: 28, height: 28, borderRadius: '6px', bgcolor: !explorerWs ? '#617BFF' : '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={!explorerWs ? 'white' : '#667085'} strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18" /></svg>
                </Box>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: !explorerWs ? '#3B4ECC' : '#344054', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{orgName || 'Organization'}</Typography>
              </Box>
              {/* Workspace nodes — only those with LLM resources */}
              {policyLlmWorkspaces.map((ws: any) => {
                const isWsActive = explorerWs === ws.id && !explorerRepo;
                const isExpanded = explorerWs === ws.id;
                return (
                  <Box key={ws.id}>
                    <Box onClick={() => { setExplorerWs(ws.id); setExplorerRepo(null); }} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 0.75, borderRadius: '6px', cursor: 'pointer', mb: 0.25, ml: 1.5, bgcolor: isWsActive ? '#F0F2FF' : 'transparent', '&:hover': { bgcolor: isWsActive ? '#F0F2FF' : '#F9FAFB' } }}>
                      <Box sx={{ width: 22, height: 22, borderRadius: '5px', bgcolor: isWsActive ? '#E8ECFF' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={isWsActive ? '#617BFF' : '#9CA3AF'} strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></svg>
                      </Box>
                      <Typography sx={{ fontSize: '12px', fontWeight: isWsActive ? 600 : 400, color: isWsActive ? '#3B4ECC' : '#535862', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ws.name}</Typography>
                    </Box>
                    {isExpanded && policyLlmRepos.map((repo: any) => {
                      const rid = repo.id || repo.repoId;
                      const isRepoActive = explorerRepo === rid;
                      return (
                        <Box key={rid} onClick={() => { setExplorerRepo(rid); }} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, borderRadius: '5px', cursor: 'pointer', mb: 0.25, ml: 4, bgcolor: isRepoActive ? '#F0F2FF' : 'transparent', '&:hover': { bgcolor: isRepoActive ? '#F0F2FF' : '#F9FAFB' } }}>
                          <Box sx={{ width: 18, height: 18, borderRadius: '4px', bgcolor: isRepoActive ? '#E8ECFF' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={isRepoActive ? '#617BFF' : '#9CA3AF'} strokeWidth="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></svg>
                          </Box>
                          <Typography sx={{ fontSize: '11px', fontWeight: isRepoActive ? 600 : 400, color: isRepoActive ? '#3B4ECC' : '#667085', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{repo.name || 'Unnamed'}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                );
              })}
              {policyLlmWorkspaces.length === 0 && (
                <Typography sx={{ fontSize: '11px', color: '#9CA3AF', px: 1.5, py: 1, ml: 1.5 }}>No workspaces with LLM resources</Typography>
              )}
            </Box>
          </Box>

          {/* Right content area */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* ── Sticky header: breadcrumb + date filter (Gateway-aligned) ── */}
            <Box sx={{ position: 'sticky', top: 16, zIndex: 4, bgcolor: 'white', pb: 1, mb: 2 }}>
              {/* Breadcrumb */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2, px: 0.5 }}>
                <Box onClick={() => { setExplorerWs(null); setExplorerRepo(null); }} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', '&:hover .bc-text': { textDecoration: 'underline' } }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18" /></svg>
                  <Typography className="bc-text" sx={{ fontSize: '13px', color: '#617BFF', fontWeight: 500 }}>{orgName || 'Organization'}</Typography>
                </Box>
                {explorerWs && (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D0D5DD" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                    <Box onClick={() => setExplorerRepo(null)} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: explorerRepo ? 'pointer' : 'default', '&:hover .bc-text': explorerRepo ? { textDecoration: 'underline' } : {} }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={explorerRepo ? '#617BFF' : '#344054'} strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></svg>
                      <Typography className="bc-text" sx={{ fontSize: '13px', color: explorerRepo ? '#617BFF' : '#101828', fontWeight: explorerRepo ? 500 : 600 }}>{policyLlmWorkspaces.find((w: any) => w.id === explorerWs)?.name || 'Workspace'}</Typography>
                    </Box>
                  </>
                )}
                {explorerRepo && (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D0D5DD" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#344054" strokeWidth="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></svg>
                      <Typography sx={{ fontSize: '13px', color: '#101828', fontWeight: 600 }}>{policyFilteredRepoStats.find(r => r.repoId === explorerRepo)?.repoName || 'Repository'}</Typography>
                    </Box>
                  </>
                )}
              </Box>

              {/* Date range filter — identical to Gateway */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Box sx={{ display: 'flex', gap: 0.5, bgcolor: '#F3F4F6', borderRadius: '8px', p: '3px' }}>
                  {(['24h', '7d', '30d', '90d', '180d', '365d'] as const).map((r) => (
                    <Button key={r} size="small" onClick={() => setDateRange(r)} sx={{
                      textTransform: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, px: 1.5, py: 0.25, minWidth: 0,
                      bgcolor: dateRange === r ? 'white' : 'transparent',
                      color: dateRange === r ? '#101828' : '#667085',
                      boxShadow: dateRange === r ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                      '&:hover': { bgcolor: dateRange === r ? 'white' : 'rgba(0,0,0,0.04)' },
                    }}>{r}</Button>
                  ))}
                </Box>
                {policiesLoading && <CircularProgress size={14} sx={{ color: '#617BFF' }} />}
              </Box>
            </Box>{/* close sticky header */}

            {/* ── KPI Cards ── */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3, mt: 2 }}>
              <KpiCard label="Total Violations" value={fmtNum(kpiViolations)} color="#F59E0B" loading={policiesLoading} />
              <KpiCard label="Blocked" value={fmtNum(kpiBlocked)} color="#EF4444" loading={policiesLoading} />
              <KpiCard label="Alerts" value={fmtNum(kpiAlerts)} color="#8B5CF6" loading={policiesLoading} />
              <KpiCard label="Logged Only" value={fmtNum(kpiLogged)} color="#3B82F6" loading={policiesLoading} />
            </Box>

            {/* ── Workspace / Repository Table (first after KPIs, Gateway order) ── */}
            {!explorerRepo && !explorerWs && (
              <Box sx={{ bgcolor: 'white', borderRadius: '10px', border: '1px solid #E9EAEB', overflow: 'hidden', mb: 3 }}>
                <Box sx={{ p: 2, borderBottom: '1px solid #E9EAEB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828' }}>Workspaces</Typography>
                    <Typography sx={{ fontSize: '11px', color: '#9CA3AF', mt: 0.25 }}>Policy enforcement per workspace (only workspaces with LLM resources)</Typography>
                  </Box>
                  <Chip label={`${policyFilteredWsStats.length} workspaces`} size="small" sx={{ bgcolor: '#F0F2FF', color: '#617BFF', fontSize: '11px', fontWeight: 500, height: 24 }} />
                </Box>
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
                    <thead>
                      <tr style={{ backgroundColor: '#F9FAFB' }}>
                        {['Workspace', 'Violations', 'Blocked', 'Alerts', 'Tokens In', 'Tokens Out'].map(h => (
                          <th key={h} style={{ padding: '10px 12px', textAlign: h === 'Workspace' ? 'left' : 'right', fontSize: '11px', fontWeight: 600, color: '#667085', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {policyFilteredWsStats.length === 0 ? (
                        <tr><td colSpan={6} style={{ padding: '24px', textAlign: 'center', color: '#9CA3AF', fontSize: '12px' }}>No policy data for this period</td></tr>
                      ) : policyFilteredWsStats.map((ws, idx) => (
                        <tr key={ws.wsId} onClick={() => { setExplorerWs(ws.wsId); setExplorerRepo(null); }} style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#FAFBFC', cursor: 'pointer', transition: 'background 0.1s' }} onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#F5F7FF')} onMouseLeave={e => (e.currentTarget.style.backgroundColor = idx % 2 === 0 ? 'white' : '#FAFBFC')}>
                          <td style={{ padding: '10px 12px', fontWeight: 500, color: '#101828', fontSize: '12px' }}>{ws.wsName}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', color: ws.violations > 0 ? '#F59E0B' : '#667085', fontWeight: ws.violations > 0 ? 600 : 400, fontSize: '12px' }}>{fmtNum(ws.violations)}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', color: ws.blocks > 0 ? '#EF4444' : '#667085', fontWeight: ws.blocks > 0 ? 600 : 400, fontSize: '12px' }}>{fmtNum(ws.blocks)}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', color: ws.alerts > 0 ? '#8B5CF6' : '#667085', fontWeight: ws.alerts > 0 ? 600 : 400, fontSize: '12px' }}>{fmtNum(ws.alerts)}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', color: '#344054', fontSize: '12px' }}>{fmtNum(ws.tokensIn)}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', color: '#344054', fontSize: '12px' }}>{fmtNum(ws.tokensOut)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </Box>
            )}

            {explorerWs && !explorerRepo && (
              <Box sx={{ bgcolor: 'white', borderRadius: '10px', border: '1px solid #E9EAEB', overflow: 'hidden', mb: 3 }}>
                <Box sx={{ p: 2, borderBottom: '1px solid #E9EAEB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828' }}>Repositories</Typography>
                    <Typography sx={{ fontSize: '11px', color: '#9CA3AF', mt: 0.25 }}>Policy enforcement per repository in this workspace</Typography>
                  </Box>
                  <Chip label={`${policyFilteredRepoStats.length} repos`} size="small" sx={{ bgcolor: '#F0F2FF', color: '#617BFF', fontSize: '11px', fontWeight: 500, height: 24 }} />
                </Box>
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
                    <thead>
                      <tr style={{ backgroundColor: '#F9FAFB' }}>
                        {['Repository', 'Violations', 'Blocked', 'Alerts', 'Tokens In', 'Tokens Out'].map(h => (
                          <th key={h} style={{ padding: '10px 12px', textAlign: h === 'Repository' ? 'left' : 'right', fontSize: '11px', fontWeight: 600, color: '#667085', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {policyFilteredRepoStats.length === 0 ? (
                        <tr><td colSpan={6} style={{ padding: '24px', textAlign: 'center', color: '#9CA3AF', fontSize: '12px' }}>No policy data for repositories in this workspace</td></tr>
                      ) : policyFilteredRepoStats.map((repo, idx) => (
                        <tr key={repo.repoId} onClick={() => setExplorerRepo(repo.repoId)} style={{ backgroundColor: explorerRepo === repo.repoId ? '#F0F2FF' : idx % 2 === 0 ? 'white' : '#FAFBFC', cursor: 'pointer', transition: 'background 0.1s' }} onMouseEnter={e => { if (explorerRepo !== repo.repoId) e.currentTarget.style.backgroundColor = '#F5F7FF'; }} onMouseLeave={e => { if (explorerRepo !== repo.repoId) e.currentTarget.style.backgroundColor = idx % 2 === 0 ? 'white' : '#FAFBFC'; }}>
                          <td style={{ padding: '10px 12px', fontWeight: 500, color: '#101828', fontSize: '12px' }}>{repo.repoName}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', color: repo.violations > 0 ? '#F59E0B' : '#667085', fontWeight: repo.violations > 0 ? 600 : 400, fontSize: '12px' }}>{fmtNum(repo.violations)}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', color: repo.blocks > 0 ? '#EF4444' : '#667085', fontWeight: repo.blocks > 0 ? 600 : 400, fontSize: '12px' }}>{fmtNum(repo.blocks)}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', color: repo.alerts > 0 ? '#8B5CF6' : '#667085', fontWeight: repo.alerts > 0 ? 600 : 400, fontSize: '12px' }}>{fmtNum(repo.alerts)}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', color: '#344054', fontSize: '12px' }}>{fmtNum(repo.tokensIn)}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', color: '#344054', fontSize: '12px' }}>{fmtNum(repo.tokensOut)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </Box>
            )}

            {/* ── Violation Types (evolution section, before charts) ── */}
            <Box sx={{ bgcolor: 'white', borderRadius: '10px', border: '1px solid #E9EAEB', p: 2.5, mb: 3 }}>
              <SectionHeader title="Violation Types" subtitle="Distribution of policy violation categories" collapsed={policyCollapsedSections.has('violationTypes')} onToggle={() => togglePolicySection('violationTypes')} />
              {!policyCollapsedSections.has('violationTypes') && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1.5 }}>
                  {(violationTypes.length > 0 ? violationTypes : [
                    { name: 'No data', percent: 0, color: '#E9EAEB' },
                  ]).map((vt, i) => (
                    <Box key={i}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography sx={{ fontSize: '12px', color: '#344054' }}>{vt.name}</Typography>
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#101828' }}>{vt.percent}%</Typography>
                      </Box>
                      <Box sx={{ height: 6, bgcolor: '#F3F4F6', borderRadius: 3 }}>
                        <Box sx={{ height: '100%', width: `${vt.percent}%`, bgcolor: vt.color, borderRadius: 3, transition: 'width 0.3s' }} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>{/* close right content area */}
        </Box>{/* close sidebar flex container */}

        {/* ══ Full-width charts (past sidebar, Gateway pattern) ══ */}
        <Box sx={{ ml: pastSidebar ? 0 : (explorerCollapsed ? '32px' : '268px'), transition: 'margin-left 0.3s ease' }}>

          {/* ── Overview: Separate full-width charts ── */}
          <SectionHeader title="Overview" subtitle="Policy enforcement over time — violations, blocks, alerts, and token traffic" collapsed={policyCollapsedSections.has('overview')} onToggle={() => togglePolicySection('overview')} />
          {!policyCollapsedSections.has('overview') && (
            <>
              {/* Policies overview charts with Line/Bar toggle */}
              {([
                { key: 'violations', label: 'Policies — Violations over time', color: '#F59E0B', gradId: 'clrPolViol' },
                { key: 'blocks', label: 'Policies — Blocks over time', color: '#EF4444', gradId: 'clrPolBlocks' },
                { key: 'alerts', label: 'Policies — Alerts over time', color: '#8B5CF6', gradId: 'clrPolAlerts' },
                { key: 'tokensIn', label: 'Policies — Token In', color: '#10B981', gradId: 'clrPolTkIn' },
                { key: 'tokensOut', label: 'Policies — Token Out', color: '#3B82F6', gradId: 'clrPolTkOut' },
              ] as const).map(({ key, label, color, gradId }) => {
                const mode = policyOverviewChartType[key] || 'line';
                const vMode = policyOverviewViewMode[key] || 'temporal';
                const polAggTotal = polChartData.reduce((s, d) => s + (Number(d[key]) || 0), 0);
                return (
                  <EnhancedChartCard
                    key={key}
                    title={label}
                    loading={policiesLoading}
                    viewMode={vMode}
                    onViewModeChange={(m) => setPolicyOverviewViewMode(prev => ({ ...prev, [key]: m }))}
                    chartType={mode}
                    onChartTypeChange={(t) => setPolicyOverviewChartType(prev => ({ ...prev, [key]: t }))}
                  >
                    {vMode === 'aggregated' ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={[{ name: 'Total', [key]: polAggTotal }]} margin={{ right: 20, left: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                          <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} />
                          <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                          <RechartsTooltip contentStyle={{ borderRadius: 8, border: '1px solid #E9EAEB', fontSize: 12 }} formatter={(v: number) => [fmtNum(v), label]} />
                          <Bar dataKey={key} fill={color} radius={[6, 6, 0, 0]} name={label} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : polChartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        {mode === 'line' ? (
                          <AreaChart data={polChartData} margin={{ right: 20 }}>
                            <defs><linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={color} stopOpacity={0.3} /><stop offset="95%" stopColor={color} stopOpacity={0} /></linearGradient></defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                            <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(polChartData.length / 8) - 1)} />
                            <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                            <RechartsTooltip content={<TradingViewTooltip color={color} valueFormatter={fmtNum} />} />
                            <Area type="monotone" dataKey={key} stroke={color} fill={`url(#${gradId})`} strokeWidth={2} name={label} />
                          </AreaChart>
                        ) : (
                          <BarChart data={polChartData} margin={{ right: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                            <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(polChartData.length / 8) - 1)} />
                            <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                            <RechartsTooltip content={<TradingViewTooltip color={color} valueFormatter={fmtNum} />} />
                            <Bar dataKey={key} fill={color} radius={[4, 4, 0, 0]} name={label} />
                          </BarChart>
                        )}
                      </ResponsiveContainer>
                    ) : <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography sx={{ fontSize: '12px', color: '#9CA3AF' }}>{policiesLoading ? 'Loading...' : 'No data'}</Typography></Box>}
                  </EnhancedChartCard>
                );
              })}
            </>
          )}

          {/* ── Comparisons ── */}
          <Box ref={comparisonsSectionRef}>
            <SectionHeader title="Comparisons" subtitle="Compare policy enforcement across workspaces and repositories" collapsed={policyCollapsedSections.has('comparisons')} onToggle={() => togglePolicySection('comparisons')} />
          </Box>
          {!policyCollapsedSections.has('comparisons') && (<>

          {/* ── C.1) Workspace / Repository Comparison (Gateway-style) ── */}
          {!explorerRepo && (
            <Box sx={{ bgcolor: 'white', borderRadius: '10px', border: '1px solid #E9EAEB', overflow: 'hidden', mb: 2.5 }}>
              <Box sx={{ p: 2, borderBottom: '1px solid #E9EAEB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828' }}>
                    {!explorerWs ? 'Workspace Comparison' : 'Repository Comparison'}
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: '#9CA3AF', mt: 0.25 }}>
                    {!explorerWs ? 'Compare policy violations, blocks, alerts and tokens across workspaces' : 'Compare policy data across repositories in this workspace'}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ p: 2, borderBottom: '1px solid #E9EAEB' }}>
                {!explorerWs ? (
                  <MultiSelectDropdown
                    label="Select workspaces"
                    options={policyLlmWorkspaces.map((ws: any) => ws.name)}
                    selected={new Set(policyLlmWorkspaces.filter((ws: any) => policySelectedCompareWs.has(ws.id)).map((ws: any) => ws.name))}
                    onToggle={(wsName: string) => { const ws = policyLlmWorkspaces.find((w: any) => w.name === wsName); if (ws) togglePolicyCompareWs(ws.id); }}
                    maxItems={5}
                    colors={compareColors}
                  />
                ) : (
                  <MultiSelectDropdown
                    label="Select repositories"
                    options={policyFilteredRepoStats.map(r => r.repoName)}
                    selected={new Set(policyFilteredRepoStats.filter(r => policySelectedCompareRepos.has(r.repoId)).map(r => r.repoName))}
                    onToggle={(rName: string) => { const r = policyFilteredRepoStats.find(rr => rr.repoName === rName); if (r) togglePolicyCompareRepo(r.repoId); }}
                    maxItems={5}
                    colors={compareColors}
                  />
                )}
              </Box>
              {(() => {
                const isWsLevel = !explorerWs;
                const items = isWsLevel
                  ? policyFilteredWsStats.filter(ws => policySelectedCompareWs.has(ws.wsId))
                  : policyFilteredRepoStats.filter(r => policySelectedCompareRepos.has(r.repoId));
                if (items.length === 0) return <Box sx={{ p: 4, textAlign: 'center' }}><Typography sx={{ fontSize: '13px', color: '#9CA3AF' }}>Select {isWsLevel ? 'workspaces' : 'repositories'} above to compare</Typography></Box>;
                const viewModeMap = isWsLevel ? policyWsViewMode : policyRepoViewMode;
                const setViewMode = isWsLevel ? setPolicyWsViewMode : setPolicyRepoViewMode;
                const chartTypeMap = isWsLevel ? policyWsChartType : policyRepoChartType;
                const setChartType = isWsLevel ? setPolicyWsChartType : setPolicyRepoChartType;
                const temporalData = isWsLevel ? policyWsComparisonTimeSeries : policyRepoComparisonTimeSeries;
                return (
                  <Box sx={{ p: 2.5 }}>
                    {(['violations', 'blocks', 'alerts', 'tokensIn', 'tokensOut'] as const).map((metric) => {
                      const labels: Record<string, string> = { violations: 'Policies — Violations', blocks: 'Policies — Blocks', alerts: 'Policies — Alerts', tokensIn: 'Policies — Token In', tokensOut: 'Policies — Token Out' };
                      const label = labels[metric];
                      const viewMode = viewModeMap[metric] || 'aggregated';
                      const chartType = chartTypeMap[metric] || 'line';
                      const aggregatedData = items.map((item: any, i: number) => ({
                        name: isWsLevel ? item.wsName : item.repoName,
                        value: item[metric] || 0,
                        fill: compareColors[i % compareColors.length],
                      }));
                      const tsData = (temporalData as any)[metric] || [];
                      return (
                        <Box key={metric} sx={{ mb: 2.5, bgcolor: '#FAFBFC', borderRadius: '8px', p: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#344054' }}>{label}</Typography>
                            <ChartViewModeSelector
                              viewMode={viewMode}
                              onViewModeChange={(mode) => setViewMode((prev: any) => ({ ...prev, [metric]: mode }))}
                              chartType={chartType}
                              onChartTypeChange={(type) => setChartType((prev: any) => ({ ...prev, [metric]: type }))}
                            />
                          </Box>
                          <Box sx={{ height: CHART_HEIGHT }}>
                            {viewMode === 'aggregated' ? (
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={aggregatedData} margin={{ right: 20, left: 20 }}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                  <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} />
                                  <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                  <RechartsTooltip contentStyle={chartTooltipStyle} formatter={(v: number) => [fmtNum(v), label]} />
                                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                                    {aggregatedData.map((entry: any, index: number) => (
                                      <Cell key={`cell-${index}`} fill={entry.fill} />
                                    ))}
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            ) : (
                              <ResponsiveContainer width="100%" height="100%">
                                {chartType === 'line' ? (
                                  <LineChart data={tsData} margin={{ right: 20 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                    <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(tsData.length / 8) - 1)} />
                                    <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                    <RechartsTooltip content={<TradingViewTooltip valueFormatter={fmtNum} />} />
                                    {items.map((item: any, i: number) => (
                                      <Line key={isWsLevel ? item.wsId : item.repoId} type="monotone" dataKey={isWsLevel ? item.wsName : item.repoName} stroke={compareColors[i % compareColors.length]} strokeWidth={2} dot={false} name={isWsLevel ? item.wsName : item.repoName} />
                                    ))}
                                  </LineChart>
                                ) : (
                                  <BarChart data={tsData} margin={{ right: 20 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                    <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(tsData.length / 8) - 1)} />
                                    <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                    <RechartsTooltip contentStyle={chartTooltipStyle} />
                                    {items.map((item: any, i: number) => (
                                      <Bar key={isWsLevel ? item.wsId : item.repoId} dataKey={isWsLevel ? item.wsName : item.repoName} fill={compareColors[i % compareColors.length]} radius={[4, 4, 0, 0]} name={isWsLevel ? item.wsName : item.repoName} />
                                    ))}
                                  </BarChart>
                                )}
                              </ResponsiveContainer>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                    {/* Recap table */}
                    <Box sx={{ overflowX: 'auto', mt: 1 }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr style={{ backgroundColor: '#F9FAFB' }}>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: '#667085' }}>{isWsLevel ? 'Workspace' : 'Repository'}</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Violations</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Blocks</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Alerts</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Token In</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Token Out</th>
                        </tr></thead>
                        <tbody>
                          {items.map((item: any, idx: number) => (
                            <tr key={isWsLevel ? item.wsId : item.repoId} style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#FAFBFC' }}>
                              <td style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 500, color: '#101828' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: compareColors[idx % compareColors.length], flexShrink: 0 }} />
                                  {isWsLevel ? item.wsName : item.repoName}
                                </Box>
                              </td>
                              <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right', fontWeight: 600 }}>{fmtNum(item.violations)}</td>
                              <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{fmtNum(item.blocks)}</td>
                              <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{fmtNum(item.alerts)}</td>
                              <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{fmtNum(item.tokensIn)}</td>
                              <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{fmtNum(item.tokensOut)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  </Box>
                );
              })()}
            </Box>
          )}

          {/* ── C.2) User Comparison (Gateway-style with MultiSelectDropdown) ── */}
          <Box sx={{ bgcolor: 'white', borderRadius: '10px', border: '1px solid #E9EAEB', overflow: 'hidden', mb: 2.5 }}>
            <Box sx={{ p: 2, borderBottom: '1px solid #E9EAEB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828' }}>User Comparison</Typography>
                <Typography sx={{ fontSize: '11px', color: '#9CA3AF', mt: 0.25 }}>Compare policy violations and token usage across users</Typography>
              </Box>
            </Box>
            <Box sx={{ p: 2, borderBottom: '1px solid #E9EAEB' }}>
              <MultiSelectDropdown
                label="Select users"
                options={policyUserStats.filter(u => u.violations > 0 || u.blocks > 0 || u.alerts > 0).slice(0, 20).map(u => u.name)}
                selected={new Set(policyUserStats.filter(u => policySelectedUsers.has(u.id)).map(u => u.name))}
                onToggle={(userName: string) => {
                  const user = policyUserStats.find(u => u.name === userName);
                  if (user) { setPolicySelectedUsers(prev => { const n = new Set(prev); if (n.has(user.id)) n.delete(user.id); else if (n.size < 5) n.add(user.id); return n; }); }
                }}
                maxItems={5}
                colors={compareColors}
              />
            </Box>
            {policySelectedUsers.size > 0 ? (
              <Box sx={{ p: 2.5 }}>
                {/* Per-user comparison charts with Aggregated/Temporal toggle (Gateway pattern) */}
                {(['violations', 'blocks', 'alerts', 'tokensIn', 'tokensOut'] as const).map((metric) => {
                  const labels: Record<string, string> = { violations: 'Policies — Violations', blocks: 'Policies — Blocks', alerts: 'Policies — Alerts', tokensIn: 'Policies — Token In', tokensOut: 'Policies — Token Out' };
                  const label = labels[metric];
                  const viewMode = policyUserViewMode[metric] || 'aggregated';
                  const chartType = policyUserChartType[metric] || 'line';
                  const selectedArr = [...policySelectedUsers];
                  const selectedUsers = policyUserStats.filter(u => policySelectedUsers.has(u.id));
                  // Aggregated data for bar chart
                  const aggregatedData = selectedUsers.map((user, i) => ({
                    name: user.name,
                    value: user[metric] || 0,
                    fill: compareColors[i % compareColors.length],
                  }));
                  // Temporal data
                  const firstUserId = selectedArr[0];
                  const baseData = policyUserComparisonTimeSeries[firstUserId] || [];
                  const mergedData = baseData.map((d: any, idx: number) => {
                    const row: any = { name: d.name };
                    selectedArr.forEach(uid => {
                      const uData = policyUserComparisonTimeSeries[uid];
                      const userName = policyUserStats.find(x => x.id === uid)?.name || uid;
                      row[userName] = uData?.[idx]?.[metric] || 0;
                    });
                    return row;
                  });
                  return (
                    <Box key={metric} sx={{ mb: 2.5, bgcolor: '#FAFBFC', borderRadius: '8px', p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#344054' }}>{label}</Typography>
                        <ChartViewModeSelector 
                          viewMode={viewMode} 
                          onViewModeChange={(mode) => setPolicyUserViewMode(prev => ({ ...prev, [metric]: mode }))} 
                          chartType={chartType} 
                          onChartTypeChange={(type) => setPolicyUserChartType(prev => ({ ...prev, [metric]: type }))} 
                        />
                      </Box>
                      <Box sx={{ height: CHART_HEIGHT }}>
                        {viewMode === 'aggregated' ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={aggregatedData} margin={{ right: 20, left: 20 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                              <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} />
                              <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                              <RechartsTooltip contentStyle={chartTooltipStyle} formatter={(v: number) => [fmtNum(v), label]} />
                              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                                {aggregatedData.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.fill} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        ) : (
                          <ResponsiveContainer width="100%" height="100%">
                            {chartType === 'line' ? (
                              <LineChart data={mergedData} margin={{ right: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(mergedData.length / 8) - 1)} />
                                <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                <RechartsTooltip content={<TradingViewTooltip valueFormatter={fmtNum} />} />
                                {selectedUsers.map((user, i) => (
                                  <Line key={user.id} type="monotone" dataKey={user.name} stroke={compareColors[i % compareColors.length]} strokeWidth={2} dot={false} name={user.name} />
                                ))}
                              </LineChart>
                            ) : (
                              <BarChart data={mergedData} margin={{ right: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(mergedData.length / 8) - 1)} />
                                <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                <RechartsTooltip contentStyle={chartTooltipStyle} />
                                {selectedUsers.map((user, i) => (
                                  <Bar key={user.id} dataKey={user.name} fill={compareColors[i % compareColors.length]} radius={[4, 4, 0, 0]} name={user.name} />
                                ))}
                              </BarChart>
                            )}
                          </ResponsiveContainer>
                        )}
                      </Box>
                    </Box>
                  );
                })}
                {/* User recap table */}
                <Box sx={{ overflowX: 'auto', mt: 1 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr style={{ backgroundColor: '#F9FAFB' }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: '#667085' }}>User</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Violations</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Blocks</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Alerts</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Token In</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Token Out</th>
                    </tr></thead>
                    <tbody>
                      {[...policySelectedUsers].map((uid, idx) => {
                        const user = policyUserStats.find(u => u.id === uid);
                        if (!user) return null;
                        return (
                          <tr key={uid} style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#FAFBFC' }}>
                            <td style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 500, color: '#101828' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: compareColors[idx % compareColors.length], flexShrink: 0 }} />
                                {user.name}
                              </Box>
                            </td>
                            <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right', fontWeight: 600 }}>{fmtNum(user.violations)}</td>
                            <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{fmtNum(user.blocks)}</td>
                            <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{fmtNum(user.alerts)}</td>
                            <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{fmtNum(user.tokensIn)}</td>
                            <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{fmtNum(user.tokensOut)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Box>

                {/* ── Recent Violations (only shown when users are selected = user drill-down context) ── */}
                {recentViolations.length > 0 && (
                  <Box sx={{ mt: 2.5 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '13px', color: '#101828', mb: 1.5 }}>Recent Violations (selected users)</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {recentViolations.filter((v: any) => {
                        const selectedNames = new Set([...policySelectedUsers].map(uid => policyUserStats.find(u => u.id === uid)?.name).filter(Boolean));
                        return selectedNames.has(v.user);
                      }).slice(0, 10).map((v: any, i: number) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, bgcolor: '#F9FAFB', borderRadius: '8px' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: v.severity === 'high' ? '#EF4444' : v.severity === 'medium' ? '#F59E0B' : '#10B981' }} />
                            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#344054', minWidth: 100 }}>{v.type}</Typography>
                            <Typography sx={{ fontSize: '12px', color: '#667085' }}>{v.user}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Chip label={v.action} size="small" sx={{ height: 22, fontSize: '11px', fontWeight: 600, bgcolor: v.action === 'Blocked' ? '#FEF2F2' : '#F0F2FF', color: v.action === 'Blocked' ? '#EF4444' : '#617BFF' }} />
                            <Typography sx={{ fontSize: '11px', color: '#9CA3AF', minWidth: 70, textAlign: 'right' }}>{v.time}</Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ p: 4, textAlign: 'center' }}><Typography sx={{ fontSize: '13px', color: '#9CA3AF' }}>Select users above to compare policy violations and token usage</Typography></Box>
            )}
          </Box>

          </>)}
        </Box>{/* close full-width area */}
        </>
        );
      })()}

      {/* ═══════════ CREDITS (new dedicated tab) ═══════════ */}
      {subSection === 'credits' && (
        <>
        {/* Page Header */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#101828' }}>Credits & Consumption</Typography>
          <Typography sx={{ fontSize: '13px', color: '#667085', mt: 0.25 }}>Track credit balance, consumption trends, and resource usage across your organization</Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 0 }}>
          {/* Left Explorer Sidebar — org → ws → repo */}
          <Box ref={sidebarRef} sx={{
            width: explorerCollapsed ? 28 : 240,
            minWidth: explorerCollapsed ? 28 : 240,
            borderRight: explorerCollapsed ? 'none' : '1px solid #E9EAEB',
            pr: explorerCollapsed ? 0 : 2,
            mr: explorerCollapsed ? 0.5 : 3,
            position: 'sticky', top: 16, alignSelf: 'flex-start',
            maxHeight: 'calc(100vh - 32px)', overflow: 'hidden',
            bgcolor: 'white', zIndex: 5,
            transition: 'width 0.25s ease, min-width 0.25s ease, padding 0.25s ease, margin 0.25s ease',
          }}>
            {explorerCollapsed ? (
              <Box onClick={() => setExplorerCollapsed(false)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 1, cursor: 'pointer', '&:hover .explorer-toggle': { bgcolor: '#E5E7EB', borderColor: '#D0D5DD' } }}>
                <Box className="explorer-toggle" sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: '#F3F4F6', border: '1px solid #E9EAEB', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#667085" strokeWidth="2"><polyline points="9 6 15 12 9 18" /></svg>
                </Box>
              </Box>
            ) : (
              <Box onClick={() => setExplorerCollapsed(true)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, cursor: 'pointer', py: 0.5, '&:hover .explorer-toggle': { bgcolor: '#E9EAEB' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Explorer</Typography>
                </Box>
                <Box className="explorer-toggle" sx={{ width: 24, height: 24, borderRadius: '5px', bgcolor: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#667085" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
                </Box>
              </Box>
            )}
            <Box sx={{ maxHeight: explorerCollapsed ? 0 : 'calc(100vh - 220px)', overflow: explorerCollapsed ? 'hidden' : 'auto', opacity: explorerCollapsed ? 0 : 1, transition: 'max-height 0.25s ease, opacity 0.2s ease', '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { bgcolor: '#D0D5DD', borderRadius: 2 } }}>
              {/* Org root */}
              <Box onClick={() => { setExplorerWs(null); setExplorerRepo(null); }} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1, borderRadius: '8px', cursor: 'pointer', mb: 0.75, bgcolor: !explorerWs ? '#F0F2FF' : 'transparent', border: !explorerWs ? '1px solid #D6DEFF' : '1px solid transparent', transition: 'all 0.15s ease', '&:hover': { bgcolor: !explorerWs ? '#F0F2FF' : '#F9FAFB' } }}>
                <Box sx={{ width: 28, height: 28, borderRadius: '6px', bgcolor: !explorerWs ? '#617BFF' : '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={!explorerWs ? 'white' : '#667085'} strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18" /></svg>
                </Box>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: !explorerWs ? '#3B4ECC' : '#344054', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{orgName || 'Organization'}</Typography>
              </Box>
              {/* Workspace nodes with repo sub-nodes */}
              {workspaces.map((ws: any) => {
                const isWsActive = explorerWs === ws.id && !explorerRepo;
                const isExpanded = explorerWs === ws.id;
                const wsRepos = isExpanded ? getReposForWorkspace(ws.id) : [];
                return (
                  <Box key={ws.id}>
                    <Box onClick={() => { setExplorerWs(ws.id); setExplorerRepo(null); }} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 0.75, borderRadius: '6px', cursor: 'pointer', mb: 0.25, ml: 1.5, bgcolor: isWsActive ? '#F0F2FF' : 'transparent', '&:hover': { bgcolor: isWsActive ? '#F0F2FF' : '#F9FAFB' } }}>
                      <Box sx={{ width: 22, height: 22, borderRadius: '5px', bgcolor: isWsActive ? '#E8ECFF' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={isWsActive ? '#617BFF' : '#9CA3AF'} strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></svg>
                      </Box>
                      <Typography sx={{ fontSize: '12px', fontWeight: isWsActive ? 600 : 400, color: isWsActive ? '#3B4ECC' : '#535862', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ws.name}</Typography>
                    </Box>
                    {/* Repository sub-nodes */}
                    {isExpanded && wsRepos.map((repo: any) => {
                      const rid = repo.id || repo.repoId;
                      const isRepoActive = explorerRepo === rid;
                      return (
                        <Box key={rid} onClick={() => { setExplorerRepo(rid); }} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, borderRadius: '5px', cursor: 'pointer', mb: 0.25, ml: 4, bgcolor: isRepoActive ? '#F0F2FF' : 'transparent', '&:hover': { bgcolor: isRepoActive ? '#F0F2FF' : '#F9FAFB' } }}>
                          <Box sx={{ width: 18, height: 18, borderRadius: '4px', bgcolor: isRepoActive ? '#E8ECFF' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={isRepoActive ? '#617BFF' : '#9CA3AF'} strokeWidth="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></svg>
                          </Box>
                          <Typography sx={{ fontSize: '11px', fontWeight: isRepoActive ? 600 : 400, color: isRepoActive ? '#3B4ECC' : '#667085', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{repo.name || 'Unnamed'}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Right content area */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Sticky header: breadcrumb + date filter (Gateway-aligned) */}
            <Box sx={{ position: 'sticky', top: 16, zIndex: 4, bgcolor: 'white', pb: 1, mb: 2 }}>
              {/* Breadcrumb */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2, px: 0.5 }}>
                <Box onClick={() => { setExplorerWs(null); setExplorerRepo(null); }} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', '&:hover .bc-text': { textDecoration: 'underline' } }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18" /></svg>
                  <Typography className="bc-text" sx={{ fontSize: '13px', color: '#617BFF', fontWeight: 500 }}>{orgName || 'Organization'}</Typography>
                </Box>
                {explorerWs && (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D0D5DD" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                    <Box onClick={() => { setExplorerRepo(null); }} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', '&:hover .bc-ws': { textDecoration: 'underline' } }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={explorerRepo ? '#617BFF' : '#344054'} strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></svg>
                      <Typography className="bc-ws" sx={{ fontSize: '13px', color: explorerRepo ? '#617BFF' : '#101828', fontWeight: 600 }}>{workspaces.find((ws: any) => ws.id === explorerWs)?.name || 'Workspace'}</Typography>
                    </Box>
                  </>
                )}
                {explorerRepo && (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D0D5DD" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#344054" strokeWidth="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></svg>
                      <Typography sx={{ fontSize: '13px', color: '#101828', fontWeight: 600 }}>{repositoryStats.find((r: any) => r.id === explorerRepo)?.name || effectiveRepositories.find((r: any) => (r.id || r.repoId) === explorerRepo)?.name || 'Repository'}</Typography>
                    </Box>
                  </>
                )}
              </Box>
              {/* Date Range Selector — Gateway-aligned */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ display: 'flex', gap: 0.5, bgcolor: '#F3F4F6', borderRadius: '8px', p: '3px' }}>
                  {(['24h', '7d', '30d', '90d', '180d', '365d'] as DatePreset[]).map(preset => (
                    <Button key={preset} size="small" onClick={() => setDateRange(preset)} sx={{
                      textTransform: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, px: 1.5, py: 0.25, minWidth: 0,
                      bgcolor: dateRange === preset ? 'white' : 'transparent',
                      color: dateRange === preset ? '#101828' : '#667085',
                      boxShadow: dateRange === preset ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                      '&:hover': { bgcolor: dateRange === preset ? 'white' : 'rgba(0,0,0,0.04)' },
                    }}>{preset}</Button>
                  ))}
                </Box>
                {(creditsLoading || perWsStatsLoading) && <CircularProgress size={14} sx={{ color: '#617BFF' }} />}
              </Box>
            </Box>

          {/* KPIs - adapts based on explorer level (org / ws / repo) */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
            {(() => {
              if (explorerRepo) {
                // Repository level
                const repo = repositoryStats.find((r: any) => r.id === explorerRepo);
                const repoCreditsAi = repo ? (repo.tokensIn + repo.tokensOut) * 0.001 : 0;
                const repoCreditsStorage = repo ? repo.storageTraffic * 0.0000001 : 0;
                const repoCreditsTotal = repoCreditsAi + repoCreditsStorage;
                return (
                  <>
                    <KpiCard label="Repository Credits" value={fmtCredits(repoCreditsTotal)} color="#617BFF" loading={perWsStatsLoading} />
                    <KpiCard label="Resources" value={String(repo?.connectedResources || 0)} color="#10B981" loading={false} />
                    <KpiCard label="AI/LLM Credits" value={fmtCredits(repoCreditsAi)} color="#8B5CF6" loading={perWsStatsLoading} />
                    <KpiCard label="Storage Credits" value={fmtCredits(repoCreditsStorage)} color="#3B82F6" loading={perWsStatsLoading} />
                  </>
                );
              } else if (explorerWs) {
                // Workspace level
                const wsData = workspaceStats.find(ws => ws.id === explorerWs);
                const wsCredits = wsData?.credits || 0;
                const wsCreditsAi = wsData?.creditsAi || 0;
                const wsCreditsStorage = wsCredits - wsCreditsAi;
                return (
                  <>
                    <KpiCard label="Workspace Credits" value={fmtCredits(wsCredits)} color="#617BFF" loading={creditsLoading || perWsStatsLoading} />
                    <KpiCard label="Repositories" value={String(repositoryStats.length)} color="#10B981" loading={false} />
                    <KpiCard label="AI/LLM Credits" value={fmtCredits(wsCreditsAi)} color="#8B5CF6" loading={creditsLoading || perWsStatsLoading} />
                    <KpiCard label="Storage Credits" value={fmtCredits(wsCreditsStorage)} color="#3B82F6" loading={creditsLoading || perWsStatsLoading} />
                  </>
                );
              } else {
                // Organization level
                return (
                  <>
                    <KpiCard label="Credit Used" value={fmtCredits(creditsStats.total)} color="#617BFF" loading={creditsLoading} />
                    <KpiCard label="Average / Month" value={fmtCredits(creditsStats.avg)} color="#10B981" loading={creditsLoading} />
                    <KpiCard label="AI/LLM Credits" value={fmtCredits(creditsBalance?.breakdown?.ai || 0)} color="#8B5CF6" loading={creditsLoading} />
                    <KpiCard label="Storage Credits" value={fmtCredits(creditsBalance?.breakdown?.storage || 0)} color="#3B82F6" loading={creditsLoading} />
                  </>
                );
              }
            })()}
          </Box>

          {/* Overview: consumption over time - adapts to level */}
          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#667085', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.5 }}>Overview</Typography>

          <FullWidthChartCard title={explorerRepo ? "Credit consumption over time (repository)" : explorerWs ? "Credit consumption over time (workspace)" : "Credit consumption over time (org-wide)"} loading={creditsLoading}>
            {creditsTimeSeriesData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={creditsTimeSeriesData} margin={{ right: 20 }}>
                  <defs><linearGradient id="clrCreditsOvw" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#617BFF" stopOpacity={0.3} /><stop offset="95%" stopColor="#617BFF" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                  <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(creditsTimeSeriesData.length / 8) - 1)} />
                  <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                  <RechartsTooltip content={<TradingViewTooltip color="#617BFF" valueFormatter={(v: number) => `${v.toLocaleString()} credits`} />} />
                  <Area type="monotone" dataKey="consumption" stroke="#617BFF" fill="url(#clrCreditsOvw)" strokeWidth={2} name="Credits" />
                </AreaChart>
              </ResponsiveContainer>
            ) : <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography sx={{ fontSize: '12px', color: '#9CA3AF' }}>{creditsLoading ? 'Loading...' : 'No data for this period'}</Typography></Box>}
          </FullWidthChartCard>

          {/* Breakdown by resource type */}
          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#667085', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.5, mt: 2 }}>By Resource Type</Typography>

          {/* Credits overview charts with EnhancedChartCard */}
          {/* Chart 1: AI/LLM credit consumption */}
          <EnhancedChartCard
            title="AI/LLM credit consumption"
            loading={creditsLoading}
            viewMode={creditsOverviewViewMode.ai || 'temporal'}
            onViewModeChange={(m) => setCreditsOverviewViewMode(prev => ({ ...prev, ai: m }))}
            chartType={creditsOverviewChartType.ai || 'line'}
            onChartTypeChange={(t) => setCreditsOverviewChartType(prev => ({ ...prev, ai: t }))}
          >
            {(creditsOverviewViewMode.ai || 'temporal') === 'aggregated' ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[{ name: 'Total', ai: creditsTimeSeriesData.reduce((s, d) => s + (Number(d.ai) || 0), 0) }]} margin={{ right: 20, left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                  <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} />
                  <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                  <RechartsTooltip contentStyle={{ borderRadius: 8, border: '1px solid #E9EAEB', fontSize: 12 }} formatter={(v: number) => [`${Number(v).toFixed(1)} credits`, 'AI/LLM']} />
                  <Bar dataKey="ai" fill="#8B5CF6" radius={[6, 6, 0, 0]} name="AI/LLM Credits" />
                </BarChart>
              </ResponsiveContainer>
            ) : creditsTimeSeriesData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                {(creditsOverviewChartType.ai || 'line') === 'line' ? (
                  <AreaChart data={creditsTimeSeriesData} margin={{ right: 20 }}>
                    <defs><linearGradient id="clrCreditsAi" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3} /><stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} /></linearGradient></defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                    <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(creditsTimeSeriesData.length / 8) - 1)} />
                    <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                    <RechartsTooltip content={<TradingViewTooltip color="#8B5CF6" valueFormatter={(v: number) => `${v.toFixed(1)} credits`} />} />
                    <Area type="monotone" dataKey="ai" stroke="#8B5CF6" fill="url(#clrCreditsAi)" strokeWidth={2} name="AI/LLM Credits" />
                  </AreaChart>
                ) : (
                  <BarChart data={creditsTimeSeriesData} margin={{ right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                    <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(creditsTimeSeriesData.length / 8) - 1)} />
                    <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                    <RechartsTooltip content={<TradingViewTooltip color="#8B5CF6" valueFormatter={(v: number) => `${v.toFixed(1)} credits`} />} />
                    <Bar dataKey="ai" fill="#8B5CF6" radius={[4, 4, 0, 0]} name="AI/LLM Credits" />
                  </BarChart>
                )}
              </ResponsiveContainer>
            ) : <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography sx={{ fontSize: '12px', color: '#9CA3AF' }}>No data</Typography></Box>}
          </EnhancedChartCard>

          {/* Chart 2: Storage credit consumption */}
          <EnhancedChartCard
            title="Storage credit consumption"
            loading={creditsLoading}
            viewMode={creditsOverviewViewMode.storage || 'temporal'}
            onViewModeChange={(m) => setCreditsOverviewViewMode(prev => ({ ...prev, storage: m }))}
            chartType={creditsOverviewChartType.storage || 'line'}
            onChartTypeChange={(t) => setCreditsOverviewChartType(prev => ({ ...prev, storage: t }))}
          >
            {(creditsOverviewViewMode.storage || 'temporal') === 'aggregated' ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[{ name: 'Total', storage: creditsTimeSeriesData.reduce((s, d) => s + (Number(d.storage) || 0), 0) }]} margin={{ right: 20, left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                  <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} />
                  <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                  <RechartsTooltip contentStyle={{ borderRadius: 8, border: '1px solid #E9EAEB', fontSize: 12 }} formatter={(v: number) => [`${Number(v).toFixed(1)} credits`, 'Storage']} />
                  <Bar dataKey="storage" fill="#10B981" radius={[6, 6, 0, 0]} name="Storage Credits" />
                </BarChart>
              </ResponsiveContainer>
            ) : creditsTimeSeriesData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                {(creditsOverviewChartType.storage || 'line') === 'line' ? (
                  <AreaChart data={creditsTimeSeriesData} margin={{ right: 20 }}>
                    <defs><linearGradient id="clrCreditsSt" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10B981" stopOpacity={0.3} /><stop offset="95%" stopColor="#10B981" stopOpacity={0} /></linearGradient></defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                    <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(creditsTimeSeriesData.length / 8) - 1)} />
                    <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                    <RechartsTooltip content={<TradingViewTooltip color="#10B981" valueFormatter={(v: number) => `${v.toFixed(1)} credits`} />} />
                    <Area type="monotone" dataKey="storage" stroke="#10B981" fill="url(#clrCreditsSt)" strokeWidth={2} name="Storage Credits" />
                  </AreaChart>
                ) : (
                  <BarChart data={creditsTimeSeriesData} margin={{ right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                    <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(creditsTimeSeriesData.length / 8) - 1)} />
                    <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                    <RechartsTooltip content={<TradingViewTooltip color="#10B981" valueFormatter={(v: number) => `${v.toFixed(1)} credits`} />} />
                    <Bar dataKey="storage" fill="#10B981" radius={[4, 4, 0, 0]} name="Storage Credits" />
                  </BarChart>
                )}
              </ResponsiveContainer>
            ) : <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography sx={{ fontSize: '12px', color: '#9CA3AF' }}>No data</Typography></Box>}
          </EnhancedChartCard>

          {/* Chart 3: Global (all resources) = AI + Storage sum */}
          <EnhancedChartCard
            title="Global (all resources)"
            loading={creditsLoading}
            viewMode={creditsOverviewViewMode.global || 'temporal'}
            onViewModeChange={(m) => setCreditsOverviewViewMode(prev => ({ ...prev, global: m }))}
            chartType={creditsOverviewChartType.global || 'line'}
            onChartTypeChange={(t) => setCreditsOverviewChartType(prev => ({ ...prev, global: t }))}
          >
            {(creditsOverviewViewMode.global || 'temporal') === 'aggregated' ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[{ name: 'Total', total: creditsTimeSeriesData.reduce((s, d) => s + (Number(d.ai) || 0) + (Number(d.storage) || 0), 0) }]} margin={{ right: 20, left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                  <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} />
                  <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                  <RechartsTooltip contentStyle={{ borderRadius: 8, border: '1px solid #E9EAEB', fontSize: 12 }} formatter={(v: number) => [`${Number(v).toFixed(1)} credits`, 'Total (AI + Storage)']} />
                  <Bar dataKey="total" fill="#617BFF" radius={[6, 6, 0, 0]} name="Total Credits" />
                </BarChart>
              </ResponsiveContainer>
            ) : creditsTimeSeriesData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                {(creditsOverviewChartType.global || 'line') === 'line' ? (
                  <AreaChart data={creditsTimeSeriesData} margin={{ right: 20 }}>
                    <defs>
                      <linearGradient id="clrCreditsGlob" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#617BFF" stopOpacity={0.3} /><stop offset="95%" stopColor="#617BFF" stopOpacity={0} /></linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                    <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(creditsTimeSeriesData.length / 8) - 1)} />
                    <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                    <RechartsTooltip content={<TradingViewTooltip color="#617BFF" valueFormatter={(v: number) => `${v.toFixed(1)} credits`} />} />
                    <Area type="monotone" dataKey="consumption" stroke="#617BFF" fill="url(#clrCreditsGlob)" strokeWidth={2} name="Total Credits (AI + Storage)" />
                  </AreaChart>
                ) : (
                  <BarChart data={creditsTimeSeriesData} margin={{ right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                    <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(creditsTimeSeriesData.length / 8) - 1)} />
                    <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                    <RechartsTooltip content={<TradingViewTooltip color="#617BFF" valueFormatter={(v: number) => `${v.toFixed(1)} credits`} />} />
                    <Bar dataKey="consumption" fill="#617BFF" radius={[4, 4, 0, 0]} name="Total Credits (AI + Storage)" />
                  </BarChart>
                )}
              </ResponsiveContainer>
            ) : <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography sx={{ fontSize: '12px', color: '#9CA3AF' }}>No data</Typography></Box>}
          </EnhancedChartCard>

          {/* ─── Comparisons (only at org and workspace level, not repo) ─── */}
          {!explorerRepo && (
            <>
            <Box ref={comparisonsSectionRef}>
              <SectionHeader title="Comparisons" subtitle="Compare credit consumption across workspaces, providers, and models" collapsed={creditsCollapsedSections.has('comparisons')} onToggle={() => toggleCreditsSection('comparisons')} />
            </Box>
            {!creditsCollapsedSections.has('comparisons') && (<>

            {/* ── Workspace Comparison (Org level only) ── */}
            {!explorerWs && (
              <Box sx={{ bgcolor: 'white', borderRadius: '10px', border: '1px solid #E9EAEB', overflow: 'hidden', mb: 2.5 }}>
                <Box sx={{ p: 2, borderBottom: '1px solid #E9EAEB' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828' }}>Workspace Comparison</Typography>
                      <Typography sx={{ fontSize: '11px', color: '#9CA3AF', mt: 0.25 }}>Credit consumption per workspace (Storage + AI)</Typography>
                    </Box>
                  </Box>
                  <MultiSelectDropdown
                    label="Select workspaces"
                    options={workspaces.map((ws: any) => ws.name)}
                    selected={new Set([...selectedCompareWs].map(id => workspaces.find((ws: any) => ws.id === id)?.name).filter(Boolean) as string[])}
                    onToggle={(name) => {
                      const ws = workspaces.find((w: any) => w.name === name);
                      if (ws) toggleCompareWs(ws.id);
                    }}
                    maxItems={5}
                    colors={compareColors}
                  />
                </Box>
                {comparisonData.length > 0 ? (
                  <Box sx={{ p: 2.5 }}>
                    {(['total', 'ai', 'storage'] as const).map((metric) => {
                      const label = metric === 'total' ? 'Total Credits' : metric === 'ai' ? 'AI Credits' : 'Storage Credits';
                      const viewMode = creditsWsCompareViewMode[metric] || 'aggregated';
                      const chartType = creditsWsCompareChartType[metric] || 'line';
                      const data = creditsWsComparisonTimeSeries[metric] || [];
                      const selectedWs = workspaceStats.filter(ws => selectedCompareWs.has(ws.id));
                      const aggregatedData = selectedWs.map((ws, i) => {
                        const bd = creditWsBreakdown.find(e => e.entityId === ws.id);
                        const val = metric === 'total' ? (bd?.creditsTotal || 0) : metric === 'ai' ? (bd?.creditsAi || 0) : (bd?.creditsStorage || 0);
                        return { name: ws.name, value: val, fill: compareColors[i % compareColors.length] };
                      });
                      return (
                        <Box key={metric} sx={{ mb: 2.5, bgcolor: '#FAFBFC', borderRadius: '8px', p: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#344054' }}>{label}</Typography>
                            <ChartViewModeSelector 
                              viewMode={viewMode} 
                              onViewModeChange={(mode) => setCreditsWsCompareViewMode(prev => ({ ...prev, [metric]: mode }))} 
                              chartType={chartType} 
                              onChartTypeChange={(type) => setCreditsWsCompareChartType(prev => ({ ...prev, [metric]: type }))} 
                            />
                          </Box>
                          <Box sx={{ height: CHART_HEIGHT }}>
                            {viewMode === 'aggregated' ? (
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={aggregatedData} margin={{ right: 20, left: 20 }}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                  <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} />
                                  <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                  <RechartsTooltip contentStyle={chartTooltipStyle} formatter={(v: number) => [`${Number(v).toFixed(2)} credits`, label]} />
                                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                                    {aggregatedData.map((entry, index) => (
                                      <Cell key={`cell-${index}`} fill={entry.fill} />
                                    ))}
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            ) : (
                              <ResponsiveContainer width="100%" height="100%">
                                {chartType === 'line' ? (
                                  <LineChart data={data} margin={{ right: 20 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                    <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(data.length / 8) - 1)} />
                                    <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                    <RechartsTooltip content={<TradingViewTooltip valueFormatter={(v: number) => `${v.toFixed(2)} credits`} />} />
                                    {selectedWs.map((ws, i) => (
                                      <Line key={ws.id} type="monotone" dataKey={ws.name} stroke={compareColors[i % compareColors.length]} strokeWidth={2} dot={false} name={ws.name} />
                                    ))}
                                  </LineChart>
                                ) : (
                                  <BarChart data={data} margin={{ right: 20 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                    <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(data.length / 8) - 1)} />
                                    <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                    <RechartsTooltip contentStyle={chartTooltipStyle} />
                                    {selectedWs.map((ws, i) => (
                                      <Bar key={ws.id} dataKey={ws.name} fill={compareColors[i % compareColors.length]} radius={[4, 4, 0, 0]} name={ws.name} />
                                    ))}
                                  </BarChart>
                                )}
                              </ResponsiveContainer>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                    <Box sx={{ overflowX: 'auto', mt: 2 }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr style={{ backgroundColor: '#F9FAFB' }}>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Workspace</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Total</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>AI Credits</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Storage Credits</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Rank</th>
                        </tr></thead>
                        <tbody>
                          {[...comparisonData].map(ws => {
                              const bd = creditWsBreakdown.find(e => e.entityId === ws.id);
                              return { ...ws, creditsTotal: bd?.creditsTotal || 0, creditsAi: bd?.creditsAi || 0, creditsStorage: bd?.creditsStorage || 0 };
                            })
                            .sort((a, b) => b.creditsTotal - a.creditsTotal)
                            .map((ws, idx) => (
                            <tr key={ws.id} style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#FAFBFC' }}>
                              <td style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 500, color: '#101828' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: ws.color, flexShrink: 0 }} />
                                  {ws.name}
                                </Box>
                              </td>
                              <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right', fontWeight: 600 }}>{fmtCredits(ws.creditsTotal)}</td>
                              <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{fmtCredits(ws.creditsAi)}</td>
                              <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{fmtCredits(ws.creditsStorage)}</td>
                              <td style={{ padding: '8px 12px', fontSize: '12px', color: '#617BFF', textAlign: 'right', fontWeight: 600 }}>#{idx + 1}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ p: 4, textAlign: 'center' }}><Typography sx={{ fontSize: '13px', color: '#9CA3AF' }}>Select workspaces above to compare credit consumption</Typography></Box>
                )}
              </Box>
            )}

            {/* ── Repositories Comparison (Org level: all repos across org, Workspace level: repos in workspace) ── */}
            <Box sx={{ bgcolor: 'white', borderRadius: '10px', border: '1px solid #E9EAEB', overflow: 'hidden', mb: 2.5 }}>
              <Box sx={{ p: 2, borderBottom: '1px solid #E9EAEB' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828' }}>Repositories Comparison</Typography>
                    <Typography sx={{ fontSize: '11px', color: '#9CA3AF', mt: 0.25 }}>
                      {explorerWs ? 'Credit consumption per repository in this workspace' : 'Credit consumption per repository across the organization'}
                    </Typography>
                  </Box>
                </Box>
                {!explorerWs ? (
                  <MultiSelectDropdown
                    label="Select repositories"
                    options={allOrgRepoStats.map(r => r.name)}
                    selected={new Set(allOrgRepoStats.filter(r => selectedCreditsCompareRepos.has(r.id)).map(r => r.name))}
                    onToggle={(repoName: string) => {
                      const repo = allOrgRepoStats.find(r => r.name === repoName);
                      if (repo) toggleCreditsCompareRepo(repo.id);
                    }}
                    maxItems={5}
                    colors={compareColors}
                  />
                ) : (
                  <MultiSelectDropdown
                    label="Select repositories"
                    options={repositoryStats.map((repo: any) => repo.name)}
                    selected={new Set(repositoryStats.filter((repo: any) => selectedAiCompareRepos.has(repo.id)).map((repo: any) => repo.name))}
                    onToggle={(repoName: string) => {
                      const repo = repositoryStats.find((r: any) => r.name === repoName);
                      if (repo) toggleAiCompareRepo(repo.id);
                    }}
                    maxItems={5}
                    colors={compareColors}
                  />
                )}
              </Box>
              {/* Org-level: line charts from creditsRepoComparisonTimeSeries */}
              {!explorerWs ? (
                selectedCreditsCompareRepos.size > 0 ? (
                  <Box sx={{ p: 2.5 }}>
                    {(['total', 'ai', 'storage'] as const).map((metric) => {
                      const label = metric === 'total' ? 'Total Credits' : metric === 'ai' ? 'AI Credits' : 'Storage Credits';
                      const viewMode = creditsRepoCompareViewMode[metric] || 'aggregated';
                      const chartType = creditsRepoCompareChartType[metric] || 'line';
                      const data = creditsRepoComparisonTimeSeries[metric] || [];
                      const selectedRepos = allOrgRepoStats.filter(r => selectedCreditsCompareRepos.has(r.id));
                      const aggregatedData = selectedRepos.map((r, i) => {
                        const val = metric === 'total' ? r.creditsTotal : metric === 'ai' ? r.creditsAi : r.creditsSt;
                        return { name: r.name, value: val, fill: compareColors[i % compareColors.length] };
                      });
                      return (
                        <Box key={metric} sx={{ mb: 2.5, bgcolor: '#FAFBFC', borderRadius: '8px', p: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#344054' }}>{label}</Typography>
                            <ChartViewModeSelector 
                              viewMode={viewMode} 
                              onViewModeChange={(mode) => setCreditsRepoCompareViewMode(prev => ({ ...prev, [metric]: mode }))} 
                              chartType={chartType} 
                              onChartTypeChange={(type) => setCreditsRepoCompareChartType(prev => ({ ...prev, [metric]: type }))} 
                            />
                          </Box>
                          <Box sx={{ height: CHART_HEIGHT }}>
                            {viewMode === 'aggregated' ? (
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={aggregatedData} margin={{ right: 20, left: 20 }}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                  <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} />
                                  <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                  <RechartsTooltip contentStyle={chartTooltipStyle} formatter={(v: number) => [`${Number(v).toFixed(2)} credits`, label]} />
                                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                                    {aggregatedData.map((entry, index) => (
                                      <Cell key={`cell-${index}`} fill={entry.fill} />
                                    ))}
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            ) : (
                              <ResponsiveContainer width="100%" height="100%">
                                {chartType === 'line' ? (
                                  <LineChart data={data} margin={{ right: 20 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                    <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(data.length / 8) - 1)} />
                                    <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                    <RechartsTooltip content={<TradingViewTooltip valueFormatter={(v: number) => `${v.toFixed(2)} credits`} />} />
                                    {selectedRepos.map((r, i) => (
                                      <Line key={r.id} type="monotone" dataKey={r.name} stroke={compareColors[i % compareColors.length]} strokeWidth={2} dot={false} name={r.name} />
                                    ))}
                                  </LineChart>
                                ) : (
                                  <BarChart data={data} margin={{ right: 20 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                    <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(data.length / 8) - 1)} />
                                    <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                    <RechartsTooltip contentStyle={chartTooltipStyle} />
                                    {selectedRepos.map((r, i) => (
                                      <Bar key={r.id} dataKey={r.name} fill={compareColors[i % compareColors.length]} radius={[4, 4, 0, 0]} name={r.name} />
                                    ))}
                                  </BarChart>
                                )}
                              </ResponsiveContainer>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                    <Box sx={{ overflowX: 'auto', mt: 2 }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr style={{ backgroundColor: '#F9FAFB' }}>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Repository</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Workspace</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Total</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>AI Credits</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Storage Credits</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Rank</th>
                        </tr></thead>
                        <tbody>
                          {[...allOrgRepoStats.filter(r => selectedCreditsCompareRepos.has(r.id))]
                            .sort((a, b) => b.creditsTotal - a.creditsTotal)
                            .map((r, idx) => (
                            <tr key={r.id} style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#FAFBFC' }}>
                              <td style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 500, color: '#101828' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: compareColors[idx % compareColors.length], flexShrink: 0 }} />
                                  {r.name}
                                </Box>
                              </td>
                              <td style={{ padding: '8px 12px', fontSize: '11px', color: '#667085' }}>{r.wsName}</td>
                              <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right', fontWeight: 600 }}>{fmtCredits(r.creditsTotal)}</td>
                              <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{fmtCredits(r.creditsAi)}</td>
                              <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{fmtCredits(r.creditsSt)}</td>
                              <td style={{ padding: '8px 12px', fontSize: '12px', color: '#617BFF', textAlign: 'right', fontWeight: 600 }}>#{idx + 1}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ p: 4, textAlign: 'center' }}><Typography sx={{ fontSize: '13px', color: '#9CA3AF' }}>Select repositories above to compare credit consumption</Typography></Box>
                )
              ) : (
                /* Workspace-level: bar charts for repos in this workspace */
                selectedAiCompareRepos.size > 0 ? (
                  <Box sx={{ p: 2.5 }}>
                    {([
                      { key: 'credits' as const, label: 'Total Credits', fmt: fmtCredits },
                      { key: 'tokenTraffic' as const, label: 'Token Traffic (AI)', fmt: fmtNum },
                      { key: 'storageTraffic' as const, label: 'Storage Traffic', fmt: formatBytes },
                    ]).map(({ key, label, fmt }) => {
                      const selectedRepos = repositoryStats.filter(repo => selectedAiCompareRepos.has(repo.id));
                      const aggregatedData = selectedRepos.map((repo, i) => ({
                        name: repo.name,
                        value: key === 'credits' ? (repo.tokensIn + repo.tokensOut) * 0.001 + repo.storageTraffic * 0.0000001 : repo[key] || 0,
                        fill: compareColors[i % compareColors.length],
                      }));
                      return (
                        <Box key={key} sx={{ mb: 2.5 }}>
                          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#344054', mb: 1 }}>{label}</Typography>
                          <Box sx={{ height: CHART_HEIGHT }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={aggregatedData} margin={{ right: 20, left: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                                <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} />
                                <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                                <RechartsTooltip contentStyle={chartTooltipStyle} formatter={(v: number) => [fmt(v), label]} />
                                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                                  {aggregatedData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.fill} />
                                  ))}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                        </Box>
                      );
                    })}
                    <Box sx={{ overflowX: 'auto', mt: 2 }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr style={{ backgroundColor: '#F9FAFB' }}>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Repository</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Total Credits</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Storage Traffic</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Token Traffic</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Rank</th>
                        </tr></thead>
                        <tbody>
                          {[...repositoryStats.filter(repo => selectedAiCompareRepos.has(repo.id))].sort((a, b) => (b.tokensIn + b.tokensOut) * 0.001 + b.storageTraffic * 0.0000001 - ((a.tokensIn + a.tokensOut) * 0.001 + a.storageTraffic * 0.0000001)).map((repo, idx) => (
                            <tr key={repo.id} style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#FAFBFC' }}>
                              <td style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 500, color: '#101828' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: compareColors[idx % compareColors.length], flexShrink: 0 }} />
                                  {repo.name}
                                </Box>
                              </td>
                              <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right', fontWeight: 600 }}>{fmtCredits((repo.tokensIn + repo.tokensOut) * 0.001 + repo.storageTraffic * 0.0000001)}</td>
                              <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{formatBytes(repo.storageTraffic)}</td>
                              <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{fmtNum(repo.tokenTraffic)}</td>
                              <td style={{ padding: '8px 12px', fontSize: '12px', color: '#617BFF', textAlign: 'right', fontWeight: 600 }}>#{idx + 1}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ p: 4, textAlign: 'center' }}><Typography sx={{ fontSize: '13px', color: '#9CA3AF' }}>Select repositories above to compare credit consumption</Typography></Box>
                )
              )}
            </Box>

            {/* Model Comparison (LLM) */}
            <Box sx={{ bgcolor: 'white', borderRadius: '10px', border: '1px solid #E9EAEB', overflow: 'hidden', mb: 2.5 }}>
              <Box sx={{ p: 2, borderBottom: '1px solid #E9EAEB' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828' }}>Model Comparison</Typography>
                    <Typography sx={{ fontSize: '11px', color: '#9CA3AF', mt: 0.25 }}>Credit consumption per AI model</Typography>
                  </Box>
                </Box>
                <MultiSelectDropdown
                  label="Select models"
                  options={creditModelBreakdown.map(e => e.entityName)}
                  selected={selectedCreditsModels}
                  onToggle={toggleCreditsModel}
                  maxItems={5}
                  colors={compareColors}
                />
              </Box>
              {creditsModelData.length > 0 ? (
                <Box sx={{ p: 2.5 }}>
                  <Box sx={{ mb: 2.5, bgcolor: '#FAFBFC', borderRadius: '8px', p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#344054' }}>Total Credits</Typography>
                      <ChartViewModeSelector 
                        viewMode={creditsModelViewMode} 
                        onViewModeChange={setCreditsModelViewMode} 
                        chartType={creditsModelChartType} 
                        onChartTypeChange={setCreditsModelChartType} 
                      />
                    </Box>
                    <Box sx={{ height: CHART_HEIGHT }}>
                      {creditsModelViewMode === 'aggregated' ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={creditsModelData.map(d => ({ name: d.name.length > 14 ? d.name.slice(0, 14) + '…' : d.name, fullName: d.name, value: d.total, color: d.color }))} margin={{ right: 20, left: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                            <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} />
                            <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                            <RechartsTooltip contentStyle={chartTooltipStyle} formatter={(v: number) => [`${Number(v).toFixed(2)} credits`, 'Total Credits']} />
                            <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                              {creditsModelData.map((d, i) => (
                                <Cell key={d.name} fill={d.color} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <ResponsiveContainer width="100%" height="100%">
                          {creditsModelChartType === 'line' ? (
                            <LineChart data={creditsModelTimeSeries} margin={{ right: 20 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                              <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(creditsModelTimeSeries.length / 8) - 1)} />
                              <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                              <RechartsTooltip content={<TradingViewTooltip valueFormatter={(v: number) => `${v.toFixed(2)} credits`} />} />
                              {[...selectedCreditsModels].map((model, i) => (
                                <Line key={model} type="monotone" dataKey={model} stroke={compareColors[i % compareColors.length]} strokeWidth={2} dot={false} name={model} />
                              ))}
                            </LineChart>
                          ) : (
                            <BarChart data={creditsModelTimeSeries} margin={{ right: 20 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#F2F4F7" />
                              <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(creditsModelTimeSeries.length / 8) - 1)} />
                              <YAxis tick={axisTick} axisLine={false} tickLine={false} />
                              <RechartsTooltip contentStyle={chartTooltipStyle} />
                              {[...selectedCreditsModels].map((model, i) => (
                                <Bar key={model} dataKey={model} fill={compareColors[i % compareColors.length]} radius={[4, 4, 0, 0]} name={model} />
                              ))}
                            </BarChart>
                          )}
                        </ResponsiveContainer>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ overflowX: 'auto', mt: 2 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead><tr style={{ backgroundColor: '#F9FAFB' }}>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Model</th>
                        <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Total Credits</th>
                        <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>AI Credits</th>
                        <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: 600, color: '#667085' }}>Rank</th>
                      </tr></thead>
                      <tbody>
                        {[...creditsModelData].sort((a, b) => b.total - a.total).map((d, idx) => (
                          <tr key={d.name} style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#FAFBFC' }}>
                            <td style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 500, color: '#101828' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: d.color, flexShrink: 0 }} />
                                {d.name}
                              </Box>
                            </td>
                            <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right', fontWeight: 600 }}>{fmtCredits(d.total)}</td>
                            <td style={{ padding: '8px 12px', fontSize: '12px', color: '#344054', textAlign: 'right' }}>{fmtCredits(d.ai)}</td>
                            <td style={{ padding: '8px 12px', fontSize: '12px', color: '#617BFF', textAlign: 'right', fontWeight: 600 }}>#{idx + 1}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ p: 4, textAlign: 'center' }}><Typography sx={{ fontSize: '13px', color: '#9CA3AF' }}>Select models above to compare</Typography></Box>
              )}
            </Box>

            {creditModelBreakdown.length === 0 && (
            <Box sx={{ p: 2.5, bgcolor: '#FFFBEB', borderRadius: '10px', border: '1px solid #FEF3C7' }}>
              <Typography sx={{ fontSize: '12px', color: '#92400E', fontWeight: 500 }}>
                No model credit data available for the selected time range.
              </Typography>
            </Box>
            )}
            </>)}
            </>
          )}
          </Box>{/* close right content area */}
        </Box>{/* close flex container */}
        </>
      )}
    </Box>
  );
}
